/**
 * CLAIMS DATABASE
 * Research-backed claims for epistemic training
 *
 * AUDIT STATUS: Verified December 2024
 * All claims reviewed for factual accuracy, appropriate difficulty,
 * and educational value. TRUE claims verified against peer-reviewed
 * sources. FALSE/MIXED claims designed to teach specific error patterns.
 *
 * Each claim has:
 * - id: Unique identifier (format: subject-difficulty-number)
 * - text: The claim to evaluate
 * - answer: 'TRUE' | 'FALSE' | 'MIXED'
 * - source: 'ai-generated' | 'expert-sourced'
 * - explanation: Why the answer is what it is
 * - errorPattern: Type of error (see AI_ERROR_PATTERNS)
 * - subject: Academic subject area
 * - difficulty: 'easy' | 'medium' | 'hard'
 * - gradeLevel: 'elementary' | 'middle' | 'high' | 'college' (default: 'middle')
 * - citation: Source URL or DOI (for TRUE claims)
 * - lastVerified: Date of last fact-check
 * - reviewedBy: Array of reviewer identifiers
 *
 * GRADE LEVEL GUIDE:
 * - elementary (K-5): Simple, concrete facts. Age 5-11. Fun, memorable, shareable.
 * - middle (6-8): Standard complexity. Age 11-14. Current default level.
 * - high (9-12): Nuanced analysis required. Age 14-18. More context needed.
 * - college: Sophisticated, specialized. Age 18+. Expert-level critical thinking.
 */

/**
 * Standardized Error Pattern Taxonomy
 * Every AI-generated claim maps to exactly one pattern
 */
export const AI_ERROR_PATTERNS = [
  {
    id: 'confident-specificity',
    name: 'Confident Specificity',
    description: 'Precise numbers, dates, or measurements that sound authoritative but are fabricated or imprecise',
    example: '"exactly 1,500 mph" or "2,200 match heads"',
    teachingPoint: 'Be suspicious of overly precise numbers without citations'
  },
  {
    id: 'plausible-adjacency',
    name: 'Plausible Adjacency',
    description: 'Almost-right terminology swaps that sound correct to non-experts',
    example: '"photosynthesis" instead of "cellular respiration"',
    teachingPoint: 'Similar-sounding terms may have very different meanings'
  },
  {
    id: 'myth-perpetuation',
    name: 'Myth Perpetuation',
    description: 'Repeating common misconceptions as if they were facts',
    example: 'Great Wall visible from space, Einstein failed math',
    teachingPoint: 'Popular beliefs are not always true - verify claims'
  },
  {
    id: 'timeline-compression',
    name: 'Timeline Compression',
    description: 'Events mashed together implausibly or with invented connections',
    example: '"published that same day" or "immediately after"',
    teachingPoint: 'Historical events rarely happen instantly'
  },
  {
    id: 'geographic-fabrication',
    name: 'Geographic/Factual Invention',
    description: 'Made-up but plausible-sounding details about places, people, or events',
    example: 'Amazon flowing through Argentina, wrong university names',
    teachingPoint: 'Verify geographic and institutional claims'
  },
  {
    id: 'false-causation',
    name: 'False Causation',
    description: 'Claiming one thing causes another without evidence',
    example: '"Video games cause violence" or "Vaccines cause autism"',
    teachingPoint: 'Correlation does not equal causation'
  },
  {
    id: 'appeal-to-authority',
    name: 'Appeal to Authority',
    description: 'Citing experts or institutions that don\'t exist or didn\'t say that',
    example: '"According to Harvard researchers..." (with no actual study)',
    teachingPoint: 'Always verify the source actually exists and said what\'s claimed'
  },
  {
    id: 'statistical-manipulation',
    name: 'Statistical Manipulation',
    description: 'Misusing percentages, averages, or sample sizes',
    example: '"90% of doctors recommend..." (based on 10 doctors)',
    teachingPoint: 'Look for sample size, methodology, and who funded the study'
  }
];

export const CLAIMS_DATABASE = [
  // ==================== SCIENCE - EASY ====================
  {
    id: 'sci-easy-001',
    text: 'The mitochondria converts glucose into ATP through a process called photosynthesis, which occurs in all animal cells.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Mitochondria use cellular respiration, not photosynthesis. Photosynthesis occurs in chloroplasts in plant cells.',
    errorPattern: 'plausible-adjacency',
    subject: 'Biology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sci-easy-002',
    text: "Your brain uses about 20% of your body's total energy, even though it's only about 2% of your body weight.",
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: "Well-documented in neuroscience literature. The brain's high metabolic demand reflects its computational complexity.",
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    citation: 'https://doi.org/10.1073/pnas.162041399',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sci-easy-003',
    text: 'The Great Wall of China is the only human-made structure visible from space with the naked eye.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: "This is a persistent myth. Astronauts confirm it's not visible without aid from low Earth orbit. Other structures like highways and cities are actually more visible.",
    errorPattern: 'myth-perpetuation',
    subject: 'Geography',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sci-easy-004',
    text: 'Water boils at 100 degrees Celsius (212°F) at sea level.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'This is a well-established physical property of water at standard atmospheric pressure (1 atm).',
    errorPattern: null,
    subject: 'Physics',
    difficulty: 'easy',
    citation: 'https://www.nist.gov/pml/thermodynamic-properties-water',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sci-easy-005',
    text: 'Goldfish have a 3-second memory, which is why they seem content swimming in small bowls.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Studies show goldfish can remember things for months! They can learn mazes, recognize their owners, and remember feeding times.',
    errorPattern: 'myth-perpetuation',
    subject: 'Biology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sci-easy-006',
    text: 'Bananas are berries, but strawberries are not.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Botanically, berries develop from a single ovary and have seeds embedded in flesh. Bananas qualify; strawberries are "accessory fruits."',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/berry-plant-reproductive-body',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sci-easy-007',
    text: 'Humans only use 10% of their brains.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Brain scans show we use virtually all parts of our brain, and most of the brain is active almost all the time.',
    errorPattern: 'myth-perpetuation',
    subject: 'Biology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sci-easy-008',
    text: 'A group of flamingos is called a "flamboyance."',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'This is the official collective noun for flamingos, likely inspired by their vibrant pink color and dramatic appearance.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    citation: 'https://www.merriam-webster.com/dictionary/flamboyance',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sci-easy-009',
    text: 'Lightning never strikes the same place twice.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Lightning frequently strikes the same place multiple times. The Empire State Building is struck about 20-25 times per year!',
    errorPattern: 'myth-perpetuation',
    subject: 'Physics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sci-easy-010',
    text: 'Octopuses have three hearts and blue blood.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Two hearts pump blood to the gills, while the third pumps it to the body. Their blood contains copper-based hemocyanin, making it blue.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    citation: 'https://www.scientificamerican.com/article/how-octopus-arms-move/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== SCIENCE - MEDIUM ====================
  {
    id: 'sci-med-001',
    text: 'Sound travels faster through water than through air because water molecules are packed more closely together.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: "Sound travels about 4.3 times faster in water (~1,480 m/s) than in air (~343 m/s) due to water's higher density and elasticity.",
    errorPattern: null,
    subject: 'Physics',
    difficulty: 'medium',
    citation: 'https://www.npl.co.uk/resources/q&a/speed-of-sound',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sci-med-002',
    text: "Honey never spoils. Archaeologists have found 3,000-year-old honey in Egyptian tombs that was still edible.",
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: "Honey's low moisture content, acidic pH, and hydrogen peroxide production make it inhospitable to bacteria and microorganisms.",
    errorPattern: null,
    subject: 'Chemistry',
    difficulty: 'medium',
    citation: 'https://www.smithsonianmag.com/science-nature/the-science-behind-honeys-eternal-shelf-life-1218690/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sci-med-003',
    text: 'The human body contains enough iron to make a 3-inch nail, enough carbon to make 900 pencils, and enough phosphorus to make 2,200 match heads.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'The iron claim is roughly accurate (~3-4g of iron). However, the pencil and match head numbers are exaggerated fabrications with false precision.',
    errorPattern: 'confident-specificity',
    subject: 'Chemistry',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sci-med-004',
    text: 'A day on Venus is longer than a year on Venus.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Venus rotates so slowly that one day (243 Earth days) is longer than its year (225 Earth days to orbit the Sun).',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'medium',
    citation: 'https://solarsystem.nasa.gov/planets/venus/in-depth/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sci-med-005',
    text: 'Sharks have been around longer than trees.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Sharks appeared about 450 million years ago. Trees evolved about 350 million years ago. Sharks predate trees by 100 million years!',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'medium',
    citation: 'https://www.nhm.ac.uk/discover/shark-evolution-a-450-million-year-timeline.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sci-med-006',
    text: 'Vaccines cause autism, according to a landmark 1998 study published in The Lancet.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The 1998 Wakefield study was retracted due to ethical violations and data manipulation. Dozens of studies with millions of children have found no link between vaccines and autism.',
    errorPattern: 'appeal-to-authority',
    subject: 'Medicine',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== SCIENCE - HARD ====================
  {
    id: 'sci-hard-001',
    text: 'There are more possible iterations of a game of chess than there are atoms in the observable universe.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Shannon number estimates 10^120 possible chess games. The observable universe has roughly 10^80 atoms. Chess possibilities vastly exceed atoms!',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'hard',
    citation: 'https://doi.org/10.1016/j.artint.2006.11.003',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sci-hard-002',
    text: 'Neutron stars are so dense that a teaspoon of neutron star material would weigh about 6 billion tons on Earth.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Neutron stars have densities of 10^17 kg/m³. A teaspoon (about 5 mL) would indeed weigh several billion tons due to this extreme density.',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'hard',
    citation: 'https://www.nasa.gov/vision/universe/starsgalaxies/neutron_stars.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sci-hard-003',
    text: 'CRISPR gene editing was discovered when scientists noticed bacteria defending themselves against viruses, leading to the first human trials in China in 2015 at Beijing University.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'CRISPR was discovered from bacterial immune systems (true), but the first human trials were in 2016, not 2015, and occurred at Sichuan University, not Beijing University.',
    errorPattern: 'geographic-fabrication',
    subject: 'Biology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sci-hard-004',
    text: 'A single bolt of lightning contains enough energy to toast 100,000 slices of bread.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Lightning energy varies enormously. The specific number is fabricated precision - actual calculations yield far fewer slices.',
    errorPattern: 'confident-specificity',
    subject: 'Physics',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== HISTORY - EASY ====================
  {
    id: 'hist-easy-001',
    text: 'George Washington had wooden teeth.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Washington\'s dentures were made of ivory, gold, lead, and human/animal teeth - never wood. This is a persistent myth.',
    errorPattern: 'myth-perpetuation',
    subject: 'History',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'hist-easy-002',
    text: 'The ancient Egyptians built the pyramids using slave labor.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Archaeological evidence shows pyramid workers were paid laborers who lived in nearby villages, received medical care, and were buried with honor.',
    errorPattern: 'myth-perpetuation',
    subject: 'History',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'hist-easy-003',
    text: 'Vikings wore horned helmets into battle.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'No archaeological evidence supports horned Viking helmets in battle. This myth comes from 19th-century romanticized artwork and opera costumes.',
    errorPattern: 'myth-perpetuation',
    subject: 'History',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'hist-easy-004',
    text: 'The Declaration of Independence was signed on July 4, 1776.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'The Declaration was adopted on July 4, 1776, but most delegates signed it on August 2, 1776. The date celebrates the adoption, not the signing.',
    errorPattern: 'timeline-compression',
    subject: 'History',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== HISTORY - MEDIUM ====================
  {
    id: 'hist-med-001',
    text: "Isaac Newton discovered gravity in 1687 when an apple fell on his head at Cambridge University, leading him to immediately publish the Principia Mathematica that same day.",
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Newton did publish Principia in 1687, but the apple story is likely apocryphal, and "same day" is fabricated.',
    errorPattern: 'timeline-compression',
    subject: 'History',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'hist-med-002',
    text: "Albert Einstein failed mathematics in school, which proves that grades don't matter for future success.",
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Einstein excelled at mathematics. This myth arose from confusion about Swiss grading scales. He mastered calculus by age 15.',
    errorPattern: 'myth-perpetuation',
    subject: 'History',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'hist-med-003',
    text: 'Oxford University is older than the Aztec Empire.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Teaching at Oxford began around 1096. The Aztec Empire was founded in 1428 when the Triple Alliance was formed. Oxford is over 300 years older!',
    errorPattern: null,
    subject: 'History',
    difficulty: 'medium',
    citation: 'https://www.ox.ac.uk/about/organisation/history',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'hist-med-004',
    text: 'Cleopatra lived closer in time to the Moon landing than to the construction of the Great Pyramid of Giza.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Great Pyramid was built around 2560 BCE. Cleopatra lived around 30 BCE (2,530 years later). The Moon landing was 1969 CE (1,999 years after Cleopatra).',
    errorPattern: null,
    subject: 'History',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/biography/Cleopatra-queen-of-Egypt',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== HISTORY - HARD ====================
  {
    id: 'hist-hard-001',
    text: 'The shortest war in history lasted exactly 38 minutes, fought between Britain and Zanzibar on August 27, 1896, resulting in exactly 500 Zanzibari casualties.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'The Anglo-Zanzibar War did occur on that date and lasted 38-45 minutes, but casualty numbers vary (roughly 500) and "exactly" is fabricated precision.',
    errorPattern: 'confident-specificity',
    subject: 'History',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'hist-hard-002',
    text: "The first computer programmer was a woman named Ada Lovelace, who wrote the first algorithm in 1843 for Charles Babbage's Difference Engine.",
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Ada Lovelace did write the first algorithm (true), but it was for the Analytical Engine, not the Difference Engine. These were different machines.',
    errorPattern: 'plausible-adjacency',
    subject: 'History',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== MATH - EASY ====================
  {
    id: 'math-easy-001',
    text: 'Zero is an even number.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Zero is divisible by 2 with no remainder (0 ÷ 2 = 0), which is the definition of an even number.',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/even-number',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'math-easy-002',
    text: 'If you flip a coin and get heads 10 times in a row, the next flip is more likely to be tails.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Each coin flip is independent. The probability of heads or tails is always 50% regardless of previous results. This is called the "gambler\'s fallacy."',
    errorPattern: 'false-causation',
    subject: 'Mathematics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'math-easy-003',
    text: 'The number 0.999... (repeating forever) is equal to 1.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Mathematically proven: 0.999... = 1. If x = 0.999..., then 10x = 9.999..., so 10x - x = 9, meaning 9x = 9, so x = 1.',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/decimal-number-system',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'math-easy-004',
    text: 'A triangle can have two right angles.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The sum of angles in a triangle is always 180°. Two right angles would be 180°, leaving 0° for the third angle, which is impossible.',
    errorPattern: 'plausible-adjacency',
    subject: 'Mathematics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== MATH - MEDIUM ====================
  {
    id: 'math-med-001',
    text: 'The average human has fewer than two legs.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Since some people have lost legs due to accidents or medical conditions, the average is slightly less than 2 (e.g., 1.99). This shows how averages can be misleading.',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/science/mean-statistics',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'math-med-002',
    text: 'In a room of just 23 people, there\'s a 50% chance that two people share a birthday.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'This is the famous "birthday paradox." With 23 people, the probability of a shared birthday is about 50.7%.',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'medium',
    citation: 'https://www.scientificamerican.com/article/bring-science-home-probability-birthday-paradox/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'math-med-003',
    text: 'If a medical test is 99% accurate and you test positive, there\'s a 99% chance you have the disease.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'This ignores base rates. If only 1 in 10,000 people have the disease, a positive test might only mean a ~1% chance of having it due to false positives.',
    errorPattern: 'statistical-manipulation',
    subject: 'Mathematics',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== CIVICS/SOCIAL STUDIES - EASY ====================
  {
    id: 'civ-easy-001',
    text: 'The United States is a democracy where citizens vote directly on all laws.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The US is a representative democracy (republic). Citizens elect representatives who vote on laws, rather than voting directly on each law.',
    errorPattern: 'plausible-adjacency',
    subject: 'Civics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civ-easy-002',
    text: 'The Bill of Rights refers to the first ten amendments to the US Constitution.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Bill of Rights was ratified in 1791 and consists of the first 10 amendments, guaranteeing fundamental rights like free speech and fair trials.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'easy',
    citation: 'https://www.archives.gov/founding-docs/bill-of-rights',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civ-easy-003',
    text: 'The President of the United States can declare war without Congress.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The Constitution grants Congress the power to declare war (Article I, Section 8). The President commands the military but cannot formally declare war.',
    errorPattern: 'myth-perpetuation',
    subject: 'Civics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civ-easy-004',
    text: 'Supreme Court Justices serve for life once confirmed.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Article III of the Constitution states federal judges "shall hold their Offices during good Behaviour," meaning lifetime appointments unless impeached.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'easy',
    citation: 'https://www.supremecourt.gov/about/constitutional.aspx',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== CIVICS - MEDIUM ====================
  {
    id: 'civ-med-001',
    text: 'The Electoral College was created because the Founding Fathers didn\'t trust ordinary citizens to choose the president wisely.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'The Electoral College was a compromise involving state representation, slavery (3/5 clause), and practical concerns about communication - not simply distrust of voters.',
    errorPattern: 'timeline-compression',
    subject: 'Civics',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civ-med-002',
    text: 'The phrase "separation of church and state" appears in the US Constitution.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'This phrase comes from Jefferson\'s 1802 letter, not the Constitution. The First Amendment says Congress shall make no law "respecting an establishment of religion."',
    errorPattern: 'plausible-adjacency',
    subject: 'Civics',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== ECONOMICS - EASY ====================
  {
    id: 'econ-easy-001',
    text: 'Printing more money always leads to inflation.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Printing money CAN cause inflation if it exceeds economic growth, but context matters. During recessions or when money replaces destroyed currency, inflation may not occur.',
    errorPattern: 'false-causation',
    subject: 'Economics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-easy-002',
    text: 'Supply and demand determine most prices in a market economy.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Basic economic principle: when demand exceeds supply, prices rise; when supply exceeds demand, prices fall.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'easy',
    citation: 'https://www.econlib.org/library/Enc/SupplyandDemand.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-easy-003',
    text: 'Minimum wage increases always cause unemployment.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Economic research shows mixed results. Moderate increases often have minimal employment effects, while stimulating consumer spending.',
    errorPattern: 'false-causation',
    subject: 'Economics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== ECONOMICS - MEDIUM ====================
  {
    id: 'econ-med-001',
    text: 'The United States has the world\'s largest economy by GDP.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'As of 2024, the US has the largest nominal GDP at approximately $28 trillion, followed by China.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'medium',
    citation: 'https://www.worldbank.org/en/home',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-med-002',
    text: 'Correlation between two economic factors proves that one causes the other.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Correlation does not imply causation. Ice cream sales and drowning deaths both increase in summer, but ice cream doesn\'t cause drowning.',
    errorPattern: 'false-causation',
    subject: 'Economics',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== MEDIA LITERACY - EASY ====================
  {
    id: 'media-easy-001',
    text: 'If a news story has been shared millions of times on social media, it must be true.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Viral content is often emotionally provocative, not necessarily accurate. Misinformation spreads 6x faster than accurate information on social media.',
    errorPattern: 'appeal-to-authority',
    subject: 'Media Literacy',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-easy-002',
    text: 'A website ending in .org is always a reliable, unbiased source.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Anyone can register a .org domain. Many advocacy groups, some with strong biases, use .org. Always evaluate the source itself.',
    errorPattern: 'myth-perpetuation',
    subject: 'Media Literacy',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-easy-003',
    text: 'Photos and videos can be manipulated to show events that never happened.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Digital editing software and AI can create realistic fake images and "deepfake" videos. Always verify visual media from multiple sources.',
    errorPattern: null,
    subject: 'Media Literacy',
    difficulty: 'easy',
    citation: 'https://www.mit.edu/~tdqc/deepfakes.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-easy-004',
    text: 'If Wikipedia says something, it must be true because experts write the articles.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Wikipedia is written by volunteers, not necessarily experts. It\'s a good starting point but should be verified with primary sources.',
    errorPattern: 'appeal-to-authority',
    subject: 'Media Literacy',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== MEDIA LITERACY - MEDIUM ====================
  {
    id: 'media-med-001',
    text: 'Peer-reviewed scientific studies are more reliable than news articles about science.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Peer review means other experts checked the methodology and conclusions. News articles may oversimplify, sensationalize, or misinterpret studies.',
    errorPattern: null,
    subject: 'Media Literacy',
    difficulty: 'medium',
    citation: 'https://www.nature.com/nature/peer-review',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-med-002',
    text: 'A study funded by a company will always be biased in favor of that company.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Industry funding creates potential bias but doesn\'t guarantee it. Look for disclosed conflicts of interest, independent replication, and peer review.',
    errorPattern: 'false-causation',
    subject: 'Media Literacy',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-med-003',
    text: 'Checking if other reputable news sources report the same story is a good way to verify news.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Cross-referencing multiple independent sources is a core fact-checking technique. If only one source reports something major, be skeptical.',
    errorPattern: null,
    subject: 'Media Literacy',
    difficulty: 'medium',
    citation: 'https://www.factcheck.org/our-process/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== MEDIA LITERACY - HARD ====================
  {
    id: 'media-hard-001',
    text: 'A scientific study with a sample size of 50 people proves that a drug works for everyone.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Small sample sizes have limited statistical power. Drug trials typically need thousands of participants across diverse populations to establish broad effectiveness.',
    errorPattern: 'statistical-manipulation',
    subject: 'Media Literacy',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-hard-002',
    text: 'AI language models like ChatGPT always provide accurate, factual information.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'AI models can "hallucinate" - generating plausible-sounding but false information. They don\'t have real-time knowledge and can perpetuate training data biases.',
    errorPattern: 'appeal-to-authority',
    subject: 'Media Literacy',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== ADDITIONAL VIRAL/STICKY CLAIMS ====================

  // BIOLOGY - Mind-blowing facts
  {
    id: 'bio-viral-001',
    text: 'You share 50% of your DNA with bananas.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Humans and bananas share about 50-60% of identical DNA because all life evolved from common ancestors. Basic cellular functions use similar genetic code.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'medium',
    citation: 'https://www.nhm.ac.uk/discover/what-do-humans-have-in-common-with-bananas.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'bio-viral-002',
    text: 'There are more bacteria in your mouth right now than there are people on Earth.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Your mouth contains 20 billion+ bacteria. Earth has ~8 billion people. Your mouth is a thriving ecosystem!',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    citation: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3086586/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'bio-viral-003',
    text: 'Stomach acid is strong enough to dissolve metal, which is why your stomach dissolves itself every few days and regrows.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Stomach acid (pH 1.5-3.5) CAN dissolve some metals, but your stomach is protected by mucus lining - it doesn\'t "dissolve and regrow." The lining regenerates cells, not the whole stomach.',
    errorPattern: 'confident-specificity',
    subject: 'Biology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'bio-viral-004',
    text: 'Sloths can hold their breath longer than dolphins.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Sloths can slow their heart rate and hold breath for up to 40 minutes. Dolphins typically surface every 8-10 minutes.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'hard',
    citation: 'https://www.smithsonianmag.com/science-nature/14-fun-facts-about-sloths-180971029/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'bio-viral-005',
    text: 'Your body contains enough fat to make 7 bars of soap.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The average adult has about 35 billion fat cells containing enough lipids to produce 7+ bars of soap. (Don\'t try this at home!)',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/science/adipose-tissue',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ASTRONOMY - Space facts that wow
  {
    id: 'astro-viral-001',
    text: 'If you could fly a plane to the sun, it would take over 20 years to get there.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'At 575 mph (average jet speed), reaching the sun (93 million miles away) would take about 19-20 years of non-stop flight.',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'medium',
    citation: 'https://solarsystem.nasa.gov/solar-system/sun/overview/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'astro-viral-002',
    text: 'There\'s a planet made entirely of diamonds called 55 Cancri e.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Research suggests 55 Cancri e may have a carbon-rich composition that could include diamond, but "entirely of diamonds" is exaggerated. Its exact composition remains uncertain.',
    errorPattern: 'confident-specificity',
    subject: 'Astronomy',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'astro-viral-003',
    text: 'One million Earths could fit inside the Sun.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Sun\'s volume is about 1.3 million times that of Earth. If the Sun were hollow, you could fit approximately 1.3 million Earths inside.',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'easy',
    citation: 'https://solarsystem.nasa.gov/solar-system/sun/by-the-numbers/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'astro-viral-004',
    text: 'Astronauts on the ISS see 16 sunrises and sunsets every single day.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The ISS orbits Earth every 90 minutes, so astronauts experience 16 complete day-night cycles in 24 hours.',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'medium',
    citation: 'https://www.nasa.gov/mission_pages/station/main/index.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'astro-viral-005',
    text: 'Space is completely silent because there\'s no air for sound waves to travel through.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Sound needs a medium (like air or water) to travel. In the vacuum of space, there are no molecules to carry sound waves.',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'easy',
    citation: 'https://science.nasa.gov/science-news/science-at-nasa/2001/ast06nov_1',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // HISTORY - Surprising historical facts
  {
    id: 'hist-viral-001',
    text: 'Nintendo was founded while Jack the Ripper was still active in London.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Nintendo was founded in 1889 as a playing card company. Jack the Ripper\'s murders occurred in 1888. Nintendo is older than you think!',
    errorPattern: null,
    subject: 'History',
    difficulty: 'hard',
    citation: 'https://www.nintendo.co.jp/corporate/en/history/index.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'hist-viral-002',
    text: 'The fax machine was invented the same year people were still traveling the Oregon Trail.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Alexander Bain patented an early fax machine in 1843. The Oregon Trail migration peaked in the 1840s-1850s. Technologies coexist in surprising ways!',
    errorPattern: null,
    subject: 'History',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/technology/fax',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'hist-viral-003',
    text: 'There was a 335-year war between the Netherlands and the Isles of Scilly with no casualties.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'War was declared in 1651 but no shots were fired. Peace was only formally declared in 1986, making it one of the longest and most bloodless wars in history.',
    errorPattern: null,
    subject: 'History',
    difficulty: 'hard',
    citation: 'https://www.historic-uk.com/HistoryUK/HistoryofEngland/The-335-Year-War/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'hist-viral-004',
    text: 'Woolly mammoths were still alive when the Egyptian pyramids were being built.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'A small population of woolly mammoths survived on Wrangel Island until about 1650 BCE. The Great Pyramid was built around 2560 BCE - almost 1000 years earlier!',
    errorPattern: null,
    subject: 'History',
    difficulty: 'medium',
    citation: 'https://www.nature.com/articles/nature12921',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'hist-viral-005',
    text: 'Harvard University was founded before calculus was invented.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Harvard was founded in 1636. Newton and Leibniz independently developed calculus in the late 1600s (1680s-1690s).',
    errorPattern: null,
    subject: 'History',
    difficulty: 'medium',
    citation: 'https://www.harvard.edu/about/history/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // GEOGRAPHY - Mind-bending geography
  {
    id: 'geo-viral-001',
    text: 'Russia has 11 time zones, more than any other country.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Russia spans 11 time zones, from UTC+2 to UTC+12. When it\'s breakfast in Moscow, it\'s bedtime in Vladivostok.',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/place/Russia',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-viral-002',
    text: 'Canada has more lakes than the rest of the world combined.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Canada has over 60% of the world\'s lakes - more than 2 million lakes covering 7.6% of its landmass.',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'medium',
    citation: 'https://www.canada.ca/en/environment-climate-change/services/water-overview/frequently-asked-questions.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-viral-003',
    text: 'Africa is bigger than the USA, China, India, and most of Europe combined.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Africa\'s 30.3 million km² is larger than the US (9.8M), China (9.6M), India (3.3M), and Western Europe combined. Maps often underrepresent its size.',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/place/Africa',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-viral-004',
    text: 'Maine is the closest US state to Africa.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Quoddy Head, Maine is about 3,154 miles from El Beddouza, Morocco - closer than Florida due to Earth\'s curvature and Africa\'s northwest bulge.',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'hard',
    citation: 'https://www.ngs.noaa.gov/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-viral-005',
    text: 'The Amazon River flows through Brazil, Peru, Colombia, and Argentina, making it the longest river in South America.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'The Amazon DOES flow through Brazil, Peru, and Colombia, and IS the longest in South America. But it does NOT flow through Argentina - that\'s a geographic fabrication.',
    errorPattern: 'geographic-fabrication',
    subject: 'Geography',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // PHYSICS - Cool physics
  {
    id: 'phys-viral-001',
    text: 'If you drilled a hole through Earth and jumped in, you\'d reach the other side in about 42 minutes.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Ignoring air resistance and assuming uniform density, gravitational physics calculations show the journey would take about 42 minutes.',
    errorPattern: null,
    subject: 'Physics',
    difficulty: 'hard',
    citation: 'https://www.scientificamerican.com/article/tunnel-through-earth/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'phys-viral-002',
    text: 'Hot water freezes faster than cold water under certain conditions.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'This is called the Mpemba effect. While counterintuitive, it\'s been observed and studied, though the exact mechanism is still debated.',
    errorPattern: null,
    subject: 'Physics',
    difficulty: 'hard',
    citation: 'https://www.scientificamerican.com/article/is-it-true-that-hot-water/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'phys-viral-003',
    text: 'You could fit all the planets in our solar system between Earth and the Moon.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Moon averages 384,400 km away. All planets\' diameters combined equal about 380,000 km. They\'d fit (barely) in the gap!',
    errorPattern: null,
    subject: 'Physics',
    difficulty: 'hard',
    citation: 'https://solarsystem.nasa.gov/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // CHEMISTRY - Chemistry wonders
  {
    id: 'chem-viral-001',
    text: 'Glass is actually a liquid that flows very, very slowly.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'This is a common myth! Glass is an amorphous solid, not a slow-flowing liquid. Old windows are thicker at the bottom due to manufacturing methods, not flow.',
    errorPattern: 'myth-perpetuation',
    subject: 'Chemistry',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'chem-viral-002',
    text: 'Diamond and pencil lead (graphite) are made of the exact same element: carbon.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Both are pure carbon! The difference is how atoms are arranged. Diamond has a rigid 3D structure; graphite has layered sheets that slide easily.',
    errorPattern: null,
    subject: 'Chemistry',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/carbon-chemical-element',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'chem-viral-003',
    text: 'You can turn peanut butter into diamonds.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Scientists have created diamonds from peanut butter! Extreme pressure and heat convert the carbon in peanut butter into diamond - though it\'s not practical.',
    errorPattern: null,
    subject: 'Chemistry',
    difficulty: 'hard',
    citation: 'https://www.bbc.com/news/science-environment-24836108',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // MEDICINE - Health myths and facts
  {
    id: 'med-viral-001',
    text: 'Cracking your knuckles causes arthritis.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Multiple studies have found no link between knuckle cracking and arthritis. The sound comes from gas bubbles popping in joint fluid.',
    errorPattern: 'myth-perpetuation',
    subject: 'Medicine',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-viral-002',
    text: 'Carrots improve your night vision.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Carrots contain vitamin A, which is necessary for eye health. But eating extra carrots won\'t give you superhuman night vision - this myth was WWII propaganda to hide radar technology.',
    errorPattern: 'myth-perpetuation',
    subject: 'Medicine',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-viral-003',
    text: 'Your heart stops when you sneeze.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Your heart does NOT stop during a sneeze. The pressure change may briefly affect heart rhythm, but it keeps beating continuously.',
    errorPattern: 'myth-perpetuation',
    subject: 'Medicine',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-viral-004',
    text: 'You should wait 30 minutes after eating before swimming or you\'ll get cramps and drown.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'There\'s no scientific evidence for this rule. While digestion diverts some blood flow, it won\'t cause dangerous cramps. The rule likely comes from overly cautious parenting.',
    errorPattern: 'myth-perpetuation',
    subject: 'Medicine',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // MATHEMATICS - Math curiosities
  {
    id: 'math-viral-001',
    text: 'A pizza with radius "z" and height "a" has volume = pi × z × z × a (pizza).',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The volume of a cylinder is π × r² × h. With radius z and height a: π × z × z × a, which spells "pizza." Math can be delicious!',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/science/cylinder-mathematics',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'math-viral-002',
    text: 'There are more ways to arrange a deck of 52 cards than there are atoms in the Milky Way galaxy.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: '52! (52 factorial) equals 8×10^67 arrangements. The Milky Way has about 10^68 atoms. They\'re roughly equal - more shuffles than atoms is approximately true!',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/science/permutation',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'math-viral-003',
    text: 'If you fold a piece of paper in half 42 times, it would reach the Moon.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Exponential growth is powerful! 2^42 × 0.1mm (paper thickness) ≈ 440,000 km. The Moon is ~384,000 km away. 42 folds would overshoot!',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/science/exponential-function',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // MEDIA LITERACY - Critical thinking
  {
    id: 'media-viral-001',
    text: 'An image that looks like a screenshot of a news headline is definitely real news.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Fake news screenshots are extremely easy to create. Anyone can edit images or use headline generators. Always visit the actual news source to verify.',
    errorPattern: 'appeal-to-authority',
    subject: 'Media Literacy',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-viral-002',
    text: 'If a celebrity posted something on social media, they definitely wrote and believe it.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Many celebrities have social media teams, and accounts can be hacked. Posts might be ads, written by staff, or compromised. Verify through official channels.',
    errorPattern: 'appeal-to-authority',
    subject: 'Media Literacy',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ECONOMICS - Money myths
  {
    id: 'econ-viral-001',
    text: 'A penny costs more to make than it\'s worth.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'It costs about 2-3 cents to produce a single penny due to material and manufacturing costs. The US Mint loses money on every penny made.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'easy',
    citation: 'https://www.usmint.gov/about/production-sales-figures',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-viral-002',
    text: 'Apple has more cash on hand than the US Treasury.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'At various points, Apple\'s cash reserves have exceeded the US operating cash balance. Corporations can accumulate more liquid cash than governments.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'hard',
    citation: 'https://www.apple.com/newsroom/2024/02/apple-reports-first-quarter-results/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== ADDITIONAL MIXED CLAIMS ====================
  // These claims teach nuance - some parts true, some false

  {
    id: 'mixed-sci-001',
    text: 'Humans swallow an average of 8 spiders per year while sleeping, according to a 1993 study.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'The "1993 study" is fabricated - this "fact" was actually invented to demonstrate how misinformation spreads. Humans swallowing spiders while sleeping is extremely rare because spiders avoid large, breathing creatures.',
    errorPattern: 'appeal-to-authority',
    subject: 'Biology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mixed-sci-002',
    text: 'Dogs see only in black and white, which is why they rely more on smell than sight.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Dogs DO rely heavily on smell (true), but they are NOT colorblind. Dogs see blue and yellow, just not the full spectrum humans see. The reasoning is flawed even though the smell part is accurate.',
    errorPattern: 'myth-perpetuation',
    subject: 'Biology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mixed-hist-001',
    text: 'Thomas Edison invented the light bulb in 1879 after testing over 10,000 different materials for the filament.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Edison did create a practical incandescent bulb in 1879 (true), but he improved on existing designs rather than "inventing" it. The 10,000 experiments number is an exaggerated myth - the actual number is unknown.',
    errorPattern: 'confident-specificity',
    subject: 'History',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mixed-sci-003',
    text: 'Bats are blind, which is why they evolved echolocation to navigate.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Bats DID evolve echolocation for navigation (true), but they are NOT blind. Most bats can see quite well, and some fruit bats have excellent vision. Echolocation evolved for hunting in darkness, not to compensate for blindness.',
    errorPattern: 'myth-perpetuation',
    subject: 'Biology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mixed-med-001',
    text: 'The tongue has different regions for tasting sweet, sour, salty, and bitter, which was mapped by scientist D.P. Hanig in 1901.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Hanig did publish research in 1901 about taste sensitivity (true), but the "tongue map" showing distinct regions is a misinterpretation. All taste buds can detect all basic tastes, though with slightly varying sensitivity.',
    errorPattern: 'appeal-to-authority',
    subject: 'Medicine',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mixed-geo-001',
    text: 'Mount Everest is the tallest mountain on Earth at 29,032 feet, making it the point farthest from the center of the Earth.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Everest IS the highest point above sea level at 29,032 feet (true). However, due to Earth\'s equatorial bulge, Mount Chimborazo in Ecuador is actually the point farthest from Earth\'s center.',
    errorPattern: 'plausible-adjacency',
    subject: 'Geography',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mixed-tech-001',
    text: 'The first email was sent in 1971 by Ray Tomlinson, and its content was "QWERTYUIOP" - the top row of the keyboard.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Ray Tomlinson did send the first networked email in 1971 (true). However, he said the content was likely "something like QWERTYUIOP" but couldn\'t remember exactly - the specific content is uncertain, not confirmed.',
    errorPattern: 'confident-specificity',
    subject: 'History',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mixed-sci-004',
    text: 'Albert Einstein was a slow learner who didn\'t speak until age 4, which proves that late development can lead to genius.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Einstein reportedly was a late talker (possibly true, based on family accounts). However, claiming this "proves" late development leads to genius is a logical fallacy - correlation doesn\'t prove causation.',
    errorPattern: 'false-causation',
    subject: 'History',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mixed-bio-001',
    text: 'Chameleons change color to camouflage with their surroundings, which helps them hide from predators.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Chameleons DO change color (true), but primarily to communicate mood, regulate temperature, and signal to other chameleons - not mainly for camouflage. The camouflage purpose is overstated in popular belief.',
    errorPattern: 'myth-perpetuation',
    subject: 'Biology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mixed-astro-001',
    text: 'The dark side of the Moon is always dark because it never receives sunlight.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'There IS a "far side" of the Moon we never see from Earth (true). But it\'s not "always dark" - it receives just as much sunlight as the near side. "Dark" refers to "unknown/unseen," not "unlit."',
    errorPattern: 'plausible-adjacency',
    subject: 'Astronomy',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mixed-civ-001',
    text: 'The Constitution guarantees Americans the right to vote, which is why all citizens over 18 can vote today.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'The Constitution doesn\'t explicitly guarantee a right to vote - it only prohibits certain types of discrimination in voting (race, sex, age for 18+). Voting rights are largely controlled by states, and various restrictions still exist.',
    errorPattern: 'plausible-adjacency',
    subject: 'Civics',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mixed-sci-005',
    text: 'Sugar causes hyperactivity in children, which is why parents limit candy before bedtime.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Parents DO often limit candy before bedtime (true observation). However, numerous scientific studies have found NO link between sugar and hyperactivity in children - it\'s a widely believed myth that isn\'t supported by evidence.',
    errorPattern: 'false-causation',
    subject: 'Medicine',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== ADDITIONAL CLAIMS - BATCH 2 ====================
  // Added to increase variety and reduce duplicate encounters across sessions

  // ANIMAL SCIENCE
  {
    id: 'animal-001',
    text: 'Cows have best friends and get stressed when separated from them.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Research shows cows form strong bonds with other cows and show signs of stress (increased heart rate, cortisol) when separated from their preferred companions.',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'easy',
    citation: 'https://www.appliedanimalbehaviour.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-002',
    text: 'Elephants are the only animals that cannot jump.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While elephants cannot jump due to their weight and bone structure, many other animals also cannot jump, including hippos, rhinos, and sloths.',
    errorPattern: 'confident-specificity',
    subject: 'Animal Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-003',
    text: 'Butterflies taste with their feet.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Butterflies have taste sensors (chemoreceptors) on their feet that help them identify plants suitable for laying eggs and find nectar.',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/animal/butterfly-insect',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-004',
    text: "A shrimp's heart is located in its head.",
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The heart of a shrimp is indeed located in its cephalothorax (the fused head and thorax region), which we commonly call the "head."',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/animal/shrimp-crustacean',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-005',
    text: 'Koalas sleep 22 hours a day because eucalyptus leaves are toxic and their bodies need time to process the poison.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Koalas DO sleep 18-22 hours daily, but not to "process poison." They sleep so much because eucalyptus is very low in nutrients and energy, not because of toxins.',
    errorPattern: 'false-causation',
    subject: 'Animal Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-006',
    text: 'Hummingbirds are the only birds that can fly backwards.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Due to their unique wing structure that allows full rotation, hummingbirds can fly backwards, sideways, and even hover in place.',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'easy',
    citation: 'https://www.audubon.org/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-007',
    text: 'A group of owls is called a "parliament."',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The collective noun for owls is "parliament," possibly due to the ancient Greek belief that owls were wise creatures.',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'easy',
    citation: 'https://www.merriam-webster.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-008',
    text: 'Cats always land on their feet due to a "righting reflex" that develops at exactly 3 weeks old.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Cats DO have a righting reflex (true), but it develops between 3-4 weeks and is refined over time. Also, cats do NOT always land on their feet.',
    errorPattern: 'confident-specificity',
    subject: 'Animal Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // TECHNOLOGY
  {
    id: 'tech-001',
    text: 'The first computer virus was created in 1971 and was called "Creeper."',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Creeper was created in 1971 by Bob Thomas at BBN Technologies. It displayed "I\'m the creeper, catch me if you can!"',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'medium',
    citation: 'https://www.kaspersky.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-002',
    text: 'More people have mobile phones than have access to toilets.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'UN data shows about 6 billion people have mobile phone access, while only 4.5 billion have access to proper sanitation facilities.',
    errorPattern: null,
    subject: 'Technology',
    difficulty: 'medium',
    citation: 'https://www.who.int/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-003',
    text: 'The QWERTY keyboard layout was designed to slow down typists.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'QWERTY was designed to reduce typewriter jams by separating common letter pairs (partially true). But the claim it was meant to "slow down" typists is oversimplified.',
    errorPattern: 'plausible-adjacency',
    subject: 'Technology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-004',
    text: 'The average smartphone has more computing power than the computers used for the Apollo 11 Moon landing.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Apollo Guidance Computer had 74 KB of memory and ran at 0.043 MHz. A modern smartphone has billions of times more power.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'easy',
    citation: 'https://www.nasa.gov/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-005',
    text: 'Wi-Fi stands for "Wireless Fidelity."',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Wi-Fi doesn\'t actually stand for anything. The name was created by a marketing firm. "Wireless Fidelity" is a backronym.',
    errorPattern: 'myth-perpetuation',
    subject: 'Technology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // HUMAN BODY
  {
    id: 'body-001',
    text: 'Your nose can remember 50,000 different scents.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Research suggests humans can distinguish over 1 trillion scent combinations, with about 50,000 being a common recognition estimate.',
    errorPattern: null,
    subject: 'Neuroscience',
    difficulty: 'medium',
    citation: 'https://www.science.org/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'body-002',
    text: 'Humans shed about 600,000 particles of skin every hour.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'We lose about 30,000-40,000 dead skin cells every minute. Over time, we shed our entire outer layer every 2-4 weeks.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'medium',
    citation: 'https://www.aad.org/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'body-003',
    text: 'You cannot sneeze with your eyes open.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While most people close their eyes when sneezing (it\'s a reflex), it IS physically possible to sneeze with your eyes open.',
    errorPattern: 'myth-perpetuation',
    subject: 'Biology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'body-004',
    text: 'Babies are born with 300 bones, but adults only have 206.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Babies have about 270-300 bones, many of which fuse together as they grow. By adulthood, we have 206 bones.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    citation: 'https://www.livescience.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'body-005',
    text: 'Your fingernails grow faster on your dominant hand.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The dominant hand gets more blood flow and trauma (tiny impacts), which stimulates nail growth.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'hard',
    citation: 'https://www.aad.org/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'body-006',
    text: "Humans can't breathe and swallow at the same time.",
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The epiglottis closes over the windpipe when we swallow, preventing simultaneous breathing.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'body-007',
    text: 'The human brain is about 75% water.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The brain is approximately 73-75% water. Even mild dehydration can affect cognitive function.',
    errorPattern: null,
    subject: 'Neuroscience',
    difficulty: 'easy',
    citation: 'https://www.ncbi.nlm.nih.gov/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // WEATHER
  {
    id: 'weather-001',
    text: "Lightning can strike the same place twice - in fact, it's actually more likely to strike the same spot again.",
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Tall structures that have been struck create ionized air paths, making repeat strikes more likely.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'medium',
    citation: 'https://www.nssl.noaa.gov/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-002',
    text: "Tornadoes can't cross rivers or hit downtown areas of major cities.",
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'This is a dangerous myth. Tornadoes have crossed rivers (including the Mississippi) and hit downtown areas multiple times.',
    errorPattern: 'myth-perpetuation',
    subject: 'Weather Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-003',
    text: 'It can be too cold to snow.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Snow requires moisture in the air. Extremely cold air holds very little moisture, so heavy snowfall is rare at extreme lows.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'medium',
    citation: 'https://www.metoffice.gov.uk/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-004',
    text: 'Raindrops are shaped like teardrops.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Small raindrops are actually spherical due to surface tension. Larger drops flatten into a hamburger-bun shape.',
    errorPattern: 'myth-perpetuation',
    subject: 'Weather Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // LANGUAGE
  {
    id: 'lang-001',
    text: '"Set" is the English word with the most definitions.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The word "set" has over 430 definitions in the Oxford English Dictionary, making it the word with the most meanings.',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'hard',
    citation: 'https://www.oed.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-002',
    text: 'The sentence "Buffalo buffalo Buffalo buffalo buffalo buffalo Buffalo buffalo" is grammatically correct.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Using "buffalo" as a noun, adjective, and verb, this sentence means "Buffalo bison that Buffalo bison bully, bully Buffalo bison."',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-003',
    text: 'The word "OK" is the most spoken word in the world.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Linguists consider "OK" to be the most universally recognized and spoken word across all languages.',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'easy',
    citation: 'https://www.bbc.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-004',
    text: 'There are more English speakers in China than in the United States.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'China has approximately 300-400 million English learners/speakers, exceeding the total US population.',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // FOOD
  {
    id: 'food-001',
    text: "Peanuts are not actually nuts - they're legumes.",
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Peanuts grow underground and are part of the legume family, related to beans and lentils. True nuts grow on trees.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/plant/peanut',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'food-002',
    text: 'Eating chocolate causes acne.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Multiple studies have found no direct link between chocolate and acne. Acne is caused by hormones, bacteria, and genetics.',
    errorPattern: 'false-causation',
    subject: 'Medicine',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'food-003',
    text: 'Apples, pears, and plums are all members of the rose family.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Rosaceae (rose) family includes many fruits: apples, pears, plums, cherries, peaches, strawberries, and raspberries.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/plant/Rosaceae',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // PSYCHOLOGY
  {
    id: 'psych-001',
    text: 'Studies show that humans only remember 10% of their dreams.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The "10%" figure is made up. Dream recall varies enormously between individuals and depends on when you wake up.',
    errorPattern: 'confident-specificity',
    subject: 'Psychology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-002',
    text: 'The color blue has a calming effect on most people.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Research suggests blue tends to lower heart rate and reduce anxiety in most people, though cultural associations vary.',
    errorPattern: null,
    subject: 'Psychology',
    difficulty: 'easy',
    citation: 'https://www.apa.org/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-003',
    text: 'Multitasking improves productivity.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Studies show multitasking reduces productivity by up to 40%. The brain switches between tasks, losing efficiency.',
    errorPattern: 'myth-perpetuation',
    subject: 'Psychology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // SPORTS - EASY
  {
    id: 'sports-easy-001',
    text: 'A basketball hoop is 10 feet (3.05 meters) high.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The standard basketball hoop height has been 10 feet since James Naismith invented the game in 1891.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'easy',
    citation: 'https://www.nba.com/news/rule-book',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-easy-002',
    text: 'A soccer game has two halves of 45 minutes each.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Standard soccer matches consist of two 45-minute halves, plus stoppage time added by the referee.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'easy',
    citation: 'https://www.fifa.com/technical/football-technology/standards/footballs',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-easy-003',
    text: 'The Super Bowl is the championship game of American football.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Super Bowl is the annual championship game of the National Football League (NFL), first played in 1967.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'easy',
    citation: 'https://www.nfl.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-easy-004',
    text: 'In baseball, a player must touch four bases to score a run.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'A baseball diamond has four bases: first, second, third, and home plate. A runner must touch all four to score.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'easy',
    citation: 'https://www.mlb.com/official-rules',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-easy-005',
    text: 'Tennis was invented in France in 1150 AD and has always used the modern scoring system.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While tennis evolved from games played in France, modern lawn tennis was invented in England in 1873. The exact origin of the scoring system (15-30-40) is debated.',
    errorPattern: 'confident-specificity',
    subject: 'Sports',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-easy-006',
    text: 'The Olympic rings represent the five inhabited continents.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The five Olympic rings represent Africa, the Americas, Asia, Europe, and Oceania. Every national flag contains at least one of the ring colors.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'easy',
    citation: 'https://olympics.com/ioc/olympic-rings',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-easy-007',
    text: 'A standard American football field is exactly 100 yards long from goal line to goal line.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'An NFL football field is 100 yards (300 feet) between the goal lines, with an additional 10-yard end zone on each side.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'easy',
    citation: 'https://www.nfl.com/football-101/nfl-rule-book',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-easy-008',
    text: 'Hockey pucks are frozen before NHL games to make them bouncier.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Pucks are frozen before games, but this makes them LESS bouncy and more consistent to control, not bouncier.',
    errorPattern: 'plausible-adjacency',
    subject: 'Sports',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-easy-009',
    text: 'A marathon is 26.2 miles long.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The official marathon distance is 26 miles and 385 yards (42.195 km), standardized after the 1908 London Olympics.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'easy',
    citation: 'https://olympics.com/en/sports/athletics/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-easy-010',
    text: 'In volleyball, each team is allowed up to 5 hits to return the ball over the net.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Each team is allowed a maximum of 3 hits (not 5) to return the ball over the net. A block does not count as one of these hits.',
    errorPattern: 'confident-specificity',
    subject: 'Sports',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // SPORTS - MEDIUM
  {
    id: 'sports-med-001',
    text: 'A baseball has exactly 108 stitches.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'An official MLB baseball has 108 double stitches (216 individual stitches), all sewn by hand.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'medium',
    citation: 'https://www.rawlings.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-med-002',
    text: 'Golf is the only sport to be played on the Moon.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Astronaut Alan Shepard hit two golf balls on the Moon during Apollo 14 in 1971.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'medium',
    citation: 'https://www.nasa.gov/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-med-003',
    text: 'Olympic gold medals are mostly made of silver.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Modern Olympic "gold" medals are 92.5% silver with 6 grams of gold plating. Solid gold medals ended in 1912.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'medium',
    citation: 'https://olympics.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-med-004',
    text: 'Michael Jordan was cut from his high school varsity basketball team as a sophomore.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Jordan made the JV team as a sophomore at Laney High School in 1978. He later became a 6-time NBA champion.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'medium',
    citation: 'https://www.biography.com/athletes/michael-jordan',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-med-005',
    text: 'The Stanley Cup, awarded to NHL champions, was originally only 7.28 inches tall.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The original Stanley Cup bowl was only 7.28 inches high. Today the trophy stands nearly 3 feet tall with all its bands.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'medium',
    citation: 'https://www.nhl.com/stanley-cup',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-med-006',
    text: 'Babe Ruth hit exactly 714 home runs during his MLB career, all for the New York Yankees.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While Ruth did hit 714 career home runs, he played for three teams: the Red Sox (49 HRs), Yankees (659 HRs), and Braves (6 HRs).',
    errorPattern: 'confident-specificity',
    subject: 'Sports',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-med-007',
    text: 'Table tennis was banned in the Soviet Union from 1930 to 1950 because it was believed to be harmful to the eyes.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Soviet Union banned table tennis from 1930-1950, believing the game was harmful to eyesight. The ban was lifted when this was proven false.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'medium',
    citation: 'https://www.ittf.com/history/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-med-008',
    text: 'The fastest recorded tennis serve was over 160 mph.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Sam Groth holds the record at 163.7 mph (263.4 km/h), set in 2012. John Isner holds the ATP-recognized record at 157.2 mph.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'medium',
    citation: 'https://www.atptour.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-med-009',
    text: 'Soccer players run an average of exactly 7 miles per game, with goalkeepers running the same distance.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Midfielders average about 7 miles per game, but goalkeepers typically cover only 3-4 miles. Different positions cover very different distances.',
    errorPattern: 'confident-specificity',
    subject: 'Sports',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-med-010',
    text: 'The NBA three-point line is 23 feet 9 inches from the basket at the top of the arc.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The NBA three-point arc is 23 feet 9 inches at the top and 22 feet in the corners. College and high school distances are shorter.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'medium',
    citation: 'https://www.nba.com/news/rule-book',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // SPORTS - HARD
  {
    id: 'sports-hard-001',
    text: 'The ancient Olympic Games allowed women to compete in chariot racing because they could enter horses as owners without being present.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Cynisca of Sparta became the first woman to win at the Olympics (396 BCE) as a horse owner. Women could not attend as spectators but could win as owners.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'hard',
    citation: 'https://olympics.com/ioc/ancient-olympic-games',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-hard-002',
    text: 'Professional athletes who train at high altitude have more red blood cells, which is why altitude training is banned by most sports organizations.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Altitude training does increase red blood cells and IS legal in virtually all sports. It is considered a natural training method, unlike blood doping.',
    errorPattern: 'false-causation',
    subject: 'Sports',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-hard-003',
    text: 'Bowling was once banned in America because it was associated with gambling.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Connecticut banned nine-pin bowling in 1841 due to gambling. Legend says ten-pin bowling was invented to circumvent this law.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'hard',
    citation: 'https://www.bowl.com/History',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-hard-004',
    text: 'Studies show that sports performance is significantly affected by uniform color, with teams wearing red winning 60% more often across all sports.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'Some studies show red uniforms correlate with higher win rates in combat sports, but the effect varies greatly by sport and is not 60% across all sports.',
    errorPattern: 'statistical-manipulation',
    subject: 'Sports',
    difficulty: 'hard',
    citation: 'https://www.nature.com/articles/436900a',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-hard-005',
    text: 'The FIFA World Cup trophy is made of solid 18-karat gold and weighs approximately 13.6 pounds.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The current FIFA World Cup trophy (since 1974) is 18-karat solid gold, weighing 6.175 kg (13.6 lbs). Winners receive a gold-plated replica.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'hard',
    citation: 'https://www.fifa.com/worldcup/trophy',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-hard-006',
    text: 'According to Harvard Medical School research, heading a soccer ball causes immediate measurable brain damage after each header.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Research shows cumulative concerns about repeated heading, but a single header does not cause immediate measurable brain damage. The claim exaggerates study findings.',
    errorPattern: 'appeal-to-authority',
    subject: 'Sports',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-hard-007',
    text: 'The fastest recorded shot in ice hockey was 108.8 mph, faster than the average MLB fastball.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Zdeno Chara holds the record at 108.8 mph (2012). The average MLB fastball is about 92-94 mph, making hockey shots faster.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'hard',
    citation: 'https://www.nhl.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-hard-008',
    text: 'Home field advantage in sports exists partly because visiting teams experience jet lag and unfamiliar environments, reducing their testosterone levels by up to 30%.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Home field advantage is real and travel is a factor, but the 30% testosterone reduction is not supported by scientific evidence. Multiple factors contribute to home advantage.',
    errorPattern: 'statistical-manipulation',
    subject: 'Sports',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-hard-009',
    text: 'Sumo wrestling has weight classes just like boxing and MMA to ensure fair competition.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Professional sumo wrestling has no weight classes. Smaller wrestlers can and do compete against much larger opponents. Skill and technique can overcome size differences.',
    errorPattern: 'plausible-adjacency',
    subject: 'Sports',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'sports-hard-010',
    text: 'The shortest NBA player in history, Muggsy Bogues, was 5 feet 3 inches tall and played 14 seasons.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Tyrone "Muggsy" Bogues stood 5\'3" and played 14 NBA seasons (1987-2001), proving height is not the only factor in basketball success.',
    errorPattern: null,
    subject: 'Sports',
    difficulty: 'hard',
    citation: 'https://www.nba.com/stats/player/203',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // OCEAN
  {
    id: 'ocean-001',
    text: 'More people have been to the Moon than to the deepest part of the ocean.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: '12 people have walked on the Moon, while only a handful have reached Challenger Deep in the Mariana Trench.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'medium',
    citation: 'https://oceanservice.noaa.gov/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'ocean-002',
    text: 'Starfish can regenerate their entire body from just one arm.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Many starfish species can regenerate an entire new body from a single arm if it includes part of the central disc.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/animal/sea-star',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'ocean-003',
    text: "The ocean produces 70% of Earth's oxygen.",
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Ocean phytoplankton DO produce significant oxygen (roughly 50-80%), but "70%" is a rough estimate. The exact percentage varies.',
    errorPattern: 'confident-specificity',
    subject: 'Marine Biology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'ocean-004',
    text: 'Jellyfish have been on Earth longer than dinosaurs.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Jellyfish fossils date back over 500 million years. Dinosaurs first appeared about 240 million years ago.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/animal/jellyfish',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ENVIRONMENT
  {
    id: 'env-001',
    text: 'There is enough gold in the ocean to give every person on Earth 9 pounds.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The ocean contains about 20 million tons of dissolved gold. However, it\'s so diluted that extraction is impossible.',
    errorPattern: null,
    subject: 'Chemistry',
    difficulty: 'hard',
    citation: 'https://oceanservice.noaa.gov/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'env-002',
    text: 'A single tree can absorb 48 pounds of carbon dioxide per year.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'A mature tree absorbs about 48 pounds of CO2 annually, though the amount varies by species and conditions.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'medium',
    citation: 'https://www.arborday.org/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'env-003',
    text: 'Plastic takes exactly 1,000 years to decompose.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Plastic decomposition time varies enormously (20-500+ years) depending on type and conditions. "Exactly 1,000 years" is fabricated.',
    errorPattern: 'confident-specificity',
    subject: 'Chemistry',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== ELEMENTARY (K-5) CLAIMS ====================
  // Simple, concrete, fun facts for young learners (ages 5-11)

  {
    id: 'elem-bio-001',
    text: 'A giraffe\'s tongue is about 18-20 inches long and is purple-black to protect it from sunburn.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Giraffes have dark tongues (purple, black, or dark blue) with melanin that protects against UV rays while eating from trees for hours.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.nationalgeographic.com/animals/mammals/facts/giraffe',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-bio-002',
    text: 'Butterflies taste with their feet.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Butterflies have taste sensors (chemoreceptors) on their feet! When they land on a plant, they can taste it immediately.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.scientificamerican.com/article/butterflies-taste-with-their-feet/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-bio-003',
    text: 'Dogs can only see in black and white.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Dogs can see colors! They see blues and yellows well, but have trouble with reds and greens. They see fewer colors than humans, but not just black and white.',
    errorPattern: 'myth-perpetuation',
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-bio-004',
    text: 'Elephants are the only animals that can\'t jump.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While elephants can\'t jump, they\'re not alone! Sloths, hippos, and rhinos can\'t jump either. Many heavy animals can\'t.',
    errorPattern: 'confident-specificity',
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-bio-005',
    text: 'A snail can sleep for 3 years.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Some snails can enter a deep sleep (estivation) during dry conditions for up to 3 years to survive without water!',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.britannica.com/animal/snail',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-bio-006',
    text: 'Octopuses have three hearts and blue blood.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Two hearts pump blood to the gills, one pumps to the body. Their blood is blue because it uses copper (not iron like ours) to carry oxygen.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://ocean.si.edu/ocean-life/invertebrates/octopus',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-bio-007',
    text: 'Cows have exactly four stomachs.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Cows have ONE stomach with FOUR compartments (rumen, reticulum, omasum, abomasum). It\'s one stomach divided into parts, not four separate stomachs.',
    errorPattern: 'plausible-adjacency',
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-bio-008',
    text: 'A group of owls is called a "parliament."',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The collective noun for owls is a "parliament," possibly because owls are often seen as wise, like parliament members.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.merriam-webster.com/dictionary/parliament',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-bio-009',
    text: 'Sharks are older than trees.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Sharks appeared about 450 million years ago. Trees only appeared about 350 million years ago. Sharks are 100 million years older!',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.smithsonianmag.com/smart-news/sharks-are-older-trees-180957312/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-bio-010',
    text: 'Penguins have knees hidden under their feathers.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Penguins do have knees! Their legs are structured with knees hidden inside their bodies, which is why they waddle.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.britannica.com/animal/penguin',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-bio-011',
    text: 'A frog must close its eyes to swallow food.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Frogs push their eyes down to help push food down their throats! They literally use their eyeballs to swallow.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.scientificamerican.com/article/frogs-use-their-eyes-to-swallow-prey/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-bio-012',
    text: 'Cats always land on their feet because they have exactly 9 lives.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Cats often land on their feet because they have a flexible spine and inner ear balance. The "9 lives" is just a saying - cats have one life like all animals.',
    errorPattern: 'myth-perpetuation',
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-astro-001',
    text: 'A day on Venus is longer than a year on Venus.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Venus rotates very slowly! One day (rotation) takes 243 Earth days, but one year (orbit) only takes 225 Earth days.',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://solarsystem.nasa.gov/planets/venus/overview/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-astro-002',
    text: 'The Moon makes its own light like the Sun does.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The Moon doesn\'t make light - it reflects sunlight! The Sun is a star that makes light from nuclear fusion. The Moon is just a giant rock.',
    errorPattern: 'plausible-adjacency',
    subject: 'Astronomy',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-astro-003',
    text: 'You could fit all the other planets between Earth and the Moon.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The average Earth-Moon distance is about 384,400 km. All the other planets lined up equal about 380,000 km!',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'medium',
    gradeLevel: 'elementary',
    citation: 'https://www.nasa.gov/audience/forstudents/k-4/stories/distances-in-space',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-astro-004',
    text: 'Saturn would float if you could find a bathtub big enough.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Saturn is the only planet less dense than water (0.687 g/cm³ vs water\'s 1 g/cm³). It would float like a giant beach ball!',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://solarsystem.nasa.gov/planets/saturn/overview/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-astro-005',
    text: 'There are exactly 100 billion stars in our galaxy.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Scientists estimate 100-400 billion stars in the Milky Way, but we can\'t count them exactly! "Exactly 100 billion" is made up.',
    errorPattern: 'confident-specificity',
    subject: 'Astronomy',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-phys-001',
    text: 'Hot water can freeze faster than cold water under certain conditions.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'This is called the Mpemba effect! Under certain conditions, hot water freezes faster than cold water. Scientists are still studying why.',
    errorPattern: null,
    subject: 'Physics',
    difficulty: 'medium',
    gradeLevel: 'elementary',
    citation: 'https://www.scientificamerican.com/article/mpemba-effect-hot-water-freezes-faster-cold/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-phys-002',
    text: 'Light from the Sun takes exactly 1 minute to reach Earth.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Sunlight takes about 8 minutes and 20 seconds to reach Earth, not 1 minute. "Exactly 1 minute" is fabricated.',
    errorPattern: 'confident-specificity',
    subject: 'Physics',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-phys-003',
    text: 'A rainbow always has exactly 7 colors.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'A rainbow is actually a continuous spectrum! We see it as 7 colors (ROYGBIV) because that\'s how Isaac Newton described it, but there are infinite colors blending into each other.',
    errorPattern: 'confident-specificity',
    subject: 'Physics',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-geo-001',
    text: 'The Amazon River flows through Argentina.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The Amazon River flows through Brazil, Peru, and Colombia, but NOT Argentina. Argentina is too far south.',
    errorPattern: 'geographic-fabrication',
    subject: 'Geography',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-geo-002',
    text: 'Australia is both a country and a continent.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Australia is the only place that is both a country (with a government) and a continent (a major landmass). It\'s unique!',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.britannica.com/place/Australia',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-geo-003',
    text: 'Mount Everest grows about half an inch taller every year.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The collision of tectonic plates pushes Mount Everest up by about 4mm (roughly half an inch) per year!',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'medium',
    gradeLevel: 'elementary',
    citation: 'https://www.nationalgeographic.com/science/article/151214-everest-grows-higher-nepal-earthquake',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-hist-001',
    text: 'Cleopatra lived closer in time to the first iPhone than to the building of the pyramids.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Great Pyramid was built around 2560 BCE. Cleopatra died in 30 BCE (2,530 years later). iPhone came in 2007 CE (2,037 years after Cleopatra). Mind-blowing!',
    errorPattern: null,
    subject: 'History',
    difficulty: 'medium',
    gradeLevel: 'elementary',
    citation: 'https://www.britannica.com/biography/Cleopatra-queen-of-Egypt',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-hist-002',
    text: 'Vikings wore helmets with horns on them.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'This is a popular myth! Real Viking helmets were plain metal or leather. The horned helmets idea came from old operas and movies.',
    errorPattern: 'myth-perpetuation',
    subject: 'History',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-hist-003',
    text: 'The Eiffel Tower can grow 6 inches taller in summer.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'When metal heats up, it expands! In hot summer weather, the iron in the Eiffel Tower expands, making it about 6 inches taller.',
    errorPattern: null,
    subject: 'History',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.toureiffel.paris/en/news/history-and-culture',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-human-001',
    text: 'Your nose and ears never stop growing your entire life.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Unlike bones, the cartilage in your nose and ears keeps growing slowly throughout your life. That\'s why older people have bigger noses and ears!',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.scientificamerican.com/article/why-do-noses-and-ears-continue-to-grow/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-human-002',
    text: 'You are about 1 cm taller in the morning than at night.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The cartilage between your spine bones (vertebrae) compresses during the day from gravity. You\'re slightly shorter at night and "taller" after sleeping!',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.scientificamerican.com/article/why-am-i-taller-in-the-morning/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-human-003',
    text: 'Humans have exactly 206 bones at all ages.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Babies are born with about 270-300 bones! Many fuse together as you grow. Adults have 206 bones, but babies have more.',
    errorPattern: 'confident-specificity',
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-human-004',
    text: 'Your stomach gets a new lining every few days.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Your stomach lining replaces itself every 3-4 days to protect against the strong acid that digests your food!',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.ncbi.nlm.nih.gov/books/NBK534792/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-chem-001',
    text: 'Honey never spoils and can last thousands of years.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Honey has natural preservatives! Archaeologists found 3,000-year-old honey in Egyptian tombs that was still edible.',
    errorPattern: null,
    subject: 'Chemistry',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.smithsonianmag.com/science-nature/the-science-behind-honeys-eternal-shelf-life-1218690/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-chem-002',
    text: 'Water is the only thing that exists as solid, liquid, and gas at normal Earth temperatures.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Water exists as ice (solid), water (liquid), and steam (gas) in everyday conditions on Earth. Most substances need extreme temperatures to change states.',
    errorPattern: null,
    subject: 'Chemistry',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.usgs.gov/special-topics/water-science-school/science/water-density',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-tech-001',
    text: 'The first computer weighed more than an elephant.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'ENIAC (1945) weighed about 30 tons (60,000 pounds)! An African elephant weighs about 6-7 tons. ENIAC was 4-5 elephants heavy!',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.britannica.com/technology/ENIAC',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'elem-tech-002',
    text: 'The @ symbol was invented for email.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The @ symbol existed for centuries! Merchants used it to mean "at the rate of" for prices. Ray Tomlinson chose it for email in 1971 because it was already on keyboards.',
    errorPattern: 'timeline-compression',
    subject: 'Computer Science',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== MIDDLE SCHOOL (6-8) CLAIMS ====================
  // Standard complexity, building critical thinking skills (ages 11-14)

  {
    id: 'mid-bio-001',
    text: 'The human body contains enough iron to make a 3-inch nail.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Adults have about 4 grams of iron in their blood and tissues. That\'s enough to forge a small nail!',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3685880/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-bio-002',
    text: 'Humans share exactly 60% of their DNA with bananas.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Humans share about 40-60% of DNA with bananas, but "exactly 60%" is too precise. The actual percentage depends on which genes you compare.',
    errorPattern: 'confident-specificity',
    subject: 'Biology',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-bio-003',
    text: 'A cockroach can live for several weeks without its head.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Cockroaches breathe through body segments and don\'t need their head for breathing. They eventually die from dehydration since they can\'t drink.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: 'https://www.scientificamerican.com/article/fact-or-fiction-a-luftwa/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-bio-004',
    text: 'The mantis shrimp can punch with the force of a bullet.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Mantis shrimp can accelerate their clubs at 50 mph, creating a force equivalent to a .22 caliber bullet! They can break aquarium glass.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: 'https://www.nature.com/articles/nature03657',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-bio-005',
    text: 'Tardigrades (water bears) can survive in the vacuum of space.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Tardigrades have survived exposure to space vacuum, extreme radiation, and temperatures from -458°F to 300°F! They enter a dried state called cryptobiosis.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: 'https://www.nature.com/articles/news.2008.1087',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-bio-006',
    text: 'Jellyfish are immortal and cannot die of old age.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Only ONE species (Turritopsis dohrnii) can potentially reverse aging. Most jellyfish die normally. Saying "jellyfish are immortal" is a huge overgeneralization.',
    errorPattern: 'plausible-adjacency',
    subject: 'Biology',
    difficulty: 'hard',
    gradeLevel: 'middle',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-chem-001',
    text: 'Glass is actually a liquid that flows very slowly over time.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'This is a popular myth! Glass is an amorphous solid. Old windows are thicker at the bottom because of how they were made, not because glass flows.',
    errorPattern: 'myth-perpetuation',
    subject: 'Chemistry',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-chem-002',
    text: 'Diamond and pencil graphite are both made of the same element - carbon.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Both diamond and graphite are pure carbon! The difference is how the carbon atoms are arranged. Diamond has a 3D crystal structure; graphite has flat sheets.',
    errorPattern: null,
    subject: 'Chemistry',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: 'https://www.britannica.com/science/carbon-chemical-element',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-phys-001',
    text: 'The speed of light is exactly 186,282 miles per second with no variation.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Light travels at about 186,282 miles/second IN A VACUUM. In water, glass, or other materials, light slows down significantly!',
    errorPattern: 'confident-specificity',
    subject: 'Physics',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-phys-002',
    text: 'If you could fold a piece of paper 42 times, it would reach the Moon.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Exponential growth! Each fold doubles thickness. 42 folds = 2^42 × 0.1mm ≈ 440,000 km. Moon is ~384,000 km away. (You can\'t actually fold paper that many times!)',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: 'https://www.sciencealert.com/fold-a-piece-of-paper-42-times-reach-moon',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-hist-001',
    text: 'Oxford University is older than the Aztec Empire.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Oxford started teaching in 1096; it was established by 1249. The Aztec Empire was founded in 1428 when they built Tenochtitlan. Oxford is about 200 years older!',
    errorPattern: null,
    subject: 'History',
    difficulty: 'hard',
    gradeLevel: 'middle',
    citation: 'https://www.ox.ac.uk/about/organisation/history',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-hist-002',
    text: 'Albert Einstein failed math as a child.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Einstein was excellent at math! This myth started from a misunderstanding of German grading scales. He excelled in math and physics from childhood.',
    errorPattern: 'myth-perpetuation',
    subject: 'History',
    difficulty: 'easy',
    gradeLevel: 'middle',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-hist-003',
    text: 'The shortest war in history lasted exactly 38 minutes.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'The Anglo-Zanzibar War (1896) lasted 38-45 minutes, but "exactly 38 minutes" is too precise. Historical records vary on the exact duration.',
    errorPattern: 'confident-specificity',
    subject: 'History',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-hist-004',
    text: 'Thomas Edison invented the light bulb.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Edison improved and commercialized the light bulb, but didn\'t invent it. At least 20 inventors created incandescent lights before him. He made it practical and affordable.',
    errorPattern: 'myth-perpetuation',
    subject: 'History',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-tech-001',
    text: 'The inventor of the Pringles can is buried in one.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Fredric Baur invented the Pringles can shape. When he died in 2008, his children honored his wishes by burying some of his ashes in a Pringles can!',
    errorPattern: null,
    subject: 'History',
    difficulty: 'easy',
    gradeLevel: 'middle',
    citation: 'https://time.com/3957979/pringles-can-inventor-buried/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-tech-002',
    text: 'Nintendo started as a video game company.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Nintendo was founded in 1889 as a playing card company! They made "Hanafuda" cards. They didn\'t enter video games until the 1970s.',
    errorPattern: 'timeline-compression',
    subject: 'History',
    difficulty: 'easy',
    gradeLevel: 'middle',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-geo-001',
    text: 'Russia has 11 time zones.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Russia spans 11 time zones, from UTC+2 to UTC+12. When it\'s midnight in western Russia, it\'s already 9 AM the next day in the far east!',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: 'https://www.britannica.com/place/Russia',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-geo-002',
    text: 'Canada has more lakes than the rest of the world combined.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Canada has an estimated 2 million lakes! That\'s more than all other countries combined. About 9% of Canada\'s area is covered by freshwater.',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: 'https://www.nrcan.gc.ca/our-natural-resources/water-resources/water-basics/8721',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-math-001',
    text: 'Zero is an even number.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Zero is even! An even number is divisible by 2 with no remainder. 0 ÷ 2 = 0 with no remainder. Zero also fits the pattern: -4, -2, 0, 2, 4...',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'easy',
    gradeLevel: 'middle',
    citation: 'https://www.britannica.com/science/zero-mathematics',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'mid-math-002',
    text: 'The number "googol" (1 followed by 100 zeros) is bigger than the number of atoms in the observable universe.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The observable universe has about 10^80 atoms. A googol is 10^100. That\'s 100 billion trillion times more than all atoms in the universe!',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'hard',
    gradeLevel: 'middle',
    citation: 'https://www.britannica.com/topic/googol',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== HIGH SCHOOL (9-12) CLAIMS ====================
  // More nuanced analysis required, complex topics (ages 14-18)

  {
    id: 'high-bio-001',
    text: 'Humans are 99.9% genetically identical to each other.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'All humans share about 99.9% of their DNA sequence. The 0.1% difference accounts for all variation in traits, disease susceptibility, and individual uniqueness.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'medium',
    gradeLevel: 'high',
    citation: 'https://www.genome.gov/about-genomics/fact-sheets/Genetics-vs-Genomics',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-bio-002',
    text: 'CRISPR technology allows scientists to cut and edit DNA with surgical precision, making zero unintended changes.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'CRISPR is powerful but not perfect. "Off-target effects" (unintended edits) remain a significant concern in gene editing research. "Zero unintended changes" is false.',
    errorPattern: 'confident-specificity',
    subject: 'Biotechnology',
    difficulty: 'hard',
    gradeLevel: 'high',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-bio-003',
    text: 'Mitochondria have their own DNA, separate from the cell\'s nuclear DNA.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Mitochondrial DNA (mtDNA) is inherited only from mothers. This supports the endosymbiotic theory that mitochondria were once free-living bacteria.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'medium',
    gradeLevel: 'high',
    citation: 'https://www.ncbi.nlm.nih.gov/books/NBK21251/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-bio-004',
    text: 'The appendix has no known function and is purely a vestigial organ.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Recent research shows the appendix serves as a "safe house" for beneficial gut bacteria and plays a role in immune function. It\'s not purely vestigial.',
    errorPattern: 'myth-perpetuation',
    subject: 'Biology',
    difficulty: 'medium',
    gradeLevel: 'high',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-chem-001',
    text: 'Neutron stars are so dense that a teaspoon would weigh about 6 billion tons.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Neutron stars pack the mass of the Sun into a sphere ~20 km across. A teaspoon of neutron star material would weigh billions of tons on Earth.',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'hard',
    gradeLevel: 'high',
    citation: 'https://www.nasa.gov/mission_pages/GLAST/science/neutron_stars.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-chem-002',
    text: 'Water is a universal solvent that can dissolve any substance given enough time.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While water dissolves many substances, it cannot dissolve everything. Oils, fats, and many plastics are hydrophobic and won\'t dissolve in water.',
    errorPattern: 'confident-specificity',
    subject: 'Chemistry',
    difficulty: 'medium',
    gradeLevel: 'high',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-phys-001',
    text: 'Nothing can travel faster than light in a vacuum.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'This is mostly true for matter and information, but some phenomena appear to exceed light speed: quantum entanglement "spooky action," the expanding universe, and phase velocities.',
    errorPattern: 'plausible-adjacency',
    subject: 'Physics',
    difficulty: 'hard',
    gradeLevel: 'high',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-phys-002',
    text: 'Schrödinger\'s cat was a real experiment performed on cats.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Schrödinger\'s cat was a thought experiment to illustrate problems with quantum superposition when applied to everyday objects. No cats were harmed - it was purely theoretical.',
    errorPattern: 'myth-perpetuation',
    subject: 'Physics',
    difficulty: 'easy',
    gradeLevel: 'high',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-phys-003',
    text: 'GPS satellites must account for time dilation from both special and general relativity to work accurately.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'GPS clocks tick faster (45 μs/day from general relativity) and slower (7 μs/day from special relativity). Without corrections, GPS would accumulate 10 km of error daily!',
    errorPattern: null,
    subject: 'Physics',
    difficulty: 'hard',
    gradeLevel: 'high',
    citation: 'https://www.scientificamerican.com/article/how-does-relativity-affect-gps/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-hist-001',
    text: 'The Library of Alexandria was destroyed in a single fire.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The Library of Alexandria declined over centuries through multiple events: Julius Caesar\'s fire, Christian riots, and gradual defunding. No single dramatic destruction.',
    errorPattern: 'timeline-compression',
    subject: 'History',
    difficulty: 'hard',
    gradeLevel: 'high',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-hist-002',
    text: 'The term "cold turkey" for quitting addictions comes from the goosebumps that appear during withdrawal.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'This is one popular etymology, as withdrawal causes goosebumps resembling plucked turkey skin. However, the true origin is uncertain and other theories exist.',
    errorPattern: 'appeal-to-authority',
    subject: 'History',
    difficulty: 'medium',
    gradeLevel: 'high',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-econ-001',
    text: 'The stock market has returned exactly 10% annually on average since its inception.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Historical S&P 500 returns average about 10-11% including dividends, but "exactly 10%" is too precise. Returns vary greatly by period measured and whether you adjust for inflation.',
    errorPattern: 'confident-specificity',
    subject: 'Mathematics',
    difficulty: 'medium',
    gradeLevel: 'high',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-psych-001',
    text: 'People only use 10% of their brain, leaving 90% untapped potential.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Brain scans show we use virtually all areas of our brain. Different regions activate for different tasks. The "10%" myth has been thoroughly debunked.',
    errorPattern: 'myth-perpetuation',
    subject: 'Neuroscience',
    difficulty: 'easy',
    gradeLevel: 'high',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-psych-002',
    text: 'The Dunning-Kruger effect shows that incompetent people think they\'re experts while experts doubt themselves.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'The original study showed low performers overestimate while high performers underestimate their ranking, but both groups\' estimates correlate with actual ability. The popular interpretation is oversimplified.',
    errorPattern: 'plausible-adjacency',
    subject: 'Neuroscience',
    difficulty: 'hard',
    gradeLevel: 'high',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-tech-001',
    text: 'Quantum computers can already crack any encryption used today.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Current quantum computers are too small and error-prone. Breaking RSA encryption would require millions of stable qubits. Today\'s quantum computers have ~1000 noisy qubits.',
    errorPattern: 'timeline-compression',
    subject: 'Computer Science',
    difficulty: 'hard',
    gradeLevel: 'high',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-tech-002',
    text: 'The first computer programmer was a woman named Ada Lovelace.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Ada Lovelace wrote the first algorithm intended for machine processing in 1843 for Charles Babbage\'s Analytical Engine, making her history\'s first computer programmer.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'easy',
    gradeLevel: 'high',
    citation: 'https://www.britannica.com/biography/Ada-Lovelace',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-env-001',
    text: 'Recycling a single aluminum can saves enough energy to power a TV for three hours.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Recycling aluminum uses 95% less energy than creating new aluminum from ore. That saved energy (about 255 watt-hours) can power a TV for 2-3 hours.',
    errorPattern: null,
    subject: 'Chemistry',
    difficulty: 'medium',
    gradeLevel: 'high',
    citation: 'https://www.epa.gov/recycle/recycling-basics',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-math-001',
    text: 'There are more possible chess games than atoms in the observable universe.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Shannon number estimates 10^120 possible chess games. The universe has about 10^80 atoms. Chess possibilities exceed atoms by a factor of 10^40!',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'hard',
    gradeLevel: 'high',
    citation: 'https://www.chess.com/article/view/how-many-possible-chess-games-are-there',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'high-math-002',
    text: 'Pi has been proven to contain every possible sequence of digits.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Pi is believed to be a "normal" number containing all digit sequences, but this has NOT been proven. It\'s one of mathematics\' open problems.',
    errorPattern: 'confident-specificity',
    subject: 'Mathematics',
    difficulty: 'hard',
    gradeLevel: 'high',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== COLLEGE/UNIVERSITY CLAIMS ====================
  // Sophisticated, specialized content for advanced critical thinking (ages 18+)

  {
    id: 'col-bio-001',
    text: 'Horizontal gene transfer means bacteria can acquire antibiotic resistance without reproducing.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Bacteria can transfer plasmids containing resistance genes directly to neighboring bacteria through conjugation, transformation, or transduction - without cell division.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'hard',
    gradeLevel: 'college',
    citation: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4536516/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-bio-002',
    text: 'Epigenetic changes can be inherited across multiple generations, proving Lamarckian evolution was correct.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Transgenerational epigenetic inheritance exists and resembles Lamarck\'s ideas, but this doesn\'t validate his full theory. Modern epigenetics works through different mechanisms than Lamarck proposed.',
    errorPattern: 'false-causation',
    subject: 'Biology',
    difficulty: 'hard',
    gradeLevel: 'college',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-bio-003',
    text: 'The human microbiome contains more bacterial cells than human cells in the body.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'The commonly cited 10:1 ratio is outdated. Current estimates suggest roughly 1:1 ratio (38 trillion bacteria vs 37 trillion human cells), varying by individual and conditions.',
    errorPattern: 'confident-specificity',
    subject: 'Biology',
    difficulty: 'hard',
    gradeLevel: 'college',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-phys-001',
    text: 'The Many-Worlds interpretation of quantum mechanics is now the accepted standard among physicists.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'There\'s no consensus interpretation of quantum mechanics. The Copenhagen interpretation remains widely taught. Many-Worlds has supporters but isn\'t "the accepted standard."',
    errorPattern: 'appeal-to-authority',
    subject: 'Physics',
    difficulty: 'hard',
    gradeLevel: 'college',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-phys-002',
    text: 'Virtual particles in quantum field theory violate conservation of energy, but only briefly.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'This depends on interpretation. Virtual particles are mathematical tools in perturbation theory. Whether they "exist" or "violate" conservation laws is debated. The energy-time uncertainty principle is often misapplied here.',
    errorPattern: 'plausible-adjacency',
    subject: 'Physics',
    difficulty: 'hard',
    gradeLevel: 'college',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-phys-003',
    text: 'Hawking radiation has been directly detected emanating from a black hole.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Hawking radiation is theoretically predicted but has never been directly detected from a black hole. It would be extremely faint. Lab analogues have shown similar effects.',
    errorPattern: 'timeline-compression',
    subject: 'Physics',
    difficulty: 'hard',
    gradeLevel: 'college',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-neuro-001',
    text: 'Neuroplasticity allows adult brains to form new neurons throughout life in multiple brain regions.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Adult neurogenesis is well-established in the hippocampus, but claims about "multiple brain regions" remain controversial. The extent of adult neurogenesis is still being researched.',
    errorPattern: 'confident-specificity',
    subject: 'Neuroscience',
    difficulty: 'hard',
    gradeLevel: 'college',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-neuro-002',
    text: 'The "left brain/right brain" theory accurately describes how creativity and logic are processed.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While hemispheric specialization exists for some functions (e.g., language in left hemisphere), the creative/logical divide is vastly oversimplified. Most tasks use both hemispheres.',
    errorPattern: 'myth-perpetuation',
    subject: 'Neuroscience',
    difficulty: 'medium',
    gradeLevel: 'college',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-stat-001',
    text: 'A statistically significant result means the finding is important and practically meaningful.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Statistical significance (p < 0.05) only means the result is unlikely under the null hypothesis. It says nothing about effect size or practical importance. Large samples can yield significant but trivial effects.',
    errorPattern: 'plausible-adjacency',
    subject: 'Mathematics',
    difficulty: 'hard',
    gradeLevel: 'college',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-stat-002',
    text: 'Bayesian statistics are now widely accepted as superior to frequentist approaches for all applications.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Both approaches have strengths. Bayesian methods excel with prior knowledge and small samples; frequentist methods are often simpler and preferred for regulatory approval. Neither is universally superior.',
    errorPattern: 'appeal-to-authority',
    subject: 'Mathematics',
    difficulty: 'hard',
    gradeLevel: 'college',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-econ-001',
    text: 'The efficient market hypothesis states it\'s impossible to consistently beat the market.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'EMH has different forms (weak, semi-strong, strong). Only the strong form claims markets are perfectly efficient. Evidence shows markets are mostly efficient but some anomalies exist.',
    errorPattern: 'plausible-adjacency',
    subject: 'Mathematics',
    difficulty: 'hard',
    gradeLevel: 'college',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-cs-001',
    text: 'P vs NP has been solved, proving P ≠ NP.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'P vs NP remains one of the seven Millennium Prize Problems. No accepted proof exists. Many attempts have been made, but none have withstood peer review.',
    errorPattern: 'timeline-compression',
    subject: 'Computer Science',
    difficulty: 'hard',
    gradeLevel: 'college',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-cs-002',
    text: 'Machine learning models can learn to be truly creative and generate genuinely novel ideas beyond their training data.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'ML models can produce novel combinations and interpolations, but whether this constitutes "true creativity" is philosophical. They cannot reason beyond patterns in training data in the way humans might.',
    errorPattern: 'plausible-adjacency',
    subject: 'Computer Science',
    difficulty: 'hard',
    gradeLevel: 'college',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-phil-001',
    text: 'Gödel\'s incompleteness theorems prove that mathematics is fundamentally flawed.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Gödel showed that sufficiently powerful formal systems can\'t prove all true statements about themselves. This is a feature of formal systems, not a "flaw" in mathematics itself.',
    errorPattern: 'plausible-adjacency',
    subject: 'Mathematics',
    difficulty: 'hard',
    gradeLevel: 'college',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-med-001',
    text: 'The placebo effect has been shown to cause measurable biological changes, not just subjective improvements.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Placebo responses involve real neurobiological changes: endorphin release, dopamine activity, and changes in brain regions associated with pain and mood. These are objective, measurable effects.',
    errorPattern: null,
    subject: 'Medical Science',
    difficulty: 'hard',
    gradeLevel: 'college',
    citation: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5841469/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-med-002',
    text: 'Vaccines cause autism according to the original 1998 Lancet study by Andrew Wakefield.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Wakefield\'s study was fraudulent and retracted. He lost his medical license. Dozens of large studies involving millions of children have found no link between vaccines and autism.',
    errorPattern: 'appeal-to-authority',
    subject: 'Medical Science',
    difficulty: 'medium',
    gradeLevel: 'college',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-env-001',
    text: 'Carbon capture technology can already remove CO2 from the atmosphere at a cost-effective scale.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Current carbon capture is expensive ($400-600+ per ton) and energy-intensive. It works but isn\'t cost-effective at climate-relevant scale. Costs are decreasing but remain prohibitive.',
    errorPattern: 'timeline-compression',
    subject: 'Chemistry',
    difficulty: 'hard',
    gradeLevel: 'college',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-hist-001',
    text: 'The "Dark Ages" were actually a period of significant technological and intellectual progress in many regions.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'While Western Europe saw decline after Rome, the Byzantine Empire, Islamic Golden Age, Tang Dynasty China, and other civilizations flourished. The term "Dark Ages" is Eurocentric and misleading.',
    errorPattern: null,
    subject: 'History',
    difficulty: 'medium',
    gradeLevel: 'college',
    citation: 'https://www.britannica.com/event/Dark-Ages',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'col-ling-001',
    text: 'The Sapir-Whorf hypothesis has been proven: language completely determines thought.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The strong version (linguistic determinism) is largely rejected. The weak version (linguistic relativity) - that language influences thought - has more support, but "completely determines" is too strong.',
    errorPattern: 'confident-specificity',
    subject: 'Neuroscience',
    difficulty: 'hard',
    gradeLevel: 'college',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== ADDITIONAL VIRAL CLAIMS (ALL LEVELS) ====================
  // High engagement potential - surprising, shareable facts

  {
    id: 'viral-001',
    text: 'A blue whale\'s heart is so big that a small child could crawl through its arteries.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'A blue whale\'s heart weighs about 400 pounds and is the size of a small car. Its aorta is large enough for a small child (or a basketball) to pass through!',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.nationalgeographic.com/animals/mammals/facts/blue-whale',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-002',
    text: 'There are more ways to arrange a deck of cards than atoms on Earth.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: '52! (52 factorial) ≈ 8×10^67 arrangements. Earth has about 10^50 atoms. Card arrangements outnumber atoms by a factor of 10^17 (100,000 trillion times)!',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'hard',
    gradeLevel: 'high',
    citation: 'https://www.sciencefocus.com/science/how-many-ways-can-you-shuffle-a-deck-of-cards/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-003',
    text: 'Honey bees can recognize human faces.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Studies show honeybees can learn to recognize and remember human faces, using the same part of their brain they use to recognize flowers!',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: 'https://www.science.org/doi/10.1126/science.1103676',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-004',
    text: 'Your brain uses more electricity than a light bulb.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Your brain uses about 12-20 watts - similar to a dim LED bulb but less than most incandescent bulbs (40-100W). "More than a light bulb" is misleading without specifying which bulb.',
    errorPattern: 'confident-specificity',
    subject: 'Biology',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-005',
    text: 'Bananas are radioactive.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Bananas contain potassium-40, a radioactive isotope. Eating one exposes you to about 0.01 millirem of radiation - completely harmless and far less than background radiation.',
    errorPattern: null,
    subject: 'Physics',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: 'https://www.epa.gov/radtown/natural-radioactivity-food',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-006',
    text: 'A cloud can weigh more than a million pounds.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'An average cumulus cloud contains about 500 million grams of water (1.1 million pounds)! It floats because the water is spread over a huge volume with air underneath.',
    errorPattern: null,
    subject: 'Physics',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: 'https://www.usgs.gov/special-topics/water-science-school/science/how-much-does-a-cloud-weigh',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-007',
    text: 'Wombat poop is cube-shaped.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Wombats produce cube-shaped droppings due to the varied elasticity of their intestinal walls. The cubes don\'t roll away, helping mark territory. Scientists won an Ig Nobel Prize studying this!',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.nationalgeographic.com/animals/article/wombat-poop-cubes-why-study-reveals-mystery',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-008',
    text: 'Scotland\'s national animal is the unicorn.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The unicorn has been a Scottish heraldic symbol since the 12th century! It represents purity, innocence, power, and independence - values important to Scottish identity.',
    errorPattern: null,
    subject: 'History',
    difficulty: 'easy',
    gradeLevel: 'elementary',
    citation: 'https://www.visitscotland.com/things-to-do/attractions/arts-culture/traditions/unicorn',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-009',
    text: 'There\'s a basketball court on the top floor of the Supreme Court building, nicknamed "the highest court in the land."',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'There\'s a full basketball court on the 5th floor above the courtrooms. It\'s nicknamed "the highest court in the land" - a legal pun since the Supreme Court is the highest court!',
    errorPattern: null,
    subject: 'History',
    difficulty: 'easy',
    gradeLevel: 'middle',
    citation: 'https://www.supremecourt.gov/about/buildingfeatures.aspx',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-010',
    text: 'Venus is the only planet that spins clockwise (viewed from above the north pole).',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Uranus also spins differently - it rotates on its side! And technically Venus and Uranus both have retrograde rotation. Saying Venus is "the only" planet is incorrect.',
    errorPattern: 'confident-specificity',
    subject: 'Astronomy',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-011',
    text: 'The inventor of the frisbee was turned into a frisbee after death.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Walter Morrison, who invented the modern frisbee (Pluto Platter), died in 2010. His family had his ashes molded into memorial frisbees!',
    errorPattern: null,
    subject: 'History',
    difficulty: 'easy',
    gradeLevel: 'middle',
    citation: 'https://www.nytimes.com/2010/02/11/sports/11frisbee.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-012',
    text: 'Crows can remember human faces and hold grudges for years.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Research shows crows recognize faces and can hold grudges against people who threatened them. They even teach their offspring to recognize "dangerous" humans!',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: 'https://www.scientificamerican.com/article/grudge-holding-crows-pass-on-their-anger-to-family-and-friends/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-013',
    text: 'A jiffy is an actual unit of time equal to 1/100th of a second.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'A "jiffy" has multiple definitions in science! In physics it\'s often the time for light to travel one centimeter (~33.4 picoseconds). In electronics it can mean 1/60 second. "1/100th of a second" is just one definition.',
    errorPattern: 'confident-specificity',
    subject: 'Physics',
    difficulty: 'medium',
    gradeLevel: 'high',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-014',
    text: 'Human trafficking happens more in the US than in any other country.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'This is difficult to measure accurately. While the US has more identified victims due to better reporting systems, raw numbers don\'t mean more trafficking. Many countries have worse problems but less reporting.',
    errorPattern: 'statistical-manipulation',
    subject: 'Geography',
    difficulty: 'hard',
    gradeLevel: 'high',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-015',
    text: 'A day on Mercury (sunrise to sunrise) lasts 176 Earth days.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Mercury rotates very slowly (59 Earth days) and orbits fast (88 days). Combined with its elliptical orbit, one solar day (noon to noon) equals 176 Earth days!',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'hard',
    gradeLevel: 'high',
    citation: 'https://solarsystem.nasa.gov/planets/mercury/overview/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-016',
    text: 'You can see the Great Wall of China from the Moon.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'You can\'t see the Great Wall from the Moon - it\'s far too thin (15-30 feet wide). Apollo astronauts confirmed you can barely see continental outlines from the Moon, let alone structures.',
    errorPattern: 'myth-perpetuation',
    subject: 'Geography',
    difficulty: 'easy',
    gradeLevel: 'middle',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-017',
    text: 'Every person alive today shares a common ancestor who lived only about 3,000 years ago.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Mathematical modeling shows everyone alive today shares at least one ancestor from roughly 1000 BCE. This "identical ancestors point" is surprisingly recent due to population interconnection.',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'hard',
    gradeLevel: 'high',
    citation: 'https://www.nature.com/articles/nature02842',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-018',
    text: 'The smell after rain has an official name: petrichor.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Australian scientists coined "petrichor" in 1964 from Greek "petra" (stone) and "ichor" (fluid of the gods). It\'s caused by bacteria, plant oils, and ozone released during rain.',
    errorPattern: null,
    subject: 'Chemistry',
    difficulty: 'easy',
    gradeLevel: 'middle',
    citation: 'https://www.scientificamerican.com/article/storm-scents-it-s-true-you-can-smell-incoming-rain/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-019',
    text: 'Maine is the closest US state to Africa.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Due to Earth\'s curvature, Quoddy Head in Maine is about 3,154 miles from El Beddouza, Morocco. Florida looks closer on flat maps but is actually farther due to map projection distortion!',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'hard',
    gradeLevel: 'high',
    citation: 'https://www.worldatlas.com/articles/the-african-nation-closest-to-the-united-states.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'viral-020',
    text: 'A strawberry is not actually a berry, but a banana is.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Botanically, berries develop from a single ovary with seeds inside the flesh. Strawberries develop from multiple ovaries (making them "aggregate fruits"). Bananas meet the berry definition!',
    errorPattern: null,
    subject: 'Biology',
    difficulty: 'medium',
    gradeLevel: 'middle',
    citation: 'https://www.britannica.com/science/berry-plant-reproductive-body',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== PSYCHOLOGY CLAIMS (30 total) ====================
  // EASY (10)
  {
    id: 'psych-easy-001',
    text: 'Humans have five basic senses: sight, hearing, smell, taste, and touch.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'While these are the traditional five senses, scientists recognize many more including balance, temperature, pain, and body position (proprioception).',
    errorPattern: null,
    subject: 'Psychology',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/sense',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-easy-002',
    text: 'People can only remember about 7 items in their short-term memory at once.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Miller\'s Law suggests short-term memory holds 7±2 items. This is why phone numbers are typically 7 digits.',
    errorPattern: null,
    subject: 'Psychology',
    difficulty: 'easy',
    citation: 'https://www.simplypsychology.org/short-term-memory.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-easy-003',
    text: 'Yawning is contagious because humans are naturally empathetic.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Research shows contagious yawning is linked to empathy and social bonding. People with higher empathy scores yawn more when seeing others yawn.',
    errorPattern: null,
    subject: 'Psychology',
    difficulty: 'easy',
    citation: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5765204/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-easy-004',
    text: 'The brain stops developing at age 18 when you become an adult.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The brain continues developing until around age 25, particularly the prefrontal cortex responsible for decision-making and impulse control.',
    errorPattern: 'confident-specificity',
    subject: 'Psychology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-easy-005',
    text: 'Smiling can actually make you feel happier, even if you force it.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The facial feedback hypothesis suggests that facial expressions can influence emotions. Smiling triggers release of mood-boosting neurotransmitters.',
    errorPattern: null,
    subject: 'Psychology',
    difficulty: 'easy',
    citation: 'https://www.psychologytoday.com/us/blog/prefrontal-nudity/201208/smile-powerful-tool',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-easy-006',
    text: 'Humans use exactly 10% of their brain capacity.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'This is a popular myth. Brain imaging shows that virtually all areas of the brain are active at various times, even during sleep.',
    errorPattern: 'myth-perpetuation',
    subject: 'Psychology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-easy-007',
    text: 'Dreams only occur during REM (rapid eye movement) sleep.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While most vivid dreams occur during REM sleep, dreams can occur during any sleep stage. Non-REM dreams tend to be less vivid and memorable.',
    errorPattern: 'plausible-adjacency',
    subject: 'Psychology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-easy-008',
    text: 'Children learn languages more easily than adults.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The critical period hypothesis suggests children have a window of enhanced language acquisition ability, particularly before puberty.',
    errorPattern: null,
    subject: 'Psychology',
    difficulty: 'easy',
    citation: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6195691/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-easy-009',
    text: 'Being in a good mood helps you think more creatively.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Research shows positive moods broaden attention and thinking, leading to more creative problem-solving and flexible thinking.',
    errorPattern: null,
    subject: 'Psychology',
    difficulty: 'easy',
    citation: 'https://www.apa.org/monitor/nov01/posemotions',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-easy-010',
    text: 'People can be classified as either left-brained or right-brained personality types.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Brain imaging studies show both hemispheres work together in most tasks. The "left-brain/right-brain" personality theory is not supported by science.',
    errorPattern: 'myth-perpetuation',
    subject: 'Psychology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  // MEDIUM (10)
  {
    id: 'psych-med-001',
    text: 'The average person has between 12,000 and 60,000 thoughts per day.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'This statistic is widely cited but its original source is unclear. Thought counting is extremely difficult to measure scientifically.',
    errorPattern: 'confident-specificity',
    subject: 'Psychology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-med-002',
    text: 'The placebo effect can cause real, measurable physical changes in the body.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Placebos can trigger real physiological responses including pain relief, improved symptoms, and measurable brain changes.',
    errorPattern: null,
    subject: 'Psychology',
    difficulty: 'medium',
    citation: 'https://www.health.harvard.edu/mental-health/the-power-of-the-placebo-effect',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-med-003',
    text: 'Multitasking makes people more productive because the brain can focus on multiple things at once.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The brain rapidly switches between tasks rather than truly multitasking. This "task switching" actually reduces efficiency and increases errors.',
    errorPattern: 'false-causation',
    subject: 'Psychology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-med-004',
    text: 'The bystander effect means people are less likely to help in emergencies when others are present.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Research shows people are less likely to offer help when others are present due to diffusion of responsibility and social influence.',
    errorPattern: null,
    subject: 'Psychology',
    difficulty: 'medium',
    citation: 'https://www.simplypsychology.org/bystander-effect.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-med-005',
    text: 'Opposites attract in romantic relationships according to psychological research.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Research consistently shows similarity is a stronger predictor of attraction. People tend to be drawn to others who share their values, attitudes, and interests.',
    errorPattern: 'myth-perpetuation',
    subject: 'Psychology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-med-006',
    text: 'Memories are stored exactly like video recordings and can be perfectly recalled.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Memory is reconstructive, not reproductive. Each recall can alter the memory, and false memories can be created through suggestion.',
    errorPattern: 'plausible-adjacency',
    subject: 'Psychology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-med-007',
    text: 'Color can affect mood and behavior, which is why hospitals often use calming colors.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Color psychology shows colors can influence mood, heart rate, and behavior. Blue and green tend to be calming, while red can increase arousal.',
    errorPattern: null,
    subject: 'Psychology',
    difficulty: 'medium',
    citation: 'https://www.verywellmind.com/color-psychology-2795824',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-med-008',
    text: 'Humans can consciously process about 40-50 bits of information per second.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'While our senses take in millions of bits per second, conscious processing is limited to about 40-50 bits. Most processing is unconscious.',
    errorPattern: null,
    subject: 'Psychology',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/science/information-theory/Physiology',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-med-009',
    text: 'Learning styles (visual, auditory, kinesthetic) determine how each person learns best.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Despite popularity, research does not support matching teaching to learning styles. People learn best through multiple modalities and varied approaches.',
    errorPattern: 'myth-perpetuation',
    subject: 'Psychology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-med-010',
    text: 'Sleep plays a critical role in memory consolidation and learning.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'During sleep, the brain consolidates memories from short-term to long-term storage. Sleep deprivation significantly impairs learning.',
    errorPattern: null,
    subject: 'Psychology',
    difficulty: 'medium',
    citation: 'https://www.sleepfoundation.org/how-sleep-works/memory-and-sleep',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  // HARD (10)
  {
    id: 'psych-hard-001',
    text: 'Stanford University research proved that violent video games directly cause aggressive behavior in children.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Research shows correlations but not direct causation. Many studies have methodological issues, and the relationship is complex with many factors involved.',
    errorPattern: 'appeal-to-authority',
    subject: 'Psychology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-hard-002',
    text: 'The Dunning-Kruger effect shows that less competent people often overestimate their abilities.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'This cognitive bias, identified in 1999, shows that people with limited knowledge in an area tend to overestimate their competence.',
    errorPattern: null,
    subject: 'Psychology',
    difficulty: 'hard',
    citation: 'https://www.apa.org/news/press/releases/2020/02/dunning-kruger-effect',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-hard-003',
    text: 'Subliminal advertising can make people buy products they do not want.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While subliminal perception exists, research shows it has minimal effect on complex behaviors like purchasing decisions. The famous "Drink Coca-Cola" study was fabricated.',
    errorPattern: 'myth-perpetuation',
    subject: 'Psychology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-hard-004',
    text: 'Cognitive behavioral therapy (CBT) has been shown to physically change brain structure.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Brain imaging studies show CBT can produce measurable changes in brain activity and structure, particularly in areas involved in emotion regulation.',
    errorPattern: null,
    subject: 'Psychology',
    difficulty: 'hard',
    citation: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4881251/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-hard-005',
    text: 'Studies show that 93% of communication is nonverbal (body language and tone).',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'This misquotes Mehrabian\'s research, which only applied to specific situations involving feelings and attitudes, not all communication.',
    errorPattern: 'statistical-manipulation',
    subject: 'Psychology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-hard-006',
    text: 'Mirror neurons fire both when we perform an action and when we observe someone else performing it.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Discovered in the 1990s, mirror neurons are believed to play a role in understanding others\' actions, learning, and empathy.',
    errorPattern: null,
    subject: 'Psychology',
    difficulty: 'hard',
    citation: 'https://www.apa.org/monitor/2007/10/mirror',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-hard-007',
    text: 'The Stanford Prison Experiment proved that good people will inevitably become cruel in certain situations.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'The study showed situational factors affect behavior, but later analysis revealed significant methodological flaws and researcher interference.',
    errorPattern: null,
    subject: 'Psychology',
    difficulty: 'hard',
    citation: 'https://www.vox.com/2018/6/13/17449118/stanford-prison-experiment-fraud-psychology-replication',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-hard-008',
    text: 'Hypnosis can help people accurately recover repressed memories of childhood.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Hypnosis can increase confidence in memories but not accuracy. It can actually lead to creation of false memories, which is why hypnotically-recovered memories are not accepted in many courts.',
    errorPattern: 'plausible-adjacency',
    subject: 'Psychology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-hard-009',
    text: 'Emotional intelligence (EQ) is a better predictor of life success than IQ.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Both EQ and IQ contribute to success in different ways. Research shows IQ predicts academic success while EQ may better predict social and leadership success.',
    errorPattern: 'false-causation',
    subject: 'Psychology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'psych-hard-010',
    text: 'Neuroplasticity allows the adult brain to form new neural connections throughout life.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Once thought impossible, we now know the adult brain can reorganize itself by forming new neural connections, especially through learning and experience.',
    errorPattern: null,
    subject: 'Psychology',
    difficulty: 'hard',
    citation: 'https://www.brainfacts.org/brain-anatomy-and-function/anatomy/2019/neuroplasticity-020519',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== TECHNOLOGY CLAIMS (30 total) ====================
  // EASY (10)
  {
    id: 'tech-easy-001',
    text: 'WiFi stands for Wireless Fidelity.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'WiFi is not an acronym. It was created as a catchy brand name by a marketing firm. It does not stand for anything.',
    errorPattern: 'myth-perpetuation',
    subject: 'Technology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-easy-002',
    text: 'The first computer mouse was made of wood.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Douglas Engelbart invented the first mouse in 1964. It was a wooden shell with two metal wheels.',
    errorPattern: null,
    subject: 'Technology',
    difficulty: 'easy',
    citation: 'https://www.computerhistory.org/revolution/input-output/14/350',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-easy-003',
    text: 'Email existed before the World Wide Web.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Email was invented in 1971. The World Wide Web was not created until 1989 by Tim Berners-Lee.',
    errorPattern: null,
    subject: 'Technology',
    difficulty: 'easy',
    citation: 'https://www.computerhistory.org/internethistory/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-easy-004',
    text: 'A byte is made up of 8 bits.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'A byte consists of 8 bits and can represent values from 0 to 255 (or a single character).',
    errorPattern: null,
    subject: 'Technology',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/technology/byte',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-easy-005',
    text: 'The first text message ever sent said "Merry Christmas."',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'On December 3, 1992, engineer Neil Papworth sent "Merry Christmas" from a computer to a Vodafone phone.',
    errorPattern: null,
    subject: 'Technology',
    difficulty: 'easy',
    citation: 'https://www.vodafone.com/news/technology/25-years-of-text-messages',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-easy-006',
    text: 'Bluetooth technology is named after a Viking king.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Bluetooth is named after Harald "Bluetooth" Gormsson, a 10th-century Danish king who united warring tribes—like the technology unites devices.',
    errorPattern: null,
    subject: 'Technology',
    difficulty: 'easy',
    citation: 'https://www.bluetooth.com/about-us/bluetooth-origin/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-easy-007',
    text: 'Leaving your phone plugged in overnight will damage the battery.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Modern smartphones have circuits that stop charging when the battery is full. Overnight charging does not damage modern lithium-ion batteries.',
    errorPattern: 'myth-perpetuation',
    subject: 'Technology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-easy-008',
    text: 'The "@" symbol in email addresses is called "at sign" in English.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'In English it\'s the "at sign." Other languages have creative names: snail (Italian), monkey tail (German), elephant trunk (Danish).',
    errorPattern: null,
    subject: 'Technology',
    difficulty: 'easy',
    citation: 'https://www.smithsonianmag.com/science-nature/the-accidental-history-of-the-symbol-18054936/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-easy-009',
    text: 'More people in the world have mobile phones than have toilets.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'UN data shows over 6 billion people have mobile phone access, while only about 4.5 billion have access to proper sanitation.',
    errorPattern: null,
    subject: 'Technology',
    difficulty: 'easy',
    citation: 'https://www.un.org/sustainabledevelopment/water-and-sanitation/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-easy-010',
    text: 'Deleting files from your computer permanently erases them immediately.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Deleted files go to the Recycle Bin first. Even after emptying it, data often remains on the drive until overwritten. Special software can recover "deleted" files.',
    errorPattern: 'plausible-adjacency',
    subject: 'Technology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  // MEDIUM (10)
  {
    id: 'tech-med-001',
    text: 'The QWERTY keyboard layout was designed to slow down typists.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'QWERTY was designed to prevent jamming on mechanical typewriters by separating common letter pairs, not specifically to slow typists. The slowing was a side effect.',
    errorPattern: null,
    subject: 'Technology',
    difficulty: 'medium',
    citation: 'https://www.smithsonianmag.com/arts-culture/fact-of-fiction-the-legend-of-the-qwerty-keyboard-49863249/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-med-002',
    text: 'Incognito/private browsing mode makes you completely anonymous online.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Private browsing only prevents your browser from saving history locally. Your ISP, employer, and websites can still track your activity.',
    errorPattern: 'plausible-adjacency',
    subject: 'Technology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-med-003',
    text: 'The first 1GB hard drive was the size of a refrigerator and cost $40,000.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'IBM\'s first gigabyte drive (1980) weighed 550 pounds and cost $40,000. Today, 1GB costs less than a penny.',
    errorPattern: null,
    subject: 'Technology',
    difficulty: 'medium',
    citation: 'https://www.computerhistory.org/storageengine/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-med-004',
    text: 'Airplane mode prevents all wireless signals from being transmitted by your phone.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Airplane mode disables cellular, WiFi, and Bluetooth transmissions. Modern phones allow you to re-enable WiFi and Bluetooth while staying in airplane mode.',
    errorPattern: null,
    subject: 'Technology',
    difficulty: 'medium',
    citation: 'https://support.apple.com/en-us/HT204234',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-med-005',
    text: 'Macs cannot get viruses or malware.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Macs can get malware, though they\'re targeted less often than Windows. As Mac popularity grows, so does Mac malware.',
    errorPattern: 'myth-perpetuation',
    subject: 'Technology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-med-006',
    text: 'GPS satellites transmit your location to your phone.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'GPS satellites only broadcast time signals. Your device calculates its own position by measuring the time delay from multiple satellites.',
    errorPattern: 'plausible-adjacency',
    subject: 'Technology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-med-007',
    text: 'The internet and the World Wide Web are the same thing.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The internet is the global network of connected computers. The World Wide Web is a service (websites) that runs on the internet, along with email and other services.',
    errorPattern: 'plausible-adjacency',
    subject: 'Technology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-med-008',
    text: 'The first webcam was created to monitor a coffee pot at Cambridge University.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'In 1991, researchers set up a camera to check coffee pot levels remotely, avoiding wasted trips to an empty pot.',
    errorPattern: null,
    subject: 'Technology',
    difficulty: 'medium',
    citation: 'https://www.bbc.com/news/technology-20439301',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-med-009',
    text: 'Closing apps running in the background always improves phone battery life.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Modern phones manage background apps efficiently. Force-closing apps can actually use more battery when you reopen them.',
    errorPattern: 'false-causation',
    subject: 'Technology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-med-010',
    text: 'The first computer programmer was a woman named Ada Lovelace.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Ada Lovelace wrote the first algorithm intended for machine processing in the 1840s, making her the first computer programmer.',
    errorPattern: null,
    subject: 'Technology',
    difficulty: 'medium',
    citation: 'https://www.computerhistory.org/babbage/adalovelace/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  // HARD (10)
  {
    id: 'tech-hard-001',
    text: 'Quantum computers can solve all problems faster than classical computers.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Quantum computers excel at specific problems (cryptography, optimization) but are not universally faster. Many problems are solved equally fast or faster by classical computers.',
    errorPattern: 'plausible-adjacency',
    subject: 'Technology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-hard-002',
    text: 'More data has been created in the last two years than in all of human history before that.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'IDC estimates 90% of the world\'s data was created in the last few years. Data creation is growing exponentially.',
    errorPattern: null,
    subject: 'Technology',
    difficulty: 'hard',
    citation: 'https://www.idc.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-hard-003',
    text: 'AI can now perfectly replicate human creativity and original thought.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While AI can generate creative content, it learns from existing human work and combines patterns. It does not truly understand or experience creativity.',
    errorPattern: 'plausible-adjacency',
    subject: 'Technology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-hard-004',
    text: 'The Bitcoin network uses more electricity than some entire countries.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Bitcoin mining consumes approximately 120+ TWh annually—more than countries like Argentina or Norway.',
    errorPattern: null,
    subject: 'Technology',
    difficulty: 'hard',
    citation: 'https://ccaf.io/cbnsi/cbeci',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-hard-005',
    text: 'MIT studies prove that 5G cell towers cause cancer in humans.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: '5G uses non-ionizing radiation that lacks energy to damage DNA. No credible studies link 5G to cancer. The claim falsely invokes authority.',
    errorPattern: 'appeal-to-authority',
    subject: 'Technology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-hard-006',
    text: 'Moore\'s Law is a physical law of nature governing computer chip development.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Moore\'s Law is an observation/prediction by Gordon Moore about transistor density doubling, not a physical law. It\'s an industry trend that\'s slowing down.',
    errorPattern: 'plausible-adjacency',
    subject: 'Technology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-hard-007',
    text: 'Deep learning neural networks work exactly like human brains.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Neural networks are inspired by brains but work very differently. They lack the complex biology, consciousness, and flexible learning of real brains.',
    errorPattern: 'plausible-adjacency',
    subject: 'Technology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-hard-008',
    text: 'The entire internet would fit on about 50 million hard drives.',
    answer: 'MIXED',
    source: 'ai-generated',
    explanation: 'Estimates vary wildly. The "surface web" is smaller, but including the deep web, the internet contains zettabytes of data—constantly growing.',
    errorPattern: 'confident-specificity',
    subject: 'Technology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-hard-009',
    text: 'CAPTCHA stands for "Completely Automated Public Turing test to tell Computers and Humans Apart."',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'CAPTCHA is indeed an acronym. It was developed at Carnegie Mellon University in 2000 to prevent automated abuse.',
    errorPattern: null,
    subject: 'Technology',
    difficulty: 'hard',
    citation: 'https://www.captcha.net/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'tech-hard-010',
    text: 'Blockchain technology can only be used for cryptocurrencies.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Blockchain has many applications including supply chain tracking, voting systems, medical records, and digital identity verification.',
    errorPattern: 'plausible-adjacency',
    subject: 'Technology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== ECONOMICS CLAIMS (30 total) ====================
  // EASY (10)
  {
    id: 'econ-easy-new-001',
    text: 'Money did not always exist—people used to trade goods directly.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Before money, people used barter systems. Money was invented to make trade easier and more efficient.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'easy',
    citation: 'https://www.federalreserveeducation.org/about-the-fed/history',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-easy-new-002',
    text: 'The dollar sign ($) comes from the letters U and S overlapping.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The $ symbol likely evolved from the Spanish peso abbreviation "ps" gradually merging. The U.S. theory is a popular myth.',
    errorPattern: 'myth-perpetuation',
    subject: 'Economics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-easy-new-003',
    text: 'Banks can lend out more money than they actually have in deposits.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Through fractional reserve banking, banks only keep a fraction of deposits and lend out the rest, effectively creating new money.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'easy',
    citation: 'https://www.federalreserve.gov/monetarypolicy/reservereq.htm',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-easy-004',
    text: 'Supply and demand is a basic principle that affects prices.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'When demand is high and supply is low, prices rise. When supply exceeds demand, prices fall. This is fundamental economics.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'easy',
    citation: 'https://www.investopedia.com/terms/l/law-of-supply-demand.asp',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-easy-005',
    text: 'Inflation means prices generally increase over time.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Inflation is the rate at which the general price level rises, meaning each dollar buys less over time.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'easy',
    citation: 'https://www.bls.gov/cpi/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-easy-006',
    text: 'All countries use the same currency for international trade.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Countries have different currencies. International trade involves currency exchange, though the US dollar is commonly used.',
    errorPattern: 'plausible-adjacency',
    subject: 'Economics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-easy-007',
    text: 'A budget helps people track their income and spending.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'A budget is a plan for managing money by tracking income, expenses, and savings goals.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'easy',
    citation: 'https://www.consumerfinance.gov/about-us/blog/budgeting-how-to-create-a-budget-and-stick-with-it/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-easy-008',
    text: 'Companies that sell similar products are in competition with each other.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Competition occurs when businesses offer similar goods or services, which typically leads to better prices and quality for consumers.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'easy',
    citation: 'https://www.investopedia.com/terms/c/competition.asp',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-easy-009',
    text: 'Paper money has been used for over 2,000 years.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Paper money was first used in China around the 7th century CE, roughly 1,300 years ago—not 2,000 years.',
    errorPattern: 'confident-specificity',
    subject: 'Economics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-easy-010',
    text: 'Interest is money paid for borrowing money.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Interest is the cost of borrowing. Lenders charge interest as compensation for the risk and the time value of money.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'easy',
    citation: 'https://www.investopedia.com/terms/i/interest.asp',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  // MEDIUM (10)
  {
    id: 'econ-med-new-001',
    text: 'The stock market always goes up over time, so investing is risk-free.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While markets have trended upward historically, they experience crashes and recessions. Investing always carries risk.',
    errorPattern: 'false-causation',
    subject: 'Economics',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-med-new-002',
    text: 'GDP (Gross Domestic Product) measures the total value of goods and services produced in a country.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'GDP is the standard measure of a country\'s economic output, calculated quarterly and annually.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'medium',
    citation: 'https://www.bea.gov/data/gdp',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-med-003',
    text: 'A recession is officially defined as two consecutive quarters of negative GDP growth.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'This is a common definition, but the official determination (in the US) is made by the NBER considering multiple factors, not just GDP.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'medium',
    citation: 'https://www.nber.org/research/business-cycle-dating',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-med-004',
    text: 'The minimum wage automatically adjusts for inflation each year.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'In most places, minimum wage requires legislative action to change. It does not automatically adjust for inflation.',
    errorPattern: 'plausible-adjacency',
    subject: 'Economics',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-med-005',
    text: 'Monopolies are illegal in all cases in the United States.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Being a monopoly is not illegal. Using monopoly power to harm competition is illegal under antitrust law. Some monopolies are even government-granted (utilities, patents).',
    errorPattern: 'plausible-adjacency',
    subject: 'Economics',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-med-006',
    text: 'The Federal Reserve can print unlimited money without consequences.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Printing too much money leads to inflation and currency devaluation. Central banks must balance money supply carefully.',
    errorPattern: 'plausible-adjacency',
    subject: 'Economics',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-med-007',
    text: 'Unemployment rate counts everyone who does not have a job.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The unemployment rate only counts people actively seeking work. Retirees, students, and those who stopped looking are not counted.',
    errorPattern: 'plausible-adjacency',
    subject: 'Economics',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-med-008',
    text: 'Trade between countries can benefit both countries involved.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Comparative advantage theory shows that trade allows countries to specialize in what they produce most efficiently, benefiting all parties.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'medium',
    citation: 'https://www.investopedia.com/terms/c/comparativeadvantage.asp',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-med-009',
    text: 'Compound interest can significantly increase savings over time.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Compound interest earns interest on both the principal and accumulated interest, leading to exponential growth over time.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'medium',
    citation: 'https://www.investor.gov/additional-resources/information/compound-interest',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-med-010',
    text: 'A trade deficit means a country imports more than it exports.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'A trade deficit occurs when a country\'s imports exceed its exports. This is not necessarily bad—it depends on other economic factors.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'medium',
    citation: 'https://www.census.gov/foreign-trade/data/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  // HARD (10)
  {
    id: 'econ-hard-001',
    text: 'According to Yale economists, raising taxes always decreases economic growth.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The relationship between taxes and growth is complex. Some research shows moderate taxes fund services that support growth. The claim falsely invokes authority.',
    errorPattern: 'appeal-to-authority',
    subject: 'Economics',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-hard-002',
    text: 'The 2008 financial crisis was largely caused by subprime mortgage lending.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Risky mortgage lending to unqualified borrowers, combined with complex financial products and inadequate regulation, triggered the 2008 crisis.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'hard',
    citation: 'https://www.federalreservehistory.org/essays/great-recession-of-200709',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-hard-003',
    text: 'Behavioral economics shows that humans always make rational economic decisions.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Behavioral economics specifically studies how psychological factors lead to irrational decisions, contradicting traditional "rational actor" assumptions.',
    errorPattern: 'plausible-adjacency',
    subject: 'Economics',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-hard-004',
    text: 'The Phillips curve suggests a stable trade-off between inflation and unemployment.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'The original Phillips curve showed an inverse relationship, but this broke down in the 1970s stagflation. The relationship is now understood to be more complex.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'hard',
    citation: 'https://www.federalreserve.gov/monetarypolicy/2018-07-mpr-summary.htm',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-hard-005',
    text: 'Studies prove that increasing minimum wage to $15/hour causes exactly 500,000 job losses.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Research on minimum wage effects is mixed. The precise number is fabricated, and effects depend on local economic conditions.',
    errorPattern: 'confident-specificity',
    subject: 'Economics',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-hard-006',
    text: 'The Laffer Curve suggests that at some tax rates, lowering taxes can increase government revenue.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Laffer Curve illustrates that at very high tax rates, people may work/invest less, so moderate rates could generate more revenue.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'hard',
    citation: 'https://www.investopedia.com/terms/l/laffercurve.asp',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-hard-007',
    text: 'Quantitative easing (QE) is when central banks buy assets to increase money supply.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'QE involves central banks purchasing government bonds and other securities to inject money into the economy and lower interest rates.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'hard',
    citation: 'https://www.federalreserve.gov/monetarypolicy/bst_openmarketops.htm',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-hard-008',
    text: 'Game theory in economics was developed to predict stock market prices.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Game theory studies strategic decision-making between rational actors. It was not developed for stock prediction but for analyzing competitive situations.',
    errorPattern: 'plausible-adjacency',
    subject: 'Economics',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-hard-009',
    text: 'The "invisible hand" concept suggests markets self-regulate without any government intervention.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'Adam Smith\'s "invisible hand" describes how self-interest can benefit society, but Smith also acknowledged roles for government in providing public goods.',
    errorPattern: null,
    subject: 'Economics',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/topic/invisible-hand',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'econ-hard-010',
    text: 'Cryptocurrency is regulated by the Federal Reserve like traditional currency.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Cryptocurrencies are decentralized and not controlled by any central bank. Regulatory frameworks vary by country and are still developing.',
    errorPattern: 'plausible-adjacency',
    subject: 'Economics',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== CIVICS CLAIMS (30 total) ====================
  // EASY (10)
  {
    id: 'civics-easy-001',
    text: 'The United States has three branches of government.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The three branches are Legislative (Congress), Executive (President), and Judicial (Supreme Court), designed to balance power.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'easy',
    citation: 'https://www.usa.gov/branches-of-government',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-easy-002',
    text: 'The President of the United States can serve unlimited terms.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The 22nd Amendment (1951) limits presidents to two four-year terms, or ten years total if they assumed office mid-term.',
    errorPattern: 'plausible-adjacency',
    subject: 'Civics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-easy-003',
    text: 'The Constitution is the supreme law of the United States.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Article VI establishes the Constitution as the "supreme Law of the Land." All other laws must comply with it.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'easy',
    citation: 'https://www.archives.gov/founding-docs/constitution',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-easy-004',
    text: 'All citizens 18 and older have the right to vote in the United States.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The 26th Amendment (1971) guarantees voting rights to citizens 18 and older. Some restrictions apply to certain felons depending on state law.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'easy',
    citation: 'https://www.usa.gov/voting-laws',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-easy-005',
    text: 'The Bill of Rights contains the first ten amendments to the Constitution.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Bill of Rights, ratified in 1791, includes freedoms of speech, religion, press, assembly, and other fundamental rights.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'easy',
    citation: 'https://www.archives.gov/founding-docs/bill-of-rights',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-easy-006',
    text: 'Congress is made up of the Senate and the House of Representatives.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The bicameral Congress has 100 Senators (2 per state) and 435 Representatives (based on state population).',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'easy',
    citation: 'https://www.congress.gov/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-easy-007',
    text: 'The Supreme Court has exactly 9 justices as required by the Constitution.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The Constitution does not specify the number of justices. Congress sets the number by law. It has been 9 since 1869 but has changed historically.',
    errorPattern: 'confident-specificity',
    subject: 'Civics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-easy-008',
    text: 'Only Congress can declare war according to the Constitution.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Article I, Section 8 gives Congress the power to declare war, though presidents have used military force without formal declarations.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'easy',
    citation: 'https://constitution.congress.gov/browse/article-1/section-8/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-easy-009',
    text: 'The President is elected directly by the popular vote.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The President is elected by the Electoral College. A candidate can win the presidency without winning the popular vote.',
    errorPattern: 'plausible-adjacency',
    subject: 'Civics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-easy-010',
    text: 'Freedom of speech means you can say anything without any consequences.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The First Amendment protects speech from government restriction, but there are limits (incitement, defamation). Private consequences still apply.',
    errorPattern: 'plausible-adjacency',
    subject: 'Civics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  // MEDIUM (10)
  {
    id: 'civics-med-001',
    text: 'The Vice President is the President of the Senate and can vote to break ties.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Article I, Section 3 makes the VP President of the Senate, voting only when there is a tie.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'medium',
    citation: 'https://www.senate.gov/about/officers-staff/vice-president.htm',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-med-002',
    text: 'Amendments to the Constitution require unanimous approval from all states.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Amendments require 2/3 vote in Congress and ratification by 3/4 of states (38 states)—not unanimous approval.',
    errorPattern: 'confident-specificity',
    subject: 'Civics',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-med-003',
    text: 'The Supreme Court can declare laws unconstitutional through judicial review.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Judicial review, established in Marbury v. Madison (1803), allows courts to strike down laws that violate the Constitution.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'medium',
    citation: 'https://www.oyez.org/cases/1789-1850/5us137',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-med-004',
    text: 'Senators serve 6-year terms while Representatives serve 2-year terms.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Constitution sets different term lengths: 6 years for Senators (1/3 up for election every 2 years) and 2 years for Representatives.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'medium',
    citation: 'https://www.senate.gov/artandhistory/history/common/briefing/Constitution_Senate.htm',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-med-005',
    text: 'The President can issue executive orders that have the force of law.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Executive orders direct federal agencies and have legal force, though they can be overturned by courts or Congress.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'medium',
    citation: 'https://www.archives.gov/federal-register/executive-orders',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-med-006',
    text: 'The federal government has power over everything not specifically given to states.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The 10th Amendment reserves powers not delegated to the federal government to the states or people—the opposite of this claim.',
    errorPattern: 'plausible-adjacency',
    subject: 'Civics',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-med-007',
    text: 'A filibuster allows senators to delay or block legislation through extended debate.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Senate rules allow unlimited debate. A filibuster continues until 60 senators vote for cloture to end debate.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'medium',
    citation: 'https://www.senate.gov/about/powers-procedures/filibusters-cloture.htm',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-med-008',
    text: 'Impeachment by the House of Representatives removes a president from office.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Impeachment is only an accusation (like an indictment). The Senate must then hold a trial and convict by 2/3 vote to remove.',
    errorPattern: 'plausible-adjacency',
    subject: 'Civics',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-med-009',
    text: 'The Census is conducted every 10 years to count the population.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Article I, Section 2 requires a census every 10 years. Results determine House seats and federal funding allocation.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'medium',
    citation: 'https://www.census.gov/programs-surveys/decennial-census.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-med-010',
    text: 'Citizens can petition the government for a redress of grievances under the First Amendment.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The First Amendment protects the right to petition the government, allowing citizens to express concerns and seek change.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'medium',
    citation: 'https://constitution.congress.gov/constitution/amendment-1/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  // HARD (10)
  {
    id: 'civics-hard-001',
    text: 'The Electoral College was created because the Founders did not trust direct democracy.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'The Electoral College was a compromise for multiple reasons: balancing state/federal power, slave state representation, and concerns about uninformed voters.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'hard',
    citation: 'https://www.archives.gov/electoral-college/history',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-hard-002',
    text: 'The Constitution explicitly mentions the separation of church and state.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The phrase "separation of church and state" does not appear in the Constitution. It comes from Jefferson\'s 1802 letter. The First Amendment prohibits establishing religion.',
    errorPattern: 'myth-perpetuation',
    subject: 'Civics',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-hard-003',
    text: 'According to Harvard Law School, the Second Amendment only protects militia membership.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The Supreme Court in DC v. Heller (2008) ruled the Second Amendment protects an individual right. The claim misattributes a position to Harvard.',
    errorPattern: 'appeal-to-authority',
    subject: 'Civics',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-hard-004',
    text: 'Gerrymandering is the practice of drawing electoral district boundaries to favor one party.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Named after Governor Elbridge Gerry, gerrymandering manipulates district shapes to concentrate or dilute voting power.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/topic/gerrymandering',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-hard-005',
    text: 'The Commerce Clause has been used to expand federal power over many activities.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Courts have interpreted Congress\'s power to regulate interstate commerce broadly, justifying federal regulation of many activities.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'hard',
    citation: 'https://www.law.cornell.edu/wex/commerce_clause',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-hard-006',
    text: 'Studies show that 95% of Americans support strict voter ID laws.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Polls show varying levels of support depending on how questions are asked. The specific 95% figure is fabricated.',
    errorPattern: 'statistical-manipulation',
    subject: 'Civics',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-hard-007',
    text: 'The President has absolute immunity from criminal prosecution while in office.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'DOJ policy says sitting presidents cannot be indicted, but this has never been tested in court. Recent Supreme Court cases have addressed aspects of presidential immunity.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'hard',
    citation: 'https://www.justice.gov/olc/opinion/sitting-president%E2%80%99s-amenability-indictment-and-criminal-prosecution',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-hard-008',
    text: 'Citizens United v. FEC ruled that corporations have the same free speech rights as individuals.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'The ruling held that political spending is protected speech and cannot be limited for corporations, but the implications are debated.',
    errorPattern: null,
    subject: 'Civics',
    difficulty: 'hard',
    citation: 'https://www.oyez.org/cases/2008/08-205',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-hard-009',
    text: 'The 14th Amendment\'s Equal Protection Clause was originally intended to protect women\'s rights.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The 14th Amendment (1868) was enacted to protect formerly enslaved people. Courts later applied it to gender discrimination, but that was not its original purpose.',
    errorPattern: 'timeline-compression',
    subject: 'Civics',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'civics-hard-010',
    text: 'The doctrine of nullification allows states to invalidate federal laws they disagree with.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Though historically claimed, nullification has been consistently rejected by courts. The Supremacy Clause makes federal law supreme over state law.',
    errorPattern: 'myth-perpetuation',
    subject: 'Civics',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== MARINE BIOLOGY CLAIMS (30 total) ====================
  // EASY (10)
  {
    id: 'marine-easy-001',
    text: 'Dolphins are fish because they live in the ocean.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Dolphins are mammals, not fish. They breathe air, are warm-blooded, give birth to live young, and nurse their babies with milk.',
    errorPattern: 'plausible-adjacency',
    subject: 'Marine Biology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-easy-002',
    text: 'Octopuses have three hearts.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Octopuses have two branchial hearts that pump blood through the gills, and one systemic heart that pumps it through the body.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/animal/octopus-mollusk',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-easy-003',
    text: 'Sea horses are the only fish where males give birth.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Male seahorses carry eggs in a pouch and give birth to live young—unique among fish and rare in the animal kingdom.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'easy',
    citation: 'https://ocean.si.edu/ocean-life/fish/seahorse',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-easy-004',
    text: 'Sharks must keep swimming constantly or they will die.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Some sharks (like great whites) must swim to breathe, but many species can pump water over their gills while resting on the ocean floor.',
    errorPattern: 'myth-perpetuation',
    subject: 'Marine Biology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-easy-005',
    text: 'Blue whales are the largest animals to ever live on Earth.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Blue whales can reach 100 feet long and weigh 200 tons—larger than any dinosaur. Their hearts alone can weigh as much as a car.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/animal/blue-whale',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-easy-006',
    text: 'Coral reefs are made by tiny animals called polyps.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Coral polyps are tiny animals that build hard calcium carbonate skeletons. Over time, these accumulate to form massive reef structures.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'easy',
    citation: 'https://oceanservice.noaa.gov/education/tutorial_corals/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-easy-007',
    text: 'Fish have very short memories, lasting only 3 seconds.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Fish can remember things for months or even years. Studies show they can learn mazes, recognize other fish, and remember feeding schedules.',
    errorPattern: 'myth-perpetuation',
    subject: 'Marine Biology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-easy-008',
    text: 'Sea turtles return to the same beach where they were born to lay their eggs.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Female sea turtles return to their natal beach to nest, traveling thousands of miles to find the exact location using Earth\'s magnetic field.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'easy',
    citation: 'https://www.seeturtles.org/sea-turtle-nesting',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-easy-009',
    text: 'Whales and dolphins sleep with one half of their brain at a time.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'This unihemispheric sleep allows them to continue swimming and surfacing to breathe while resting half their brain.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'easy',
    citation: 'https://www.scientificamerican.com/article/how-do-whales-and-lufti/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-easy-010',
    text: 'Clownfish and sea anemones help each other survive.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'This is called mutualism. Clownfish are protected by the anemone\'s stinging tentacles, while they provide the anemone food and nutrients.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'easy',
    citation: 'https://ocean.si.edu/ocean-life/fish/clownfish-and-sea-anemone',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  // MEDIUM (10)
  {
    id: 'marine-med-001',
    text: 'The giant squid has the largest eyes of any animal, up to 10 inches in diameter.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Giant squid eyes are about the size of dinner plates—the largest in the animal kingdom. They help detect bioluminescence in deep, dark waters.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'medium',
    citation: 'https://ocean.si.edu/ocean-life/invertebrates/giant-squid',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-med-002',
    text: 'All jellyfish stings are deadly to humans.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Most jellyfish stings cause only mild discomfort. Only a few species, like the box jellyfish, are potentially fatal to humans.',
    errorPattern: 'plausible-adjacency',
    subject: 'Marine Biology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-med-003',
    text: 'Bioluminescence is light produced by living organisms through chemical reactions.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Many deep-sea creatures produce their own light through bioluminescence, using it for communication, attracting prey, or camouflage.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'medium',
    citation: 'https://ocean.si.edu/ocean-life/fish/bioluminescence',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-med-004',
    text: 'Great white sharks are responsible for most shark attacks on humans.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While great whites cause some attacks, bull sharks and tiger sharks are responsible for more. Overall, shark attacks are extremely rare.',
    errorPattern: 'myth-perpetuation',
    subject: 'Marine Biology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-med-005',
    text: 'The mantis shrimp can punch with the force of a bullet.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Mantis shrimp can strike at 50 mph, creating cavitation bubbles. The impact produces force equivalent to a .22 caliber bullet.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'medium',
    citation: 'https://www.nature.com/articles/nature03111',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-med-006',
    text: 'Sea cucumbers can expel their internal organs as a defense mechanism.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'When threatened, sea cucumbers can eject their digestive organs through their anus to distract predators. They regenerate these organs later.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'medium',
    citation: 'https://ocean.si.edu/ocean-life/invertebrates/sea-cucumber',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-med-007',
    text: 'All species of whales use echolocation to navigate.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Only toothed whales (dolphins, orcas, sperm whales) use echolocation. Baleen whales like blue whales and humpbacks do not.',
    errorPattern: 'plausible-adjacency',
    subject: 'Marine Biology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-med-008',
    text: 'The ocean\'s twilight zone (200-1000m deep) contains more fish than all other ocean zones combined.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Recent research suggests the mesopelagic zone may contain 10 billion tons of fish—far more than previously estimated.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'medium',
    citation: 'https://www.whoi.edu/know-your-ocean/ocean-topics/ocean-life/twilight-zone/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-med-009',
    text: 'Parrotfish create much of the white sand on tropical beaches.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Parrotfish eat coral and excrete it as fine white sand. A single parrotfish can produce hundreds of pounds of sand per year.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'medium',
    citation: 'https://www.floridamuseum.ufl.edu/fish/discover/species-profiles/parrotfish/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-med-010',
    text: 'Hagfish can produce enough slime in minutes to fill a bucket.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Hagfish produce copious amounts of slime when stressed. Their slime glands can generate liters of mucus almost instantly.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'medium',
    citation: 'https://www.scientificamerican.com/article/hagfish-slime-could-be-the-next-great-biomaterial/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  // HARD (10)
  {
    id: 'marine-hard-001',
    text: 'According to NOAA research, we have explored more than 80% of the ocean floor.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Only about 20-25% of the ocean floor has been mapped in detail. The deep ocean remains largely unexplored.',
    errorPattern: 'appeal-to-authority',
    subject: 'Marine Biology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-hard-002',
    text: 'Some deep-sea fish can withstand pressure over 1,000 times atmospheric pressure.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Fish in the hadal zone (below 6,000m) experience pressures over 1,100 atmospheres. They have evolved special proteins and cell membranes.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'hard',
    citation: 'https://www.nature.com/articles/s41559-019-0864-8',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-hard-003',
    text: 'Studies show that exactly 71% of marine species are threatened with extinction.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While many marine species face threats, the specific 71% figure is fabricated. Extinction risk varies greatly by species and region.',
    errorPattern: 'confident-specificity',
    subject: 'Marine Biology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-hard-004',
    text: 'Hydrothermal vent ecosystems are powered by chemosynthesis rather than photosynthesis.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Bacteria at hydrothermal vents use chemical energy from minerals to produce food, supporting entire ecosystems without sunlight.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'hard',
    citation: 'https://oceanexplorer.noaa.gov/facts/vents.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-hard-005',
    text: 'The immortal jellyfish can theoretically live forever by reverting to its juvenile form.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Turritopsis dohrnii can transform back into a polyp when stressed or old, potentially repeating its life cycle indefinitely.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'hard',
    citation: 'https://www.nhm.ac.uk/discover/immortal-jellyfish.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-hard-006',
    text: 'Ocean acidification directly causes fish to lose their sense of smell.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'Research shows acidification affects fish sensory abilities and behavior, but the exact mechanisms and extent are still being studied.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'hard',
    citation: 'https://www.nature.com/articles/nclimate1324',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-hard-007',
    text: 'Cephalopods like octopuses can edit their own RNA, unlike almost all other animals.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Octopuses, squid, and cuttlefish extensively edit their RNA, allowing rapid adaptation. This is extremely rare in the animal kingdom.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'hard',
    citation: 'https://www.cell.com/cell/fulltext/S0092-8674(17)30344-6',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-hard-008',
    text: 'Whale falls (dead whales on the seafloor) can support ecosystems for decades.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'A single whale carcass can sustain a complex community of organisms for 50-100 years as it slowly decomposes.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'hard',
    citation: 'https://oceanexplorer.noaa.gov/facts/whale-fall.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-hard-009',
    text: 'Shark populations have declined by 90% since 1970 according to marine biologists.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'Many shark populations have declined dramatically (70%+ for oceanic sharks), but the exact figure varies by species and region.',
    errorPattern: 'statistical-manipulation',
    subject: 'Marine Biology',
    difficulty: 'hard',
    citation: 'https://www.nature.com/articles/s41586-021-03173-7',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'marine-hard-010',
    text: 'The pistol shrimp snap is the second loudest sound in the ocean after sperm whale clicks.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Pistol shrimp snaps can reach 218 decibels, creating a cavitation bubble that stuns prey. Only sperm whale clicks are louder.',
    errorPattern: null,
    subject: 'Marine Biology',
    difficulty: 'hard',
    citation: 'https://www.science.org/content/article/pistol-shrimp-s-snap-may-be-loudest-ocean-sound',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== COMPUTER SCIENCE CLAIMS (30 total) ====================
  // EASY (10)
  {
    id: 'cs-easy-001',
    text: 'A computer program is a set of instructions that tells a computer what to do.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Programs are sequences of instructions written in programming languages that computers execute to perform tasks.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/technology/computer-program',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-easy-002',
    text: 'Computers only understand two digits: 0 and 1.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Computers use binary (base-2) because electronic circuits have two states: on (1) and off (0). All data is ultimately stored in binary.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/technology/binary-number-system',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-easy-003',
    text: 'A bug in a computer program is an error that causes unexpected behavior.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The term "bug" has been used since the 1940s to describe errors in computer systems. Debugging is the process of finding and fixing bugs.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'easy',
    citation: 'https://www.computerhistory.org/tdih/september/9/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-easy-004',
    text: 'Algorithms always require computers to run.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'An algorithm is just a step-by-step procedure. Recipes, assembly instructions, and math procedures are all algorithms that humans follow without computers.',
    errorPattern: 'plausible-adjacency',
    subject: 'Computer Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-easy-005',
    text: 'RAM stands for Random Access Memory.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'RAM is temporary, fast memory that stores data currently being used. It loses its contents when power is turned off.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/technology/RAM-computing',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-easy-006',
    text: 'All programming languages are exactly the same, just with different names.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Programming languages differ in syntax, purpose, and capabilities. Python excels at readability, C at speed, JavaScript at web development.',
    errorPattern: 'plausible-adjacency',
    subject: 'Computer Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-easy-007',
    text: 'The CPU is often called the "brain" of the computer.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Central Processing Unit executes instructions and performs calculations. It\'s the primary component that processes data.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/technology/central-processing-unit',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-easy-008',
    text: 'Computers can only do one thing at a time.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Modern computers use multitasking and multi-core processors to run many programs simultaneously.',
    errorPattern: 'plausible-adjacency',
    subject: 'Computer Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-easy-009',
    text: 'An operating system manages computer hardware and software.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Operating systems like Windows, macOS, and Linux manage hardware resources and provide a platform for running applications.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/technology/operating-system',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-easy-010',
    text: 'A URL is the address of a webpage on the internet.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'URL stands for Uniform Resource Locator. It specifies the location of a resource on the internet.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'easy',
    citation: 'https://www.w3.org/Addressing/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  // MEDIUM (10)
  {
    id: 'cs-med-001',
    text: 'The first computer virus was created in 1983 as an experiment.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'The first virus in the wild was Elk Cloner (1982). The term "virus" was coined in 1983. Earlier self-replicating programs existed in the 1970s.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/technology/computer-virus',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-med-002',
    text: 'Encryption scrambles data so only authorized parties can read it.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Encryption converts readable data into ciphertext. Only those with the correct key can decrypt it back to readable form.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'medium',
    citation: 'https://www.nist.gov/cryptography',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-med-003',
    text: 'Machine learning and artificial intelligence are exactly the same thing.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Machine learning is a subset of AI. AI is the broader concept of machines mimicking intelligence; ML is specifically about learning from data.',
    errorPattern: 'plausible-adjacency',
    subject: 'Computer Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-med-004',
    text: 'A database is an organized collection of structured information.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Databases store and organize data for easy access, management, and updating. They power most applications and websites.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'medium',
    citation: 'https://www.oracle.com/database/what-is-database/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-med-005',
    text: 'All websites use HTML to create their structure.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'HTML (HyperText Markup Language) is the standard language for creating web pages. CSS styles them, JavaScript adds interactivity.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'medium',
    citation: 'https://www.w3.org/html/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-med-006',
    text: 'Open source software is always free of cost.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Open source refers to source code being available, not price. Companies can charge for support, hosting, or commercial versions.',
    errorPattern: 'plausible-adjacency',
    subject: 'Computer Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-med-007',
    text: 'Cloud computing means your data is stored on computers in the sky.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The "cloud" is a network of remote servers in data centers around the world, not literally in the sky. It\'s a metaphor for distributed computing.',
    errorPattern: 'plausible-adjacency',
    subject: 'Computer Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-med-008',
    text: 'Git is a version control system used by software developers.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Git tracks changes in code, enabling collaboration and history tracking. GitHub and GitLab are platforms built around Git.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'medium',
    citation: 'https://git-scm.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-med-009',
    text: 'HTTP and HTTPS are identical protocols with no security difference.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'HTTPS includes encryption (SSL/TLS) for security. HTTP transmits data in plain text, making it vulnerable to interception.',
    errorPattern: 'plausible-adjacency',
    subject: 'Computer Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-med-010',
    text: 'Recursion is when a function calls itself to solve a problem.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Recursive functions break problems into smaller versions of themselves. Classic examples include factorial calculation and tree traversal.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/science/recursion',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  // HARD (10)
  {
    id: 'cs-hard-001',
    text: 'According to MIT researchers, quantum computers will replace all classical computers by 2030.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Quantum computers excel at specific problems but cannot replace classical computers for general tasks. The timeline is fabricated.',
    errorPattern: 'appeal-to-authority',
    subject: 'Computer Science',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-hard-002',
    text: 'The halting problem proves that some computing problems are impossible to solve.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Alan Turing proved in 1936 that no algorithm can determine whether an arbitrary program will halt or run forever.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/science/halting-problem',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-hard-003',
    text: 'P vs NP is one of the most important unsolved problems in computer science.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'This question asks whether problems whose solutions can be quickly verified can also be quickly solved. Solving it carries a $1 million prize.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'hard',
    citation: 'https://www.claymath.org/millennium-problems/p-vs-np-problem',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-hard-004',
    text: 'Big O notation describes the exact running time of an algorithm.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Big O describes the upper bound of growth rate, not exact time. It shows how performance scales with input size, ignoring constants.',
    errorPattern: 'plausible-adjacency',
    subject: 'Computer Science',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-hard-005',
    text: 'The TCP/IP protocol stack has exactly 7 layers like the OSI model.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The OSI model has 7 layers, but TCP/IP uses 4 layers (Network Interface, Internet, Transport, Application). They\'re related but different.',
    errorPattern: 'confident-specificity',
    subject: 'Computer Science',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-hard-006',
    text: 'Functional programming languages do not allow changing variable values after assignment.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'Pure functional languages emphasize immutability, but many practical functional languages allow mutability. It\'s a spectrum.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/technology/functional-language',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-hard-007',
    text: 'A hash function can always be reversed to find the original input.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Cryptographic hash functions are designed to be one-way. Finding the original input from a hash should be computationally infeasible.',
    errorPattern: 'plausible-adjacency',
    subject: 'Computer Science',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-hard-008',
    text: 'Turing complete languages can theoretically compute anything that is computable.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'A Turing complete system can simulate any Turing machine. Most programming languages are Turing complete.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/technology/Turing-machine',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-hard-009',
    text: 'The Y2K bug would have crashed all computers worldwide exactly at midnight on January 1, 2000.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The Y2K bug was a real concern about date handling, but massive remediation efforts prevented most issues. Not all computers were affected, and timing varied.',
    errorPattern: 'confident-specificity',
    subject: 'Computer Science',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'cs-hard-010',
    text: 'NP-hard problems are considered intractable for large inputs.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'NP-hard problems have no known polynomial-time solutions. For large inputs, solving them exactly becomes practically impossible.',
    errorPattern: null,
    subject: 'Computer Science',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/science/NP-complete-problem',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== PHYSICS (Additional claims to reach 30) ====================
  // EASY (adding 10)
  {
    id: 'physics-easy-new-001',
    text: 'Light travels faster than sound.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Light travels at about 300,000 km/s in a vacuum. Sound travels at about 343 m/s in air—nearly a million times slower.',
    errorPattern: null,
    subject: 'Physics',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/speed-of-light',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'physics-easy-new-002',
    text: 'Gravity only exists on Earth.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Gravity exists everywhere in the universe. All objects with mass attract each other. Earth\'s gravity is just what we experience daily.',
    errorPattern: 'plausible-adjacency',
    subject: 'Physics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'physics-easy-new-003',
    text: 'Heat rises because hot air is less dense than cold air.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'When air heats up, molecules move faster and spread apart, making the air less dense. This lighter air rises above cooler, denser air.',
    errorPattern: null,
    subject: 'Physics',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/convection',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'physics-easy-new-004',
    text: 'The three states of matter are solid, liquid, and gas.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'While these are the most common states, there are others including plasma (found in stars and lightning) and more exotic states like Bose-Einstein condensates.',
    errorPattern: null,
    subject: 'Physics',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/state-of-matter',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'physics-easy-new-005',
    text: 'A rainbow contains exactly seven colors.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'A rainbow is a continuous spectrum of colors. The "seven colors" (ROYGBIV) is a simplification. There are infinite shades blending together.',
    errorPattern: 'confident-specificity',
    subject: 'Physics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'physics-easy-new-006',
    text: 'Magnets have two poles called north and south.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Every magnet has a north and south pole. Like poles repel, opposite poles attract. You cannot have a magnet with only one pole.',
    errorPattern: null,
    subject: 'Physics',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/magnet',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'physics-easy-new-007',
    text: 'In space, there is no gravity.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Gravity exists in space. Astronauts float because they\'re in constant free-fall around Earth, not because there\'s no gravity.',
    errorPattern: 'myth-perpetuation',
    subject: 'Physics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'physics-easy-new-008',
    text: 'Sound cannot travel through a vacuum.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Sound needs a medium (solid, liquid, or gas) to travel. In the vacuum of space, there are no molecules to carry sound waves.',
    errorPattern: null,
    subject: 'Physics',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/sound-physics',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'physics-easy-new-009',
    text: 'Electricity and lightning are the same thing.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'Lightning is a form of electricity (electrostatic discharge), but electricity encompasses many phenomena. Lightning is just one dramatic example.',
    errorPattern: null,
    subject: 'Physics',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/lightning-meteorology',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'physics-easy-new-010',
    text: 'An object in motion will stay in motion unless acted on by a force.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'This is Newton\'s First Law of Motion (Law of Inertia). Objects maintain their state unless an external force changes it.',
    errorPattern: null,
    subject: 'Physics',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/Newtons-laws-of-motion',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== MATHEMATICS (Additional claims to reach 30) ====================
  // EASY (adding 8)
  {
    id: 'math-easy-new-001',
    text: 'A triangle always has three sides.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'By definition, a triangle is a polygon with exactly three sides and three angles. "Tri" means three.',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/triangle',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'math-easy-new-002',
    text: 'Zero is neither positive nor negative.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Zero is the boundary between positive and negative numbers. It has no sign and is considered neither positive nor negative.',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/zero-mathematics',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'math-easy-new-003',
    text: 'Multiplying any number by zero gives zero.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'This is a fundamental property of zero. Any number times zero equals zero (the multiplicative property of zero).',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/multiplication',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'math-easy-new-004',
    text: 'A square is a special type of rectangle.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'A square is a rectangle with all sides equal. All squares are rectangles, but not all rectangles are squares.',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/square-geometry',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'math-easy-new-005',
    text: 'Dividing by zero is undefined in mathematics.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Division by zero has no defined value because no number multiplied by zero gives anything other than zero.',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/division-mathematics',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'math-easy-new-006',
    text: 'The order of numbers matters when subtracting but not when adding.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Addition is commutative (3+5=5+3), but subtraction is not (5-3≠3-5). Order matters in subtraction and division.',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/commutative-law',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'math-easy-new-007',
    text: 'Negative numbers are always smaller than positive numbers.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'On the number line, all negative numbers lie to the left of zero, and all positive numbers to the right. Any negative is less than any positive.',
    errorPattern: null,
    subject: 'Mathematics',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/negative-number',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'math-easy-new-008',
    text: 'There are exactly 100 prime numbers.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'There are infinitely many prime numbers. Euclid proved this over 2,000 years ago. The search for larger primes continues.',
    errorPattern: 'confident-specificity',
    subject: 'Mathematics',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== ASTRONOMY (14 more to reach 30) ====================
  {
    id: 'astro-new-001',
    text: 'The Moon is slowly moving away from Earth.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Moon drifts about 3.8 cm farther from Earth each year due to tidal interactions.',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'easy',
    citation: 'https://www.nasa.gov/moon',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'astro-new-002',
    text: 'Stars twinkle because they are blinking on and off.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Stars appear to twinkle because Earth\'s atmosphere bends their light. The stars themselves shine steadily.',
    errorPattern: 'plausible-adjacency',
    subject: 'Astronomy',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'astro-new-003',
    text: 'A day on Venus is longer than a year on Venus.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Venus rotates so slowly that one day (243 Earth days) is longer than its year (225 Earth days).',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'easy',
    citation: 'https://solarsystem.nasa.gov/planets/venus',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'astro-new-004',
    text: 'All planets in our solar system rotate in the same direction.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Venus and Uranus rotate in the opposite direction compared to most other planets.',
    errorPattern: 'plausible-adjacency',
    subject: 'Astronomy',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'astro-new-005',
    text: 'The Sun contains over 99% of all the mass in our solar system.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Sun is so massive that all planets, moons, and asteroids combined make up less than 1%.',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'easy',
    citation: 'https://solarsystem.nasa.gov/sun',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'astro-new-006',
    text: 'Black holes emit a type of radiation called Hawking radiation.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Stephen Hawking theorized that black holes slowly emit radiation due to quantum effects.',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'medium',
    citation: 'https://www.nature.com/articles/nature.2014.14583',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'astro-new-007',
    text: 'The North Star (Polaris) is the brightest star in the night sky.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Sirius is the brightest star. Polaris is only about the 50th brightest but marks true north.',
    errorPattern: 'myth-perpetuation',
    subject: 'Astronomy',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'astro-new-008',
    text: 'Neutron stars can spin hundreds of times per second.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Some neutron stars rotate over 700 times per second due to conservation of angular momentum.',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'medium',
    citation: 'https://www.nasa.gov/mission_pages/GLAST/science/pulsars.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'astro-new-009',
    text: 'Light from the Sun takes exactly 1 minute to reach Earth.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Sunlight takes about 8 minutes and 20 seconds to reach Earth.',
    errorPattern: 'confident-specificity',
    subject: 'Astronomy',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'astro-new-010',
    text: 'The observable universe contains approximately 2 trillion galaxies.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Studies using Hubble data estimate about 2 trillion galaxies in the observable universe.',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'hard',
    citation: 'https://hubblesite.org/contents/news-releases/2016/news-2016-39',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'astro-new-011',
    text: 'Gravity travels at the speed of light.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Gravitational waves propagate at light speed, confirmed by LIGO observations.',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'hard',
    citation: 'https://www.ligo.org/science/GW-Specifics.php',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'astro-new-012',
    text: 'Dark matter makes up about 27% of the universe.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Current models estimate dark matter ~27%, dark energy ~68%, ordinary matter only ~5%.',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'hard',
    citation: 'https://science.nasa.gov/astrophysics/focus-areas/what-is-dark-energy',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'astro-new-013',
    text: 'The Big Bang was an explosion in space.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The Big Bang was not an explosion in space but an expansion of space itself from a singularity.',
    errorPattern: 'myth-perpetuation',
    subject: 'Astronomy',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'astro-new-014',
    text: 'There is a supermassive black hole at the center of the Milky Way.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Sagittarius A* is a supermassive black hole with 4 million solar masses at our galaxy\'s center.',
    errorPattern: null,
    subject: 'Astronomy',
    difficulty: 'hard',
    citation: 'https://www.nasa.gov/mission_pages/chandra/news/black-hole-image-makes-history',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== CHEMISTRY (15 more to reach 30) ====================
  {
    id: 'chem-new-001',
    text: 'Water is the only substance that expands when it freezes.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While water is famous for expanding when frozen, some other substances like bismuth and silicon also expand upon freezing.',
    errorPattern: 'plausible-adjacency',
    subject: 'Chemistry',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'chem-new-002',
    text: 'Salt lowers the freezing point of water.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Salt disrupts ice crystal formation, lowering the freezing point. This is why salt is used on icy roads.',
    errorPattern: null,
    subject: 'Chemistry',
    difficulty: 'easy',
    citation: 'https://www.scientificamerican.com/article/why-do-we-put-salt-on-icy/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'chem-new-003',
    text: 'Diamonds and pencil lead (graphite) are made of the same element.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Both are pure carbon - just arranged differently. Diamond has a tetrahedral structure while graphite has layers.',
    errorPattern: null,
    subject: 'Chemistry',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/carbon-chemical-element',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'chem-new-004',
    text: 'Rust is a chemical reaction between iron and oxygen.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Rust (iron oxide) forms when iron reacts with oxygen and water in a process called oxidation.',
    errorPattern: null,
    subject: 'Chemistry',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/rust-chemical-compound',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'chem-new-005',
    text: 'Helium is heavier than air.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Helium is much lighter than air, which is why helium balloons float upward.',
    errorPattern: 'plausible-adjacency',
    subject: 'Chemistry',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'chem-new-006',
    text: 'The pH scale only goes from 0 to 14.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While 0-14 is the typical range, pH can go below 0 or above 14 for extremely concentrated acids or bases.',
    errorPattern: 'confident-specificity',
    subject: 'Chemistry',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'chem-new-007',
    text: 'Noble gases do not react with other elements.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'Noble gases are very stable but not completely inert. Xenon and krypton can form compounds under special conditions.',
    errorPattern: null,
    subject: 'Chemistry',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/science/noble-gas',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'chem-new-008',
    text: 'Acids always have a pH less than 7.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'By definition, acids have pH values below 7. The lower the pH, the stronger the acid.',
    errorPattern: null,
    subject: 'Chemistry',
    difficulty: 'medium',
    citation: 'https://www.usgs.gov/special-topics/water-science-school/science/ph-and-water',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'chem-new-009',
    text: 'Oil floats on water because it is less dense.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Oil has a lower density than water, so it floats. This is why oil spills spread on water surfaces.',
    errorPattern: null,
    subject: 'Chemistry',
    difficulty: 'medium',
    citation: 'https://www.noaa.gov/education/resource-collections/ocean-coasts/oil-spills',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'chem-new-010',
    text: 'Isotopes of an element have different numbers of protons.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Isotopes have the same number of protons but different numbers of neutrons. Proton count defines the element.',
    errorPattern: 'plausible-adjacency',
    subject: 'Chemistry',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'chem-new-011',
    text: 'Radioactive decay can be sped up by heating the material.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Radioactive decay rates are constant and cannot be changed by temperature, pressure, or chemical reactions.',
    errorPattern: 'false-causation',
    subject: 'Chemistry',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'chem-new-012',
    text: 'Catalysts are consumed in chemical reactions.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Catalysts speed up reactions but are not consumed - they can be used repeatedly.',
    errorPattern: 'plausible-adjacency',
    subject: 'Chemistry',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'chem-new-013',
    text: 'Glass is technically a very slow-moving liquid.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'This is a popular myth. Glass is an amorphous solid, not a liquid. Old windows are thicker at the bottom due to manufacturing.',
    errorPattern: 'myth-perpetuation',
    subject: 'Chemistry',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'chem-new-014',
    text: 'Entropy in a closed system always increases over time.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The second law of thermodynamics states that entropy (disorder) in isolated systems tends to increase.',
    errorPattern: null,
    subject: 'Chemistry',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/science/entropy-physics',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'chem-new-015',
    text: 'Covalent bonds involve the sharing of electrons between atoms.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'In covalent bonds, atoms share electron pairs. This is different from ionic bonds where electrons are transferred.',
    errorPattern: null,
    subject: 'Chemistry',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/science/covalent-bond',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== GEOGRAPHY (15 more to reach 30) ====================
  {
    id: 'geo-new-001',
    text: 'Mount Everest is the tallest mountain measured from base to peak.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Mauna Kea in Hawaii is taller from base to peak (10,210m), but most of it is underwater. Everest is highest above sea level.',
    errorPattern: 'plausible-adjacency',
    subject: 'Geography',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-new-002',
    text: 'Russia spans 11 time zones.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Russia is so wide that it covers 11 time zones, from UTC+2 to UTC+12.',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'easy',
    citation: 'https://www.worldatlas.com/articles/how-many-time-zones-are-there-in-russia.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-new-003',
    text: 'The Sahara Desert is the largest desert in the world.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Antarctica is the largest desert (cold desert). The Sahara is the largest hot desert.',
    errorPattern: 'plausible-adjacency',
    subject: 'Geography',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-new-004',
    text: 'The Amazon River is the longest river in the world.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The Nile is generally considered the longest river. The Amazon carries the most water.',
    errorPattern: 'plausible-adjacency',
    subject: 'Geography',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-new-005',
    text: 'Australia is both a country and a continent.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Australia is the only landmass that is both a country and a continent.',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/place/Australia',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-new-006',
    text: 'The Dead Sea is actually a lake.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Despite its name, the Dead Sea is a salt lake bordered by Jordan and Israel.',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/place/Dead-Sea',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-new-007',
    text: 'Iceland is covered mostly in ice.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Despite its name, Iceland is mostly green. Greenland is the one mostly covered in ice.',
    errorPattern: 'myth-perpetuation',
    subject: 'Geography',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-new-008',
    text: 'The equator passes through 13 countries.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The equator crosses 13 countries in South America, Africa, and Asia.',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'medium',
    citation: 'https://www.worldatlas.com/articles/what-countries-are-on-the-equator.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-new-009',
    text: 'The Pacific Ocean is shrinking.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Due to plate tectonics, the Pacific is slowly shrinking while the Atlantic is expanding.',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'medium',
    citation: 'https://www.usgs.gov/faqs/ocean-getting-larger-or-smaller',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-new-010',
    text: 'All rivers flow from north to south.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Rivers flow downhill regardless of direction. The Nile flows north, for example.',
    errorPattern: 'myth-perpetuation',
    subject: 'Geography',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-new-011',
    text: 'The Mariana Trench is deeper than Mount Everest is tall.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Mariana Trench reaches about 11,000 meters deep, while Everest is about 8,849 meters tall.',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'hard',
    citation: 'https://oceanexplorer.noaa.gov/facts/mariana-trench.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-new-012',
    text: 'More people live in the Northern Hemisphere than the Southern Hemisphere.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'About 90% of the world\'s population lives in the Northern Hemisphere.',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'hard',
    citation: 'https://www.worldatlas.com/articles/what-percentage-of-the-world-population-live-in-the-northern-hemisphere.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-new-013',
    text: 'Tectonic plates move about as fast as fingernails grow.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Tectonic plates typically move 1-10 centimeters per year, similar to fingernail growth.',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'hard',
    citation: 'https://www.usgs.gov/faqs/how-fast-do-tectonic-plates-move',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-new-014',
    text: 'There are exactly 7 continents on Earth.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'The 7-continent model is common but not universal. Some models count 5 or 6 continents.',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/science/continent',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'geo-new-015',
    text: 'The Ring of Fire contains 75% of the world\'s volcanoes.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The Pacific Ring of Fire hosts about 75% of the world\'s active and dormant volcanoes.',
    errorPattern: null,
    subject: 'Geography',
    difficulty: 'hard',
    citation: 'https://www.usgs.gov/programs/volcano-hazards/ring-fire',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== MEDIA LITERACY (19 more to reach 30) ====================
  {
    id: 'media-new-001',
    text: 'Photos can never be manipulated to show things that did not happen.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Photos can be easily edited with software to add, remove, or change elements. Always verify images from trusted sources.',
    errorPattern: 'myth-perpetuation',
    subject: 'Media Literacy',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-002',
    text: 'If something is on the news, it must be true.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'News organizations can make mistakes or have biases. Critical consumers verify information from multiple sources.',
    errorPattern: 'appeal-to-authority',
    subject: 'Media Literacy',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-003',
    text: 'Clickbait headlines are designed to get emotional reactions.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Clickbait uses sensational language to trigger curiosity or outrage, driving clicks regardless of content quality.',
    errorPattern: null,
    subject: 'Media Literacy',
    difficulty: 'easy',
    citation: 'https://mediasmarts.ca/digital-media-literacy/media-issues/online-ethics/clickbait',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-004',
    text: 'Websites ending in .org are always trustworthy.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Anyone can register a .org domain. Website credibility depends on the organization, not the domain extension.',
    errorPattern: 'appeal-to-authority',
    subject: 'Media Literacy',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-005',
    text: 'Social media algorithms tend to show you content similar to what you already engage with.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Algorithms create filter bubbles by prioritizing content matching your past behavior, potentially limiting diverse viewpoints.',
    errorPattern: null,
    subject: 'Media Literacy',
    difficulty: 'easy',
    citation: 'https://www.pewresearch.org/internet/2017/02/08/code-dependent-pros-and-cons-of-the-algorithm-age/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-006',
    text: 'Satire websites are required to label their content as fake.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'There is no legal requirement for satire sites to label content. Readers must check if a source is satirical.',
    errorPattern: 'plausible-adjacency',
    subject: 'Media Literacy',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-007',
    text: 'Deepfake videos can make people appear to say things they never said.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'AI-generated deepfakes can create realistic fake videos of real people, making verification increasingly important.',
    errorPattern: null,
    subject: 'Media Literacy',
    difficulty: 'medium',
    citation: 'https://www.brookings.edu/articles/how-to-combat-fake-news-and-disinformation/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-008',
    text: 'Confirmation bias makes us more likely to believe information that matches our existing beliefs.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Confirmation bias leads people to favor information confirming their views while dismissing contradictory evidence.',
    errorPattern: null,
    subject: 'Media Literacy',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/science/confirmation-bias',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-009',
    text: 'A story with many shares on social media is probably accurate.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Viral content spreads based on emotional appeal, not accuracy. Misinformation often spreads faster than corrections.',
    errorPattern: 'false-causation',
    subject: 'Media Literacy',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-010',
    text: 'Advertisements are required to be 100% truthful.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While false advertising is illegal, ads can use puffery, selective facts, and emotional manipulation within legal limits.',
    errorPattern: 'plausible-adjacency',
    subject: 'Media Literacy',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-011',
    text: 'Primary sources are generally more reliable than secondary sources.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Primary sources provide firsthand information, while secondary sources interpret or analyze primary sources.',
    errorPattern: null,
    subject: 'Media Literacy',
    difficulty: 'hard',
    citation: 'https://guides.library.harvard.edu/primarysources',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-012',
    text: 'Anonymous sources should never be trusted in journalism.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Anonymous sources can be legitimate when protecting whistleblowers. Reputable outlets verify anonymous claims before publishing.',
    errorPattern: 'plausible-adjacency',
    subject: 'Media Literacy',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-013',
    text: 'Fact-checking websites use standardized rating systems.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Different fact-checkers use different rating systems (True/False, Pinocchios, pants on fire, etc.).',
    errorPattern: 'plausible-adjacency',
    subject: 'Media Literacy',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-014',
    text: 'Peer-reviewed research has been evaluated by other experts in the field.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Peer review means experts have assessed the research methodology and conclusions before publication.',
    errorPattern: null,
    subject: 'Media Literacy',
    difficulty: 'hard',
    citation: 'https://www.nature.com/nature-portfolio/about/the-peer-review-process',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-015',
    text: 'Media bias only exists in opinion pieces, not news reporting.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Bias can appear in news through story selection, headline framing, source choices, and word selection.',
    errorPattern: 'plausible-adjacency',
    subject: 'Media Literacy',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-016',
    text: 'Correlation between two things proves one causes the other.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Correlation does not equal causation. Two things may be related without one causing the other.',
    errorPattern: 'false-causation',
    subject: 'Media Literacy',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-017',
    text: 'Reverse image search can help verify if a photo is being used out of context.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Reverse image search can reveal when and where an image first appeared, exposing misuse.',
    errorPattern: null,
    subject: 'Media Literacy',
    difficulty: 'easy',
    citation: 'https://support.google.com/websearch/answer/1325808',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-018',
    text: 'All Wikipedia articles are written by experts in the field.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Anyone can edit Wikipedia. While many editors are knowledgeable, articles vary in quality and should be verified.',
    errorPattern: 'appeal-to-authority',
    subject: 'Media Literacy',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'media-new-019',
    text: 'Native advertising is designed to look like regular content.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Native ads match the style of surrounding content, making them harder to identify as paid promotions.',
    errorPattern: null,
    subject: 'Media Literacy',
    difficulty: 'medium',
    citation: 'https://www.ftc.gov/tips-advice/business-center/guidance/native-advertising-guide-businesses',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== MEDICINE (22 more to reach 30) ====================
  {
    id: 'med-new-001',
    text: 'Antibiotics can cure viral infections like the common cold.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Antibiotics only work against bacteria, not viruses. Misuse contributes to antibiotic resistance.',
    errorPattern: 'plausible-adjacency',
    subject: 'Medicine',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-002',
    text: 'Vaccines work by training the immune system to recognize pathogens.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Vaccines expose the immune system to weakened or inactive pathogens, teaching it to fight future infections.',
    errorPattern: null,
    subject: 'Medicine',
    difficulty: 'easy',
    citation: 'https://www.cdc.gov/vaccines/hcp/conversations/understanding-vacc-work.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-003',
    text: 'You should always finish your full course of antibiotics.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Stopping early can leave surviving bacteria that may become resistant. Complete the prescription as directed.',
    errorPattern: null,
    subject: 'Medicine',
    difficulty: 'easy',
    citation: 'https://www.who.int/news-room/fact-sheets/detail/antibiotic-resistance',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-004',
    text: 'Blood is blue inside your body until it hits oxygen.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Blood is always red. Deoxygenated blood is darker red, not blue. Veins look blue due to how light penetrates skin.',
    errorPattern: 'myth-perpetuation',
    subject: 'Medicine',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-005',
    text: 'The human body has more bacterial cells than human cells.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'Recent estimates suggest roughly equal numbers, though this varies. Earlier claims of 10:1 ratio were overestimated.',
    errorPattern: null,
    subject: 'Medicine',
    difficulty: 'easy',
    citation: 'https://www.nature.com/articles/nature.2016.19136',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-006',
    text: 'Fever is the body\'s way of fighting infection.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Elevated temperature helps immune cells work better and makes it harder for some pathogens to survive.',
    errorPattern: null,
    subject: 'Medicine',
    difficulty: 'easy',
    citation: 'https://www.ncbi.nlm.nih.gov/books/NBK279456/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-007',
    text: 'Generic medications are less effective than brand-name versions.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Generic drugs have the same active ingredients and must meet the same FDA standards as brand-name drugs.',
    errorPattern: 'myth-perpetuation',
    subject: 'Medicine',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-008',
    text: 'The placebo effect can produce real physical changes in the body.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Placebos can trigger real physiological responses, including pain relief and immune changes, through psychological mechanisms.',
    errorPattern: null,
    subject: 'Medicine',
    difficulty: 'medium',
    citation: 'https://www.health.harvard.edu/mental-health/the-power-of-the-placebo-effect',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-009',
    text: 'Cancer is always caused by genetic mutations.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'Cancer involves genetic changes, but these can be inherited, caused by environment, or occur randomly during cell division.',
    errorPattern: null,
    subject: 'Medicine',
    difficulty: 'medium',
    citation: 'https://www.cancer.gov/about-cancer/causes-prevention/genetics',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-010',
    text: 'Humans only use 10% of their brains.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Brain scans show we use all parts of our brain, though not all at once. This is a persistent myth.',
    errorPattern: 'myth-perpetuation',
    subject: 'Medicine',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-011',
    text: 'Double-blind studies are considered the gold standard for clinical trials.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'In double-blind trials, neither participants nor researchers know who gets the treatment, reducing bias.',
    errorPattern: null,
    subject: 'Medicine',
    difficulty: 'medium',
    citation: 'https://www.nih.gov/health-information/nih-clinical-research-trials-you/basics',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-012',
    text: 'Allergies occur when the immune system overreacts to harmless substances.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Allergies are immune system responses to substances like pollen or peanuts that are not actually harmful.',
    errorPattern: null,
    subject: 'Medicine',
    difficulty: 'medium',
    citation: 'https://www.niaid.nih.gov/diseases-conditions/allergies',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-013',
    text: 'Stem cells can only become one type of cell.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Stem cells can differentiate into many cell types. This versatility makes them valuable for medical research.',
    errorPattern: 'plausible-adjacency',
    subject: 'Medicine',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-014',
    text: 'mRNA vaccines change your DNA.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'mRNA never enters the cell nucleus where DNA is stored. It provides instructions briefly then breaks down.',
    errorPattern: 'myth-perpetuation',
    subject: 'Medicine',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-015',
    text: 'The liver can regenerate itself.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The liver is the only internal organ that can regenerate. It can regrow to full size from just 25% of tissue.',
    errorPattern: null,
    subject: 'Medicine',
    difficulty: 'hard',
    citation: 'https://www.liverfoundation.org/about-your-liver/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-016',
    text: 'All bacteria in the human body are harmful.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Many bacteria are beneficial, aiding digestion and immune function. The microbiome is essential for health.',
    errorPattern: 'plausible-adjacency',
    subject: 'Medicine',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-017',
    text: 'Autoimmune diseases occur when the body attacks its own tissues.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'In autoimmune conditions, the immune system mistakenly targets healthy cells and tissues.',
    errorPattern: null,
    subject: 'Medicine',
    difficulty: 'medium',
    citation: 'https://www.niaid.nih.gov/diseases-conditions/autoimmune-diseases',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-018',
    text: 'Cracking your knuckles causes arthritis.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Studies have found no link between knuckle cracking and arthritis. The sound comes from gas bubbles in joint fluid.',
    errorPattern: 'myth-perpetuation',
    subject: 'Medicine',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-019',
    text: 'CRISPR technology can edit specific genes.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'CRISPR-Cas9 allows precise editing of DNA sequences, revolutionizing genetic research and potential treatments.',
    errorPattern: null,
    subject: 'Medicine',
    difficulty: 'hard',
    citation: 'https://www.broadinstitute.org/what-broad/areas-focus/project-spotlight/crispr',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-020',
    text: 'Viruses are considered living organisms.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'Scientists debate this. Viruses have genetic material but cannot reproduce without a host cell.',
    errorPattern: null,
    subject: 'Medicine',
    difficulty: 'hard',
    citation: 'https://www.nature.com/scitable/topicpage/the-origins-of-viruses-14398218/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-021',
    text: 'Organ transplants require lifelong immunosuppressive medication.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Without immunosuppressants, the body would reject foreign organs. Recipients must take these medications for life.',
    errorPattern: null,
    subject: 'Medicine',
    difficulty: 'hard',
    citation: 'https://www.mayoclinic.org/tests-procedures/organ-transplant/about/pac-20384787',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'med-new-022',
    text: 'Reading in dim light permanently damages your eyes.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Dim light may cause temporary eye strain but does not cause permanent damage to vision.',
    errorPattern: 'myth-perpetuation',
    subject: 'Medicine',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== ANIMAL SCIENCE (22 more to reach 30) ====================
  {
    id: 'animal-new-001',
    text: 'Goldfish have a 3-second memory.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Goldfish can remember things for months. Studies show they can learn and remember tasks for extended periods.',
    errorPattern: 'myth-perpetuation',
    subject: 'Animal Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-002',
    text: 'Elephants are the only animals that cannot jump.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While elephants cannot jump, neither can sloths, hippos, and rhinos due to their body structure.',
    errorPattern: 'confident-specificity',
    subject: 'Animal Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-003',
    text: 'Octopuses have three hearts.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Octopuses have one main heart and two branchial hearts that pump blood to the gills.',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'easy',
    citation: 'https://www.nationalgeographic.com/animals/invertebrates/facts/octopus',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-004',
    text: 'Dogs see the world in black and white.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Dogs see colors but differently than humans. They see blues and yellows but not reds and greens.',
    errorPattern: 'myth-perpetuation',
    subject: 'Animal Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-005',
    text: 'Bats are completely blind and rely only on echolocation.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'All bat species can see. Many also use echolocation, but "blind as a bat" is a myth.',
    errorPattern: 'myth-perpetuation',
    subject: 'Animal Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-006',
    text: 'Honeybees die after stinging.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Honeybee stingers are barbed and tear from their body when stinging mammals, killing the bee.',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'easy',
    citation: 'https://www.usgs.gov/faqs/what-happens-bee-after-it-stings-someone',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-007',
    text: 'Chameleons change color primarily for camouflage.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Color changes mainly reflect mood, temperature, and social signals. Camouflage is secondary.',
    errorPattern: 'myth-perpetuation',
    subject: 'Animal Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-008',
    text: 'Sharks must keep swimming to breathe.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'Some shark species must swim to breathe (ram ventilation), but many can pump water over their gills while stationary.',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'medium',
    citation: 'https://www.floridamuseum.ufl.edu/discover-fish/sharks/faqs/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-009',
    text: 'A group of crows is called a murder.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The collective noun for crows is indeed a "murder," dating back to medieval times.',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'medium',
    citation: 'https://www.audubon.org/news/why-group-crows-called-murder',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-010',
    text: 'Cats always land on their feet.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While cats have a righting reflex, they may not land safely from very short or very high falls.',
    errorPattern: 'confident-specificity',
    subject: 'Animal Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-011',
    text: 'Flamingos are pink because of their diet.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Flamingos get their pink color from carotenoids in shrimp and algae they eat. Zoo flamingos need supplements.',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/animal/flamingo-bird',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-012',
    text: 'Dolphins sleep with one eye open.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Dolphins sleep with half their brain at a time, keeping one eye open to watch for predators and surface to breathe.',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'medium',
    citation: 'https://www.nature.com/articles/d41586-019-02233-5',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-013',
    text: 'Tardigrades can survive in the vacuum of space.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Tardigrades (water bears) can survive extreme conditions including space vacuum, radiation, and extreme temperatures.',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'hard',
    citation: 'https://www.scientificamerican.com/article/tardigrades-may-have-survived-crash-landing-on-moon/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-014',
    text: 'Earthworms have five hearts.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Earthworms have five pairs of aortic arches that function like hearts, pumping blood through their bodies.',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/animal/earthworm',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-015',
    text: 'Hummingbirds can fly backwards.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Hummingbirds are the only birds that can fly backwards, thanks to their unique wing structure.',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'easy',
    citation: 'https://www.audubon.org/news/the-amazing-science-behind-hummingbird-flight',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-016',
    text: 'Penguins live only in cold climates.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'African penguins live in warm climates, and Galápagos penguins live near the equator.',
    errorPattern: 'plausible-adjacency',
    subject: 'Animal Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-017',
    text: 'Cows have four stomachs.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Cows have one stomach with four compartments (rumen, reticulum, omasum, abomasum), not four separate stomachs.',
    errorPattern: 'myth-perpetuation',
    subject: 'Animal Science',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-018',
    text: 'Some species of jellyfish are immortal.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Turritopsis dohrnii can revert to its juvenile form, theoretically living forever if not killed by predators or disease.',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'hard',
    citation: 'https://www.amnh.org/explore/news-blogs/on-exhibit-posts/the-immortal-jellyfish',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-019',
    text: 'Kangaroos cannot walk backwards.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Due to their large tails and leg structure, kangaroos physically cannot walk backwards.',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'medium',
    citation: 'https://www.nationalgeographic.com/animals/mammals/facts/kangaroos',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-020',
    text: 'Sloths are lazy because they are slow.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Sloths move slowly to conserve energy due to their low-calorie leaf diet, not laziness.',
    errorPattern: 'false-causation',
    subject: 'Animal Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-021',
    text: 'Mantis shrimp can punch as fast as a bullet.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Mantis shrimp strike at about 50 mph, accelerating faster than a .22 caliber bullet.',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'hard',
    citation: 'https://www.nature.com/articles/nature.2012.10527',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'animal-new-022',
    text: 'Butterflies taste with their feet.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Butterflies have taste receptors on their feet, helping them identify plants for feeding and laying eggs.',
    errorPattern: null,
    subject: 'Animal Science',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/animal/butterfly-insect',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== NEUROSCIENCE (23 more to reach 30) ====================
  {
    id: 'neuro-new-001',
    text: 'The brain feels no pain.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The brain has no pain receptors. Headaches come from surrounding tissues, blood vessels, and muscles.',
    errorPattern: null,
    subject: 'Neuroscience',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/pain',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-002',
    text: 'Neurons are the only cells in the brain.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Glial cells outnumber neurons and provide crucial support functions like insulation and nutrient delivery.',
    errorPattern: 'plausible-adjacency',
    subject: 'Neuroscience',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-003',
    text: 'The brain uses about 20% of the body\'s energy.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Despite being only 2% of body weight, the brain consumes roughly 20% of oxygen and calories.',
    errorPattern: null,
    subject: 'Neuroscience',
    difficulty: 'easy',
    citation: 'https://www.pnas.org/doi/10.1073/pnas.122249499',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-004',
    text: 'Left-brained people are logical and right-brained people are creative.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'This is a myth. Both hemispheres work together for all cognitive tasks. People don\'t favor one side.',
    errorPattern: 'myth-perpetuation',
    subject: 'Neuroscience',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-005',
    text: 'Sleep is essential for memory consolidation.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'During sleep, the brain processes and consolidates memories from short-term to long-term storage.',
    errorPattern: null,
    subject: 'Neuroscience',
    difficulty: 'easy',
    citation: 'https://www.ninds.nih.gov/health-information/public-education/brain-basics/brain-basics-understanding-sleep',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-006',
    text: 'Adult brains cannot grow new neurons.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Neurogenesis continues in certain brain regions like the hippocampus throughout adulthood.',
    errorPattern: 'myth-perpetuation',
    subject: 'Neuroscience',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-007',
    text: 'Dopamine is only responsible for pleasure.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Dopamine is involved in motivation, movement, learning, and anticipation of rewards, not just pleasure.',
    errorPattern: 'plausible-adjacency',
    subject: 'Neuroscience',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-008',
    text: 'Mirror neurons fire both when performing an action and when watching others perform it.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Mirror neurons respond to observed actions, thought to play a role in learning, empathy, and understanding others.',
    errorPattern: null,
    subject: 'Neuroscience',
    difficulty: 'medium',
    citation: 'https://www.nature.com/articles/nrn1176',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-009',
    text: 'The amygdala is primarily involved in processing emotions.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The amygdala plays a key role in emotional processing, especially fear and emotional memories.',
    errorPattern: null,
    subject: 'Neuroscience',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/science/amygdala',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-010',
    text: 'Brain damage is always permanent.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The brain has neuroplasticity - the ability to reorganize and form new connections, allowing some recovery.',
    errorPattern: 'confident-specificity',
    subject: 'Neuroscience',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-011',
    text: 'Synapses transmit signals between neurons.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Synapses are the gaps between neurons where chemical or electrical signals pass from one neuron to another.',
    errorPattern: null,
    subject: 'Neuroscience',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/synapse',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-012',
    text: 'The hippocampus is essential for forming new memories.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The hippocampus converts short-term memories to long-term. Damage to it impairs new memory formation.',
    errorPattern: null,
    subject: 'Neuroscience',
    difficulty: 'medium',
    citation: 'https://www.britannica.com/science/hippocampus',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-013',
    text: 'The corpus callosum connects the left and right brain hemispheres.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The corpus callosum is a band of nerve fibers enabling communication between the two hemispheres.',
    errorPattern: null,
    subject: 'Neuroscience',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/science/corpus-callosum',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-014',
    text: 'Neurotransmitters can only have excitatory effects.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Neurotransmitters can be excitatory (promoting signals) or inhibitory (reducing signals). GABA is inhibitory.',
    errorPattern: 'plausible-adjacency',
    subject: 'Neuroscience',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-015',
    text: 'The prefrontal cortex is fully developed by age 12.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The prefrontal cortex, responsible for decision-making and impulse control, develops until about age 25.',
    errorPattern: 'confident-specificity',
    subject: 'Neuroscience',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-016',
    text: 'Myelin helps nerve signals travel faster.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Myelin is a fatty sheath that insulates axons, dramatically increasing the speed of electrical signals.',
    errorPattern: null,
    subject: 'Neuroscience',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/science/myelin',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-017',
    text: 'Brain waves can be measured with EEG.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Electroencephalography (EEG) records electrical activity in the brain using scalp electrodes.',
    errorPattern: null,
    subject: 'Neuroscience',
    difficulty: 'medium',
    citation: 'https://www.mayoclinic.org/tests-procedures/eeg/about/pac-20393875',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-018',
    text: 'Multitasking is an efficient way to use your brain.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The brain rapidly switches between tasks rather than doing them simultaneously, reducing efficiency and increasing errors.',
    errorPattern: 'myth-perpetuation',
    subject: 'Neuroscience',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-019',
    text: 'Long-term potentiation strengthens connections between neurons.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'LTP is a process where repeated stimulation strengthens synaptic connections, underlying learning and memory.',
    errorPattern: null,
    subject: 'Neuroscience',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/science/long-term-potentiation',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-020',
    text: 'The brain\'s reward system involves the nucleus accumbens.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The nucleus accumbens is a key structure in the brain\'s reward circuitry, responding to pleasurable stimuli.',
    errorPattern: null,
    subject: 'Neuroscience',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/science/nucleus-accumbens',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-021',
    text: 'Alcohol kills brain cells permanently.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While heavy drinking damages neurons and their connections, moderate use doesn\'t cause permanent cell death.',
    errorPattern: 'confident-specificity',
    subject: 'Neuroscience',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-022',
    text: 'The cerebellum coordinates movement and balance.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The cerebellum fine-tunes motor control, balance, and coordination of voluntary movements.',
    errorPattern: null,
    subject: 'Neuroscience',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/science/cerebellum',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'neuro-new-023',
    text: 'Phantom limb sensations prove the brain creates body awareness.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'People can feel sensations in amputated limbs because the brain maintains a neural map of the body.',
    errorPattern: null,
    subject: 'Neuroscience',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/science/phantom-limb-syndrome',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== WEATHER SCIENCE (26 more to reach 30) ====================
  {
    id: 'weather-new-001',
    text: 'Lightning can strike the same place twice.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Tall structures like the Empire State Building are struck by lightning many times per year.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'easy',
    citation: 'https://www.weather.gov/safety/lightning-myths',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-002',
    text: 'Tornadoes only occur in the United States.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Tornadoes occur on every continent except Antarctica. The US has the most due to geography.',
    errorPattern: 'plausible-adjacency',
    subject: 'Weather Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-003',
    text: 'Humidity refers to the amount of water vapor in the air.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Humidity measures moisture content in air. Relative humidity compares it to maximum possible at that temperature.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'easy',
    citation: 'https://www.weather.gov/lmk/humidity',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-004',
    text: 'The eye of a hurricane is the most dangerous part.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The eye is calm with light winds. The eyewall surrounding it has the strongest winds and heaviest rain.',
    errorPattern: 'plausible-adjacency',
    subject: 'Weather Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-005',
    text: 'Snow is white because ice crystals reflect all colors of light.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Snow appears white because ice crystals have many surfaces that scatter all wavelengths of visible light equally.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'easy',
    citation: 'https://www.scientificamerican.com/article/why-is-snow-white/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-006',
    text: 'Heat lightning is a special type of lightning without thunder.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Heat lightning is regular lightning from distant storms. The thunder is too far away to hear.',
    errorPattern: 'myth-perpetuation',
    subject: 'Weather Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-007',
    text: 'Clouds are made of water droplets or ice crystals.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Clouds form when water vapor condenses around tiny particles, creating droplets or ice crystals.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'easy',
    citation: 'https://www.noaa.gov/jetstream/clouds',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-008',
    text: 'The Coriolis effect causes hurricanes to spin.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Earth\'s rotation deflects moving air, causing hurricanes to spin counterclockwise in the Northern Hemisphere.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'medium',
    citation: 'https://oceanservice.noaa.gov/education/tutorial_currents/03conveyor2.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-009',
    text: 'Rainbows always appear in the same order of colors.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Rainbows always show ROYGBIV because different wavelengths of light refract at consistent angles.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'easy',
    citation: 'https://www.weather.gov/jetstream/rainbow',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-010',
    text: 'Opening windows during a tornado helps equalize pressure.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'This is a dangerous myth. Opening windows wastes time and allows more debris inside. Seek shelter instead.',
    errorPattern: 'myth-perpetuation',
    subject: 'Weather Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-011',
    text: 'El Niño affects weather patterns globally.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'El Niño warming in the Pacific Ocean disrupts atmospheric circulation, affecting weather worldwide.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'medium',
    citation: 'https://www.climate.gov/enso',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-012',
    text: 'Thunder is caused by lightning heating the air.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Lightning superheats air to about 30,000 K, causing rapid expansion that creates a sonic shock wave - thunder.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'medium',
    citation: 'https://www.weather.gov/jetstream/lightning',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-013',
    text: 'The jet stream only affects airplane travel.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Jet streams influence weather patterns, storm tracks, and temperature changes across continents.',
    errorPattern: 'plausible-adjacency',
    subject: 'Weather Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-014',
    text: 'Dew forms when air temperature drops to the dew point.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'When air cools to its dew point, water vapor condenses into liquid droplets on surfaces.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'medium',
    citation: 'https://www.weather.gov/lmk/humidity',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-015',
    text: 'Hailstones are always spherical.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Hailstones can be irregular, spiky, or layered depending on how they form in thunderstorm updrafts.',
    errorPattern: 'confident-specificity',
    subject: 'Weather Science',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-016',
    text: 'Red sky at night means good weather is coming.',
    answer: 'MIXED',
    source: 'expert-sourced',
    explanation: 'This saying has some validity in mid-latitudes where weather moves west to east, but it\'s not always reliable.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'hard',
    citation: 'https://www.loc.gov/everyday-mysteries/meteorology-climatology/item/is-the-old-adage-red-sky-at-night-sailors-delight-true/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-017',
    text: 'Cold air can hold more moisture than warm air.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Warm air holds more water vapor. This is why summer air feels more humid than winter air.',
    errorPattern: 'plausible-adjacency',
    subject: 'Weather Science',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-018',
    text: 'Barometric pressure changes can indicate weather changes.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Falling pressure often indicates approaching storms, while rising pressure suggests improving weather.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'hard',
    citation: 'https://www.weather.gov/jetstream/pressure',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-019',
    text: 'Fog is a cloud at ground level.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Fog and clouds are both composed of tiny water droplets. Fog simply forms at or near Earth\'s surface.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'easy',
    citation: 'https://www.weather.gov/jetstream/fog',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-020',
    text: 'Microburst winds can exceed tornado wind speeds.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Microbursts can produce winds over 150 mph, comparable to strong tornadoes. They\'re extremely dangerous.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'hard',
    citation: 'https://www.weather.gov/lmk/microburst',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-021',
    text: 'It can be too cold to snow.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Very cold air holds little moisture, making heavy snowfall rare at extremely low temperatures.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'medium',
    citation: 'https://www.weather.gov/safety/cold-precip',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-022',
    text: 'All hurricanes rotate counterclockwise.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Only Northern Hemisphere hurricanes rotate counterclockwise. In the Southern Hemisphere, they spin clockwise.',
    errorPattern: 'confident-specificity',
    subject: 'Weather Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-023',
    text: 'The stratosphere is warmer at higher altitudes.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The ozone layer in the stratosphere absorbs UV radiation, warming the upper stratosphere.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'hard',
    citation: 'https://www.noaa.gov/jetstream/atmosphere',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-024',
    text: 'Wind is caused by differences in air pressure.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Air moves from high pressure to low pressure areas, creating wind. Greater differences mean stronger winds.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'easy',
    citation: 'https://www.weather.gov/jetstream/wind',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-025',
    text: 'Blizzards require both heavy snow and high winds.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'A blizzard is defined by sustained winds over 35 mph with falling or blowing snow reducing visibility.',
    errorPattern: null,
    subject: 'Weather Science',
    difficulty: 'easy',
    citation: 'https://www.weather.gov/safety/winter-blizzard',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'weather-new-026',
    text: 'Weather and climate are the same thing.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Weather is short-term atmospheric conditions. Climate is the long-term average of weather patterns.',
    errorPattern: 'plausible-adjacency',
    subject: 'Weather Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== LANGUAGE - NEW CLAIMS ====================
  {
    id: 'lang-new-001',
    text: 'English is the language with the most words in the world.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'English has over 170,000 words in current use, plus 47,000 obsolete words. This exceeds most other languages.',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'easy',
    citation: 'https://www.oed.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-002',
    text: 'The shortest complete sentence in English is "I am."',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The shortest complete sentence is "Go." - an imperative with an implied subject. "I am" has two words.',
    errorPattern: 'confident-specificity',
    subject: 'Language',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-003',
    text: 'A pangram is a sentence that uses every letter of the alphabet.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: '"The quick brown fox jumps over the lazy dog" is a famous pangram containing all 26 letters.',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'easy',
    citation: 'https://www.merriam-webster.com/dictionary/pangram',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-004',
    text: 'The word "alphabet" comes from the first two Greek letters: alpha and beta.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The word "alphabet" is derived from alpha (α) and beta (β), the first two letters of the Greek alphabet.',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'easy',
    citation: 'https://www.etymonline.com/word/alphabet',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-005',
    text: 'All languages have exactly 26 letters in their alphabets.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Alphabet sizes vary greatly. Hawaiian has 13 letters, Russian has 33, and some languages use non-alphabetic systems.',
    errorPattern: 'confident-specificity',
    subject: 'Language',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-006',
    text: 'Mandarin Chinese is the most spoken native language in the world.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'With over 900 million native speakers, Mandarin Chinese has more native speakers than any other language.',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'easy',
    citation: 'https://www.ethnologue.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-007',
    text: 'Learning a second language damages your ability to speak your first language.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Research shows bilingualism enhances cognitive abilities and does not harm first language skills.',
    errorPattern: 'false-causation',
    subject: 'Language',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-008',
    text: 'A synonym is a word that means the opposite of another word.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Synonyms are words with similar meanings. Words with opposite meanings are called antonyms.',
    errorPattern: 'plausible-adjacency',
    subject: 'Language',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-009',
    text: 'The word "set" has the most definitions in the English dictionary.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The word "set" has over 430 different definitions in the Oxford English Dictionary.',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'easy',
    citation: 'https://www.oed.com/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-010',
    text: 'A palindrome is a word that reads the same forwards and backwards.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Examples include "radar," "level," and "civic" - words that spell the same in both directions.',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'medium',
    citation: 'https://www.merriam-webster.com/dictionary/palindrome',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-011',
    text: 'The word "queue" is pronounced differently in British and American English.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Queue is pronounced "kyoo" in both British and American English. The pronunciation is identical.',
    errorPattern: 'geographic-fabrication',
    subject: 'Language',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-012',
    text: 'An onomatopoeia is a word that sounds like what it describes.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Words like "buzz," "splash," and "meow" are onomatopoeias that imitate the sounds they represent.',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'medium',
    citation: 'https://www.merriam-webster.com/dictionary/onomatopoeia',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-013',
    text: 'Shakespeare invented over 1,700 words that we still use today.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Shakespeare coined words like "assassination," "lonely," "generous," and "bedroom" that remain in use.',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'medium',
    citation: 'https://www.shakespeare.org.uk/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-014',
    text: 'All languages read from left to right.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Arabic and Hebrew read right to left. Traditional Chinese and Japanese can be read top to bottom.',
    errorPattern: 'myth-perpetuation',
    subject: 'Language',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-015',
    text: 'The dot over the letters "i" and "j" is called a tittle.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The small distinguishing mark over lowercase i and j is called a tittle, from Latin "titulus."',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'medium',
    citation: 'https://www.merriam-webster.com/dictionary/tittle',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-016',
    text: 'The word "emoji" comes from the Japanese words for "picture" and "character."',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Emoji combines the Japanese words "e" (picture) and "moji" (character). It is unrelated to "emotion."',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'medium',
    citation: 'https://www.etymonline.com/word/emoji',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-017',
    text: 'Grammar rules in English have remained unchanged for 500 years.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'English grammar has evolved significantly. "Thee" and "thou" disappeared, and new constructions emerged.',
    errorPattern: 'timeline-compression',
    subject: 'Language',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-018',
    text: 'Sign languages are universal and the same in all countries.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Sign languages vary by country. American Sign Language (ASL) differs from British Sign Language (BSL).',
    errorPattern: 'myth-perpetuation',
    subject: 'Language',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-019',
    text: 'An oxymoron is a figure of speech combining contradictory terms.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Examples include "jumbo shrimp," "deafening silence," and "bittersweet" - contradictions used for effect.',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'medium',
    citation: 'https://www.merriam-webster.com/dictionary/oxymoron',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-020',
    text: 'The ampersand (&) was once the 27th letter of the English alphabet.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'In the 19th century, & was taught as the 27th letter, recited as "and per se and."',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'hard',
    citation: 'https://www.dictionary.com/e/ampersand/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-021',
    text: 'Proto-Indo-European is the ancestor of most European and South Asian languages.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'This reconstructed ancient language is the common ancestor of English, Spanish, Hindi, Russian, and many others.',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/topic/Indo-European-languages',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-022',
    text: 'Children can only learn languages fluently before age 3.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While earlier is easier, people can learn languages at any age. The "critical period" extends to puberty, not age 3.',
    errorPattern: 'confident-specificity',
    subject: 'Language',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-023',
    text: 'The International Phonetic Alphabet (IPA) represents all sounds in human language.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The IPA provides symbols for all documented speech sounds, enabling accurate pronunciation notation.',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'hard',
    citation: 'https://www.internationalphoneticassociation.org/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-024',
    text: 'Latin is the direct ancestor of English.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'English is a Germanic language descended from Proto-Germanic. Latin influenced English but is not its ancestor.',
    errorPattern: 'plausible-adjacency',
    subject: 'Language',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-025',
    text: 'Esperanto is a constructed language designed to be easy to learn.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Created in 1887 by L.L. Zamenhof, Esperanto was designed as an easy international auxiliary language.',
    errorPattern: null,
    subject: 'Language',
    difficulty: 'hard',
    citation: 'https://www.britannica.com/topic/Esperanto',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'lang-new-026',
    text: 'The Rosetta Stone was written in exactly 4 languages.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'The Rosetta Stone contains the same text in 2 languages (Egyptian and Greek) using 3 scripts.',
    errorPattern: 'confident-specificity',
    subject: 'Language',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== MEDICAL SCIENCE - NEW CLAIMS ====================
  {
    id: 'medsci-new-001',
    text: 'Antibiotics are effective against viral infections like the common cold.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Antibiotics only kill bacteria, not viruses. Colds are caused by viruses and cannot be treated with antibiotics.',
    errorPattern: 'plausible-adjacency',
    subject: 'Medical Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-002',
    text: 'The human body replaces most of its cells over time.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Most cells are replaced regularly - red blood cells every 120 days, skin cells every 2-3 weeks, stomach lining every few days.',
    errorPattern: null,
    subject: 'Medical Science',
    difficulty: 'easy',
    citation: 'https://www.ncbi.nlm.nih.gov/books/NBK26869/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-003',
    text: 'A fever is always dangerous and should be immediately reduced.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Moderate fevers help the immune system fight infection. Only very high fevers (over 104°F/40°C) typically need intervention.',
    errorPattern: 'myth-perpetuation',
    subject: 'Medical Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-004',
    text: 'Blood types are inherited from parents.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Blood type is determined by genes inherited from both parents following Mendelian genetics.',
    errorPattern: null,
    subject: 'Medical Science',
    difficulty: 'easy',
    citation: 'https://www.redcrossblood.org/learn-about-blood/blood-types.html',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-005',
    text: 'You need to wait 24 hours before reporting a missing person.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'This is a myth. Police accept missing person reports immediately, especially for vulnerable individuals.',
    errorPattern: 'myth-perpetuation',
    subject: 'Medical Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-006',
    text: 'The appendix has no known function in the human body.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Research shows the appendix serves as a reservoir for beneficial gut bacteria and supports the immune system.',
    errorPattern: 'myth-perpetuation',
    subject: 'Medical Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-007',
    text: 'Vaccines work by training the immune system to recognize pathogens.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Vaccines contain weakened or inactive pathogen parts that teach immune cells to recognize and fight actual infections.',
    errorPattern: null,
    subject: 'Medical Science',
    difficulty: 'easy',
    citation: 'https://www.who.int/news-room/feature-stories/detail/how-do-vaccines-work',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-008',
    text: 'Reading in dim light permanently damages your eyesight.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Reading in dim light can cause eye strain and fatigue but does not cause permanent damage to vision.',
    errorPattern: 'false-causation',
    subject: 'Medical Science',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-009',
    text: 'The human heart creates enough pressure to squirt blood 30 feet.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The heart generates significant pressure - systolic pressure can push blood up to 30 feet if an artery is severed.',
    errorPattern: null,
    subject: 'Medical Science',
    difficulty: 'easy',
    citation: 'https://www.heart.org/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-010',
    text: 'Humans can survive without a spleen.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'While the spleen helps filter blood and fight infection, other organs compensate if it is removed.',
    errorPattern: null,
    subject: 'Medical Science',
    difficulty: 'medium',
    citation: 'https://www.mayoclinic.org/diseases-conditions/enlarged-spleen/diagnosis-treatment/drc-20354331',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-011',
    text: 'Chemotherapy always causes complete hair loss.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Hair loss depends on the type and dose of chemotherapy drugs. Some regimens cause no hair loss at all.',
    errorPattern: 'confident-specificity',
    subject: 'Medical Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-012',
    text: 'The gut contains more neurons than the spinal cord.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'The enteric nervous system in the gut has about 500 million neurons, earning it the name "second brain."',
    errorPattern: null,
    subject: 'Medical Science',
    difficulty: 'medium',
    citation: 'https://www.scientificamerican.com/article/gut-second-brain/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-013',
    text: 'Sugar causes hyperactivity in children.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Multiple controlled studies have found no link between sugar consumption and hyperactivity in children.',
    errorPattern: 'false-causation',
    subject: 'Medical Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-014',
    text: 'Bone is stronger than steel by weight.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Ounce for ounce, bone is stronger than steel. A cubic inch of bone can bear 19,000 pounds.',
    errorPattern: null,
    subject: 'Medical Science',
    difficulty: 'medium',
    citation: 'https://www.bones.nih.gov/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-015',
    text: 'Cracking your knuckles causes arthritis.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Studies have found no connection between knuckle cracking and arthritis. The sound comes from gas bubbles.',
    errorPattern: 'false-causation',
    subject: 'Medical Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-016',
    text: 'MRI machines use magnetic fields, not radiation, to create images.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'MRI uses powerful magnetic fields and radio waves. Unlike X-rays or CT scans, it uses no ionizing radiation.',
    errorPattern: null,
    subject: 'Medical Science',
    difficulty: 'medium',
    citation: 'https://www.nibib.nih.gov/science-education/science-topics/magnetic-resonance-imaging-mri',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-017',
    text: 'You lose 40% of your body heat through your head.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'You lose heat proportional to exposed surface area. The head loses about 10% when the rest of the body is covered.',
    errorPattern: 'confident-specificity',
    subject: 'Medical Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-018',
    text: 'The human body contains about 2.5 million sweat glands.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Humans have 2-4 million sweat glands distributed across the body, concentrated on palms and soles.',
    errorPattern: null,
    subject: 'Medical Science',
    difficulty: 'medium',
    citation: 'https://www.aad.org/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-019',
    text: 'All genetic diseases are inherited from parents.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Some genetic diseases result from new mutations that occur spontaneously and are not inherited.',
    errorPattern: 'myth-perpetuation',
    subject: 'Medical Science',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-020',
    text: 'CRISPR technology allows scientists to edit specific genes.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'CRISPR-Cas9 enables precise cutting and modification of DNA sequences at specific locations in the genome.',
    errorPattern: null,
    subject: 'Medical Science',
    difficulty: 'hard',
    citation: 'https://www.nature.com/articles/d41586-020-03018-x',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-021',
    text: 'Stem cells can differentiate into any type of cell in the body.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Pluripotent stem cells can develop into almost any cell type, making them valuable for regenerative medicine.',
    errorPattern: null,
    subject: 'Medical Science',
    difficulty: 'hard',
    citation: 'https://stemcells.nih.gov/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-022',
    text: 'Prions are infectious agents made entirely of protein.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Prions are misfolded proteins that can cause other proteins to misfold, causing diseases like mad cow disease.',
    errorPattern: null,
    subject: 'Medical Science',
    difficulty: 'hard',
    citation: 'https://www.cdc.gov/prions/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-023',
    text: 'A double-blind study means neither patients nor doctors know who receives treatment.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Double-blind methodology prevents bias by keeping both participants and researchers unaware of group assignments.',
    errorPattern: null,
    subject: 'Medical Science',
    difficulty: 'hard',
    citation: 'https://www.nih.gov/health-information/nih-clinical-research-trials-you/basics',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-024',
    text: 'The human microbiome contains approximately 100 trillion cells.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Recent estimates suggest the microbiome contains roughly as many cells as human cells (about 38 trillion), not 100 trillion.',
    errorPattern: 'confident-specificity',
    subject: 'Medical Science',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-025',
    text: 'CAR-T cell therapy reprograms a patient\'s own immune cells to fight cancer.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'CAR-T therapy extracts T cells, engineers them to recognize cancer cells, and returns them to the patient.',
    errorPattern: null,
    subject: 'Medical Science',
    difficulty: 'hard',
    citation: 'https://www.cancer.gov/about-cancer/treatment/research/car-t-cells',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-026',
    text: 'Epigenetic changes cannot be passed to offspring.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Some epigenetic modifications can be inherited across generations, affecting gene expression without changing DNA.',
    errorPattern: 'myth-perpetuation',
    subject: 'Medical Science',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-027',
    text: 'The blood-brain barrier protects the brain from most pathogens and toxins.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Specialized cells form a selective barrier preventing most harmful substances from entering brain tissue.',
    errorPattern: null,
    subject: 'Medical Science',
    difficulty: 'hard',
    citation: 'https://www.ninds.nih.gov/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'medsci-new-028',
    text: 'Antibiotics become less effective over time because bacteria evolve resistance.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Bacteria evolve through natural selection. Those surviving antibiotics reproduce, spreading resistance genes.',
    errorPattern: null,
    subject: 'Medical Science',
    difficulty: 'hard',
    citation: 'https://www.cdc.gov/drugresistance/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },

  // ==================== BIOTECHNOLOGY - NEW CLAIMS ====================
  {
    id: 'biotech-new-001',
    text: 'GMO stands for Genetically Modified Organism.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'GMO refers to organisms whose genetic material has been altered using genetic engineering techniques.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'easy',
    citation: 'https://www.fda.gov/food/agricultural-biotechnology/gmos-and-farmers',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-002',
    text: 'All GMO foods are harmful to humans.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Scientific consensus indicates approved GMO foods are safe to eat. They undergo extensive safety testing.',
    errorPattern: 'myth-perpetuation',
    subject: 'Biotechnology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-003',
    text: 'Insulin for diabetics is now produced using genetically modified bacteria.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Since 1982, human insulin has been produced by bacteria engineered with the human insulin gene, replacing animal sources.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'easy',
    citation: 'https://www.diabetes.org/healthy-living/medication-treatments/insulin-other-injectables',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-004',
    text: 'Cloning creates an exact genetic copy of an organism.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Cloning produces organisms with identical nuclear DNA to the donor. Dolly the sheep was the first cloned mammal.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'easy',
    citation: 'https://www.genome.gov/genetics-glossary/Cloning',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-005',
    text: 'Fermentation is a biotechnology process that has been used for thousands of years.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Humans have used fermentation to make bread, wine, and cheese for over 8,000 years using microorganisms.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'easy',
    citation: 'https://www.britannica.com/technology/fermentation',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-006',
    text: 'Gene therapy can only be performed on adults.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Gene therapy can be performed at any age. Some treatments work better in younger patients before disease progresses.',
    errorPattern: 'myth-perpetuation',
    subject: 'Biotechnology',
    difficulty: 'easy',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-007',
    text: 'Bacteria can be engineered to produce human proteins.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Recombinant DNA technology allows human genes to be inserted into bacteria, which then produce human proteins.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'easy',
    citation: 'https://www.nature.com/scitable/topicpage/recombinant-dna-technology-and-transgenic-animals-34513/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-008',
    text: 'Biofuels are made from biological materials.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Biofuels like ethanol and biodiesel are produced from organic matter such as corn, sugarcane, or algae.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'easy',
    citation: 'https://www.energy.gov/eere/bioenergy/biofuels-basics',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-009',
    text: 'PCR (Polymerase Chain Reaction) can amplify small amounts of DNA.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'PCR copies specific DNA segments millions of times, enabling analysis from tiny samples like a single hair.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'easy',
    citation: 'https://www.ncbi.nlm.nih.gov/probe/docs/techpcr/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-010',
    text: 'Genetic engineering was invented in the 21st century.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Genetic engineering began in the 1970s. The first recombinant DNA organism was created in 1973.',
    errorPattern: 'timeline-compression',
    subject: 'Biotechnology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-011',
    text: 'Golden Rice was engineered to contain vitamin A.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Golden Rice contains beta-carotene, which the body converts to vitamin A, addressing deficiency in rice-dependent populations.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'medium',
    citation: 'https://www.goldenrice.org/',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-012',
    text: 'Biopharming uses genetically modified plants to produce pharmaceuticals.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Plants can be engineered to produce medicines, vaccines, and antibodies more cheaply than traditional methods.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'medium',
    citation: 'https://www.nature.com/articles/nbt0902-922',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-013',
    text: 'Transgenic animals have DNA from other species inserted into their genome.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Transgenic animals contain foreign DNA that was artificially introduced, often to study gene function or produce proteins.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'medium',
    citation: 'https://www.genome.gov/genetics-glossary/Transgenic',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-014',
    text: 'CRISPR was discovered in humans.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'CRISPR was discovered as a bacterial immune system. Bacteria use it to defend against viruses.',
    errorPattern: 'geographic-fabrication',
    subject: 'Biotechnology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-015',
    text: 'Bioremediation uses organisms to clean up environmental pollution.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Bacteria, fungi, and plants can be used to break down or absorb pollutants from soil and water.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'medium',
    citation: 'https://www.epa.gov/remedytech/bioremediation-overview',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-016',
    text: 'Monoclonal antibodies are identical antibodies made from a single cell clone.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Monoclonal antibodies are produced by cloning a single B cell, yielding identical antibodies for targeted treatments.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'medium',
    citation: 'https://www.cancer.gov/about-cancer/treatment/types/immunotherapy/monoclonal-antibodies',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-017',
    text: 'Gene drives can spread genetic changes through entire wild populations.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Gene drives bias inheritance so modified genes spread rapidly through populations, potentially eliminating disease vectors.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'medium',
    citation: 'https://www.nature.com/articles/d41586-019-02087-5',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-018',
    text: 'DNA fingerprinting is 100% accurate with no possibility of error.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'While highly accurate, DNA testing can be affected by contamination, degradation, and human error in analysis.',
    errorPattern: 'confident-specificity',
    subject: 'Biotechnology',
    difficulty: 'medium',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-019',
    text: 'Synthetic biology aims to design and build new biological systems.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Synthetic biology combines engineering principles with biology to create novel biological parts, devices, and systems.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'medium',
    citation: 'https://www.genome.gov/genetics-glossary/Synthetic-Biology',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-020',
    text: 'mRNA vaccines contain live virus particles.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'mRNA vaccines contain instructions for cells to make a harmless protein. They contain no virus at all.',
    errorPattern: 'myth-perpetuation',
    subject: 'Biotechnology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-021',
    text: 'Xenotransplantation involves transplanting organs between different species.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Xenotransplantation transplants organs from one species to another, such as pig hearts into humans.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'hard',
    citation: 'https://www.fda.gov/vaccines-blood-biologics/xenotransplantation',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-022',
    text: 'Optogenetics uses light to control genetically modified neurons.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Light-sensitive proteins are inserted into neurons, allowing precise control of brain activity using light pulses.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'hard',
    citation: 'https://www.nature.com/articles/nmeth.f.324',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-023',
    text: 'Genome sequencing reveals the complete DNA sequence of an organism.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Sequencing determines the exact order of all nucleotide bases in an organism\'s DNA.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'hard',
    citation: 'https://www.genome.gov/genetics-glossary/DNA-Sequencing',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-024',
    text: 'Gene editing permanently changes the DNA in every cell of the body.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Most gene therapies only modify specific target cells. Editing all cells would require germline modification.',
    errorPattern: 'confident-specificity',
    subject: 'Biotechnology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-025',
    text: 'Chimeric antigen receptor (CAR) T-cell therapy modifies a patient\'s T cells.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'CAR-T therapy extracts patient T cells, engineers them to recognize cancer, then reinfuses them.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'hard',
    citation: 'https://www.cancer.gov/about-cancer/treatment/research/car-t-cells',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-026',
    text: 'Organoids are miniature organs grown in laboratories.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Organoids are 3D tissue cultures derived from stem cells that mimic organ structure and function.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'hard',
    citation: 'https://www.nature.com/articles/s41576-019-0196-3',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-027',
    text: 'Base editing can change individual DNA letters without cutting the double helix.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'Base editors chemically convert one base to another without breaking DNA strands, reducing unwanted mutations.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'hard',
    citation: 'https://www.nature.com/articles/nbt.3811',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-028',
    text: 'All biotechnology applications require genetic modification.',
    answer: 'FALSE',
    source: 'ai-generated',
    explanation: 'Traditional biotechnology like fermentation does not involve genetic modification. Even modern techniques vary.',
    errorPattern: 'myth-perpetuation',
    subject: 'Biotechnology',
    difficulty: 'hard',
    citation: null,
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  },
  {
    id: 'biotech-new-029',
    text: 'Induced pluripotent stem cells (iPSCs) can be created from adult cells.',
    answer: 'TRUE',
    source: 'expert-sourced',
    explanation: 'iPSCs are reprogrammed adult cells that regain the ability to become any cell type, like embryonic stem cells.',
    errorPattern: null,
    subject: 'Biotechnology',
    difficulty: 'hard',
    citation: 'https://www.nature.com/articles/nature05944',
    lastVerified: '2024-12-01',
    reviewedBy: ['content-team']
  }
];

/**
 * Get all unique subjects from the claims database
 */
export function getSubjects() {
  const subjects = [...new Set(CLAIMS_DATABASE.map(c => c.subject))];
  return subjects.sort();
}

/**
 * Get claims by subject
 */
export function getClaimsBySubject(subject) {
  return CLAIMS_DATABASE.filter(c => c.subject === subject);
}

/**
 * Get claims by grade level
 * @param {string} gradeLevel - 'elementary' | 'middle' | 'high' | 'college'
 * @returns {Array} Claims for that grade level (+ claims without gradeLevel for backwards compat)
 */
export function getClaimsByGradeLevel(gradeLevel) {
  return CLAIMS_DATABASE.filter(c =>
    c.gradeLevel === gradeLevel || (!c.gradeLevel && gradeLevel === 'middle')
  );
}

/**
 * Get claims filtered by multiple criteria
 * @param {Object} filters - { gradeLevel, difficulty, subject, excludeIds }
 * @returns {Array} Filtered claims
 */
export function getFilteredClaims(filters = {}) {
  const { gradeLevel, difficulty, subject, excludeIds = [] } = filters;
  const excludeSet = new Set(excludeIds);

  return CLAIMS_DATABASE.filter(claim => {
    // Exclude already-seen claims
    if (excludeSet.has(claim.id)) return false;

    // Grade level filter (claims without gradeLevel default to 'middle')
    if (gradeLevel) {
      const claimLevel = claim.gradeLevel || 'middle';
      if (claimLevel !== gradeLevel) return false;
    }

    // Difficulty filter
    if (difficulty && claim.difficulty !== difficulty) return false;

    // Subject filter
    if (subject && claim.subject !== subject) return false;

    return true;
  });
}

/**
 * Get grade level distribution statistics
 */
export function getGradeLevelDistribution() {
  const dist = { elementary: 0, middle: 0, high: 0, college: 0 };
  CLAIMS_DATABASE.forEach(c => {
    const level = c.gradeLevel || 'middle';
    if (dist[level] !== undefined) dist[level]++;
  });
  return dist;
}

/**
 * Get answer distribution statistics
 */
export function getAnswerDistribution() {
  const dist = { TRUE: 0, FALSE: 0, MIXED: 0 };
  CLAIMS_DATABASE.forEach(c => dist[c.answer]++);
  return dist;
}

/**
 * Validate claims database integrity
 */
export function validateClaimsDatabase() {
  const ids = new Set();
  const duplicates = [];
  const invalidClaims = [];

  CLAIMS_DATABASE.forEach((claim, index) => {
    // Check for required fields
    if (!claim.id || !claim.text || !claim.answer) {
      invalidClaims.push({ index, claim, reason: 'Missing required field (id, text, or answer)' });
      return;
    }

    // Check for duplicate IDs
    if (ids.has(claim.id)) {
      duplicates.push(claim.id);
    } else {
      ids.add(claim.id);
    }

    // Validate answer is valid
    if (!['TRUE', 'FALSE', 'MIXED'].includes(claim.answer)) {
      invalidClaims.push({ index, claim, reason: `Invalid answer: ${claim.answer}` });
    }

    // Validate difficulty if present
    if (claim.difficulty && !['easy', 'medium', 'hard'].includes(claim.difficulty)) {
      invalidClaims.push({ index, claim, reason: `Invalid difficulty: ${claim.difficulty}` });
    }

    // Validate grade level if present
    if (claim.gradeLevel && !['elementary', 'middle', 'high', 'college'].includes(claim.gradeLevel)) {
      invalidClaims.push({ index, claim, reason: `Invalid gradeLevel: ${claim.gradeLevel}` });
    }

    // Validate error pattern for AI-generated claims
    if (claim.source === 'ai-generated' && claim.errorPattern) {
      const validPatterns = AI_ERROR_PATTERNS.map(p => p.id);
      if (!validPatterns.includes(claim.errorPattern)) {
        invalidClaims.push({ index, claim, reason: `Invalid error pattern: ${claim.errorPattern}` });
      }
    }
  });

  return {
    valid: duplicates.length === 0 && invalidClaims.length === 0,
    duplicates,
    invalidClaims,
    totalClaims: CLAIMS_DATABASE.length,
    distribution: getAnswerDistribution(),
    gradeLevels: getGradeLevelDistribution(),
    subjects: getSubjects()
  };
}

// Run validation in development
if (import.meta.env?.DEV) {
  const validation = validateClaimsDatabase();
  if (!validation.valid) {
    console.warn('Claims database validation failed:', validation);
  } else {
    console.log('Claims database validation passed:', validation.totalClaims, 'claims');
    console.log('Answer distribution:', validation.distribution);
    console.log('Grade levels:', validation.gradeLevels);
    console.log('Subjects:', validation.subjects);
  }
}
